import chalk from "chalk";
import * as Diff from "diff";
import Device from "./models/Device";
import { EventEmitter } from "events";
import { sleep } from "./models/utils";
import GroupAddress from "./models/GroupAdress";
import { firstChildWithTag } from "./components/XmlUtils";
import { showErrToast, showToast, updateToastProgress } from "./components/Toasts";
import ProductStorageHandler from "./persistence/ProductStorageHandle";
import { CheckPointNames, InstallationStates } from "./models/InstallationChecks";
import XMLParserSingleton from "./XMLParserSingleton";
import { downloadMode } from "./installation/DeviceProgramminCard";
import DeviceProgrammingInfo, { DeviceStatus } from "./models/DeviceProgrammingInfo";
import { EP } from "./performance/PerfomanceItem";
import { AssociationListEntry } from "./models/programming/DeviceProgramming";
import { CodeSegment } from "./models/programming/CodeSegment";
import { itx, noItx } from "./translationItx/itx";
import { knxReadBack } from "./readback/KnxReadback";
import { generateGroupAddresses } from "./addressGeneration/addressGenerator";
import { MathUtils } from "three";

// connected gateway ip and bridge status
export let gatewayIp = "";
export let started = false;
export let connType = "ip";
export let monitoring = false;
export let bridgeOn: boolean = false;
export let lockedState: boolean = false;
export let ipConnected: boolean = false;
export const knxbridgeEmitter = new EventEmitter();

// 3 LP types
const MergedProcedure = "MergedProcedure"; // no itx
const ProductProcedure = "ProductProcedure"; // no itx
const DefaultProcedure = "DefaultProcedure"; // no itx

// Memory Check Status
const MemoryNotReachable = "memoryNotReachable"; // no itx
export const DeviceNotReachable = "deviceNotReachable"; // no itx

export type DeviceAction =
  | "broadcastIndividualAddress" // no itx
  | "assignNewIndividualAddress" // no itx
  | "isDevicePresentInNetwork" // no itx
  | "isDeviceInProgramMode"; // no itx

export type TableSizes = { address: number; association: number; groupObj: number; param: number; param2: number };
export type TableOffsets = { address: string; association: string; groupObj: string; param: string };

// gateway status codes
export enum gwState {
  off = 0,
  on = 1,
  noIp = 2,
}
interface Association {
  addressIndex: number;
  signalNumber: number;
  hex: string;
}
export interface MemoryStats {
  testedOnce: boolean;
  address: string;
  fullName: string;
  apVersion: string;
  maskVersion: string;
  manufacturer: string;
  addrMemDiff: number;
  assocMemDiff: number;
  comObjMemDiff: number;
  paramMemDiff: number;
  totalMemDiff: number;
  failureCount: number;
}

export type ProgrammingOperationType = "download" | "individualAddress" | "verify" | "unload" | "check";
export type ProgrammingOperationStatus = "running" | "success" | "failed" | "stopped";

export interface OperationLogEntry {
  timestamp: string;
  level: "info" | "warn" | "error";
  stage: string;
  message: string;
  percent: number;
  repeatCount: number;
  detail?: string;
}

export interface ProgrammingOperationSummary {
  id: string;
  deviceAddress: string;
  deviceName: string;
  operationType: ProgrammingOperationType;
  status: ProgrammingOperationStatus;
  startedAt: number;
  finishedAt?: number;
  durationMs: number;
  percent: number;
  currentStage: string;
  currentMessage: string;
  backendCalls: number;
  retries: number;
  warnings: number;
  errors: number;
  failureCount: number;
  lastError: string;
  eventLog: OperationLogEntry[];
}

// (Note: There are more PIDs mentioned in documentation, however these are inaccessible for most devices)
// Also this is applicable only for OT 0 (Device base OT) (with exceptions)
const PIDList = {
  InterfaceObjectType: "1", // no itx
  InterfaceObjectName: "2", // no itx
  LoadStateControl: "5", // no itx
  RunControl: "6", // no itx
  TableReference: "7", // no itx
  ServiceControl: "8", // no itx
  FirmwareRevision: "9", // no itx
  ServicesSupported: "10", // no itx
  KNXSerialNumber: "11", // no itx
  ManufacturerId: "12", // no itx
  ApplicationVersion: "13", // no itx
  DeviceControl: "14", // no itx
  OrderInfo: "15", // no itx
  PEIType: "16", // no itx
  PortConfiguration: "17", // no itx
  PollGroupSettings: "18", // no itx
  ManufacturerData: "19", // no itx
  Description: "21", // no itx
  AddressAssociationTable: "23", // no itx
  Version: "25", // no itx
  MemoryControlTable: "27", // no itx
  ErrorCode: "28", // no itx
  ObjectIndex: "29", // no itx
  DownloadCounter: "30", // no itx

  RoutingCount: "51", // no itx
  MaxRetryCount: "52", // no itx
  ErrorFlags: "53", // no itx
  ProgrammingMode: "54", // no itx
  ProductID: "55", // no itx
  MaxAPDULength: "56", // no itx
  SubnetworkAddress: "57", // no itx
  DeviceAddress: "58", // no itx
  PBConfig: "59", // no itx
  AddrReport: "60", // no itx
  AddrCheck: "61", // no itx
  ObjectValue: "62", // no itx
  ObjectLink: "63", // no itx
  Application: "64", // no itx
  Parameter: "65", // no itx
  ObjectAddress: "66", // no itx
  PSUType: "67", // no itx
  PSUStatus: "68", // no itx
  PSUEnable: "69", // no itx
  DomainAddress: "70", // no itx
  IOList: "71", // no itx
  MgtDescriptor: "72", // no itx
  PL110Param: "73", // no itx
  RecieveBlockTable: "75", // no itx
  RandomPauseTable: "76", // no itx
  RecieveBlockNr: "77", // no itx
  HardwareType: "78", // no itx
  RetransmitterNumber: "79", // no itx
  SerialNrTable: "80", // no itx
  BibatMasterAddress: "81", // no itx
  RfDomainAddress: "82", // no itx
  DeviceDescriptor: "83", // no itx
  GroupTelegramRateLimitTimeBase: "85", // no itx
  GroupTelegramRateLimitNoOfTelegram: "86", // no itx
};

// Events that can be triggered by writing into loadStates
const LoadEvent = {
  NoOperation: "0", // no itx
  StartLoading: "1", // no itx
  LoadCompleted: "2", // no itx
  AdditionalLoadControls: "3", // no itx
  Unload: "4", // no itx
};

// Possible Output States of the above Load Events
const OutputLoadStates = {
  Unloaded: "0", // no itx
  Loaded: "1", // no itx
  Loading: "2", // no itx
  Error: "3", // no itx
  Unloading: "4", // no itx
  LoadCompleting: "5", // no itx
};

export const StandardObjectTypeList: Record<string, number> = {
  Device: 0,
  AddressTable: 1,
  AssociationTable: 2,
  ApplicationProgram: 3,
  InterfaceProgram: 4,
  EibObjectAssociationTable: 5,
  Router: 6,
  LTEAddressRoutingTable: 7,
  cEMIServer: 8,
  GroupObjectTable: 9,
  PollingMaster: 10,
  KNXnetIPParameter: 11,
  MediaType: 12,
  FileServer: 13,
  EmodeChannel: 14,
  AdjustedEmodeChannel: 15,
  TextCatalogue: 16,
  SecurityInterface: 17,
  EmodeDevice: 18,
  RFMedium: 19,
  IndoorBrightnessSensor: 409,
  IndoorLuminanceSensor: 410,
  LightSwitchingActuatorBasic: 417,
  DimmingActuatorBasic: 418,
  DimmingSensorBasic: 420,
  SwitchingSensorBasic: 421,
  SunblindActuatorBasic: 800,
  SunblindSensorBasic: 801,
};
const ObjectTypeById = Object.fromEntries(Object.entries(StandardObjectTypeList).map(([key, value]) => [value, key]));

export function setIp(newIp: string) {
  if (gatewayIp != newIp) {
    gatewayIp = newIp;
    if (newIp != "") {
      setIpConnected(true);
      setBridgeStatus(true);
    } else {
      setIpConnected(false);
    }
    knxbridgeEmitter.emit("ipChange", gatewayIp);
  }
}

export function setIpConnected(status: boolean) {
  if (ipConnected != status) {
    ipConnected = status;
    knxbridgeEmitter.emit("ipConnectionChange", ipConnected);
  }
}

export function setBridgeStatus(status: boolean) {
  if (bridgeOn != status) {
    bridgeOn = status;
    if (!status) setIp("");
    knxbridgeEmitter.emit("bridgeChange", bridgeOn);

    // Making sure the window events are properly attached/detached
    if (status) KnxBridge.start();
    else KnxBridge.stop();
  }
}

export function refreshUI() {
  knxbridgeEmitter.emit("refreshUI", true);
}

class KnxBridgeClass {
  allGroupAddresses = new Map<string, GroupAddress>();

  // Absolute Memory Segments Constants
  deviceMemoryOffset = "0x4000";
  deviceMemorySize = 3328;
  ramOffset = "0x0700";
  ramSize = 468;

  // Only Applicable to Product Procedure
  defaultAddressTableOffset = "0x4000";
  defaultAssociationTableOffset = "0x4201";
  defaultComObjTableOffset = "0x4400";

  // Maximum Readable memory per request
  maxBytesReadable = 12; // Default; Lowest APDU length  (Standard frame Size)
  maxNibblesReadable = this.maxBytesReadable * 2; // 4bits = 1 hex character
  maxReqRetries = 5;

  // ComObj's TypeOctet Size Definitions
  typeOctetTable = [
    "1 bit",
    "2 bit",
    "3 bit",
    "4 bit",
    "5 bit",
    "6 bit",
    "7 bit",
    "1 byte",
    "2 bytes",
    "3 bytes",
    "4 bytes",
    "6 bytes",
    "8 bytes",
    "10 bytes",
    "14 bytes",
  ];

  priorityIndex = {
    Low: "11",
    High: "01",
    Urgent: "10",
    System: "00",
  };

  // Mask Version Categories (Supported Mask Versions so far)
  // At the moment we only support Device Masks with Twisted Pair (TP) Line medium.
  BimM112Masks = ["0700", "0701", "0705", "1900", "2705", "5705"]; // AbsoluteSegments // no itx
  Bcu2Masks = ["0020", "0021", "0012"]; // AbsoluteSegments // no itx
  SystemBMasks = ["07B0", "17B0", "27B0", "2920", "57B0"]; // RelativeSegments  // no itx
  IPRouterMask = ["091A"]; // NoMemorySegment (Uses Obj Types 6,11) // no itx
  SupportedMasks = ["0705", "07B0", "0701", "0021", "0012", "091A"]; // no itx

  // Supported:
  // BimM112 management Model with TP Line :  Mask 0705, 0701 (Potentially 0700 may also work)
  // SystemB management Model with TP Line :  Mask 07B0

  // Unsupported:
  // Very old Systems such as Bcu1, Bcu2
  // Special systems (System300 etc) used by Siemens
  // 0700 & 0701 are older device masks and mostly supported by older ETS (ETS5 and below)
  // 1xxx is PowerLine Medium (PL)
  // 2xxx is RF / Coupler
  // 5xxx is Knxnet/IP

  // Verify Memory Statistics
  lastDeviceMemoryStats: MemoryStats = {
    testedOnce: false,
    address: "",
    fullName: "",
    apVersion: "",
    maskVersion: "",
    manufacturer: "",
    addrMemDiff: 0,
    assocMemDiff: 0,
    comObjMemDiff: 0,
    paramMemDiff: 0,
    totalMemDiff: 0,
    failureCount: 0,
  };

  // Toast Progress bar
  percentUI: number = 0;
  percentText: string = "";
  toastTextUI: string = "";

  // Status Checks for device Programming operations
  operationInProgress = false;
  downloadInProgress = false;
  unloadInProgress = false;
  verifyInProgress = false;
  deviceLoading = false;
  batchOperationInProgress = false;
  batchIndex = 0;
  batchCount = 1;

  operatingDevice: string | undefined = undefined;
  stopOperationCommand: boolean = false;
  downloadMode = downloadMode.none;

  // othe status checks for device (program mode, exists in network)
  deviceInProgMode: any = undefined;
  lastDeviceUnreachable = "";
  operatingDeviceAddress: string = "";
  operationFailure = false;

  // Diagnostics
  networkMonitorRunning = false;
  scanLineMode = false;
  scanline = "";
  scanarea = "";

  xmlString = "";
  paramTableString = "";
  absoluteSegmentId = "";
  failureCount = 0;
  strictETSMode = true;
  msgs = {};

  currentOperationSummary: ProgrammingOperationSummary | undefined = undefined;
  lastOperationSummary: ProgrammingOperationSummary | undefined = undefined;
  operationSequence = 0;
  progressLowerBound = 0;
  progressUpperBound = 100;

  // Bridge Start/Stop
  async start() {
    this.allGroupAddresses = new Map<string, GroupAddress>();
    if (started) {
      console.log("[KnxBridge] Already started, skipping reinit");
      return;
    }
    stop();
    try {
      (window as any).onVarChanged = this.onVarChanged;
      (window as any).onKnxReceived = this.onKnxReceived;
      (window as any).onPing = this.onPing;
      (window as any).onError = this.onError;
      (window as any).onConnect = this.onConnect;
      (window as any).showError = this.onShowError;

      if (!(window as any).webSocketServer) {
        (window as any).startupWebSocket();
        (window as any).startupKnxApiSocket();
      }

      const dataPoints = new Array<string>();
      const readDatapoints = new Array<string>();
      state.addresses.forEach((addresslevel1) => {
        addresslevel1.children.forEach((addressLevel2) => {
          addressLevel2.children.forEach((addressLevel3) => {
            const p = "";
            this.allGroupAddresses.set(addressLevel3.fullAddress, addressLevel3);

            // read the inital values only for status group addresses
            if (addressLevel3.isStatusGroupAddress) {
              readDatapoints.push(addressLevel3.fullAddress);
            }
            dataPoints.push(addressLevel3.fullAddress + "," + "DPST-" + addressLevel3.dpt + p); // no itx
          });
        });
      });

      //   Test Examples:
      //   dataPoints.push("1/1/68" + "," + "DPST-1-1"); // command
      //   dataPoints.push("1/1/69" + "," + "DPST-3-7"); // command
      //   dataPoints.push("1/1/70" + "," + "DPST-5-1"); // state/feedback
      //   dataPoints.push("1/1/71" + "," + "DPST-1-11"); // state/feedback
      //   dataPoints.push("3/1/19" + "," + "DPST-1-1"); // command
      //   dataPoints.push("3/1/210" + "," + "DPST-1-1"); // state/feedback
      //   dataPoints.push(data.groupAddress + "," + data.dptType + p); // feedback

      var s = dataPoints.join(";");
      (window as any).sendToSocketServer({ command: "loadDatapoints", data: s }); // no itx

      await sleep(5000);

      (window as any).sendToSocketServer({ command: "requestUpdateFromKnx", data: { groupAddresses: readDatapoints } }); // no itx

      if ((window as any).webSocketServer) {
        try {
          // Call the KNX API over WebSocket instead of HTTP fetch
          const text = await (window as any).knxApiCall({ command: "getCurrentState" });
          //   const response = await fetch("http://" + (window as any).webSocketServer + ":8080/getCurrentState");
          //   const text = await response.text();

          const lines = text.split("\n");
          lines.forEach((line) => {
            const ss = line.split(";");
            const groupAddress = this.allGroupAddresses.get(ss[0]);
            if (groupAddress) {
              groupAddress.currentValue = ss[2]; // use numericValue
              groupAddress.raiseOnChange("currentValue");
            }
          });
        } catch (err) {
          console.error("getCurrentState failed:", err);
        }
      }

      started = true;
    } catch (ex) {
      return false;
    }
  }

  stop() {
    started = false;
    (window as any).onVarChanged = null;
    (window as any).onKnxReceived = null;
    (window as any).onPing = null;
    (window as any).onError = null;
    (window as any).onConnect = null;
  }

  ///////////////////////////////////////
  // Group Monitor

  startGroupMonitor() {
    if ((window as any).onGroupMonitor) {
      console.log("[KnxBridge] Group monitor already running");
      return;
    }
    (window as any).onGroupMonitor = this.onGroupMonitor;
    knxReadBack().clearTelegrams();
    this.networkMonitorRun(true);
  }

  stopGroupMonitor() {
    (window as any).onGroupMonitor = null;
    if (this.networkMonitorRunning) this.networkMonitorRun(false);
  }

  ////////////////////////////////////////
  // Event Listeners
  lastPing = null;
  onPing(dataFromServer: any) {
    this.lastPing = dataFromServer;
    if (dataFromServer?.time) {
      console.log("[KnxBridge] WebSocket ping success at timestamp: " + dataFromServer.time);
    } else {
      console.error("WebSocket ping failure: " + dataFromServer);
    }

    state.knxState.value = "active";
    state.knxStateText.value = "";
  }

  onConnect() {
    console.log("[KnxBridge] Web socket connected!");
  }

  onError(dataFromServer: any) {
    try {
      console.log("[KnxBridge] Error from knxInterface ", JSON.stringify(dataFromServer));
      console.error();
      const errorCode = dataFromServer.errorCode;
      state.knxState.value = errorCode != 0 ? "error" : "active";
      state.knxStateText.value = dataFromServer.errorText; // route error to App.js
    } catch (ex) {
      console.error(ex);
    }
  }

  onKnxReceived = (dataFromServer: any) => {
    console.log(
      `onKnxReceived... ${dataFromServer.groupAddress}  ${dataFromServer.value} ${dataFromServer.numericValue} `,
    );
    const groupAddress = this.allGroupAddresses.get(dataFromServer.groupAddress);
    const sourceAddr = dataFromServer.sourceAddr;

    const rb = knxReadBack();
    if (!ipConnected) {
      rb.clearFoundAddrs();
    }

    if (ipConnected && rb.gatewayIp != gatewayIp) {
      rb.clearFoundAddrs();
      rb.setGatewayIp(gatewayIp);
    }

    if (this.scanLineMode) {
      const [area, line, segment] = sourceAddr.split(".");
      if (area == this.scanarea && line == this.scanline) rb.registerScannedAddr(sourceAddr);
    }

    if (rb.readbackMonitoringIsRunning) {
      rb.registerFoundAddr(sourceAddr);
    }
    console.log("[KnxBridge] sourceAddr: ", sourceAddr);

    if (groupAddress) {
      groupAddress.currentValue = dataFromServer.numericValue;
      groupAddress.raiseOnChange("currentValue");
    }
  };

  onGroupMonitor = (dataFromServer: any) => {
    console.log(dataFromServer);
    const rb = knxReadBack();
    rb.registerTelegram(dataFromServer);
    console.log(`[KnxBridge] sourceAddr: ${dataFromServer.sourceAddr} targetAddr: ${dataFromServer.targetAddr}`);
  };

  onVarChanged = (dataFromServer: any) => {
    console.log("[KnxBridge] onVarChanged... " + dataFromServer.varname + " = " + dataFromServer.value);
    const groupAddress = this.allGroupAddresses.get(dataFromServer.varname);
    if (groupAddress) {
      groupAddress.currentValue = dataFromServer.value;
      groupAddress.raiseOnChange("currentValue");
    }
  };

  onShowError = (dataFromServer: any) => {
    console.error(`knxServerError... ${dataFromServer.errorCode}  ${dataFromServer.errorText}`);
  };

  // Read/Write Group Addresses
  sendGroupAddressValue(groupAddress: GroupAddress, value: any) {
    // Example Write data:
    // {
    //   groupAddress: "1/1/68",
    //   dptType: "DPST-1-1",
    //   value: false,
    // }

    let dpst = groupAddress.dpt;
    if (!dpst.toUpperCase().startsWith("DPST")) {
      dpst = "DPST-" + dpst;
    }

    var data = {
      command: "write", // no itx
      data: {
        groupAddress: groupAddress.fullAddress,

        dptType: dpst,
        value: value,
      },
    };

    (window as any).sendToSocketServer(data);
  }
  loadGroupAddress(data: any) {
    return {
      command: "loadDatapoints", // no itx
      data: data,
    };
  }

  /////////////////////////////////////////////////////
  /////////////////////////////////////////////////////
  ////// Device Memory Operations (Read/Write)
  ////// Address Table, Association Table
  /////////////////////////////////////////////////////
  /////////////////////////////////////////////////////

  // 22.01.2026 - Http functions still kept for testing purposes
  async fetchAPI(apiName: string, relativeUrl: string) {
    let output = "";
    let response, text;
    if ((window as any).webSocketServer) {
      try {
        let retryCounter = 0;
        for (retryCounter; retryCounter < this.maxReqRetries; retryCounter += 1) {
          response = await fetch(`http://${(window as any).webSocketServer}:8080/${relativeUrl}`);
          text = await response.text();
          if (apiName != "getDevicesInProgMode" && (!ipConnected || text == "Disconnected")) {
            await sleep(10000);
          } else if (text == "failure") {
            await sleep(5000);
          } else {
            break;
          }
        }
        if (retryCounter == this.maxReqRetries) {
          this.failureCount++;
          console.log(`[KnxBridge API Failure]: APIName: ${apiName}, RelativeURL: ${relativeUrl}`);
        }

        const statusCode = response?.status;

        if ((apiName == "getDevicesInProgMode" || apiName == "isDeviceInProgramMode") && statusCode == 503) {
          output = "Service Unavailable"; // no itx
          setBridgeStatus(false);
        } else {
          setBridgeStatus(true);
          if (
            apiName == "getDevicesInProgMode" &&
            statusCode == 200 &&
            text != "Disconnected" &&
            !text.startsWith("Error")
          ) {
            setIpConnected(true);
          }

          if (
            text == "Disconnected" ||
            text.includes("Network is unreachable") ||
            text.includes("Communication failure on connect")
          ) {
            setIp("");
          } else {
            const lines = text.split("\n");
            if (
              text.startsWith("Error: \n") // no itx
            ) {
              console.error(text.split("Error: \n")[1]);
            } else if (
              text.startsWith("Error") // no itx
            ) {
              console.error("Error in response:" + text);
            } else if (lines.length == 1) {
              output = lines[0];
            } else {
              if (apiName == "readAllDeviceProperties" || apiName == "readCompleteDeviceMemory") {
                return text;
              }
              console.error("More than one lines found in response:" + text);
            }
            // if (text == "success" || output != "") {
            // console.log(apiName + " success");
            // }
          }
          if (text.includes("timeout waiting for data response") || text.includes("send data connected failed")) {
            output = DeviceNotReachable;
          } else if (text.includes("could not read memory from")) {
            output = MemoryNotReachable;
          }
        }
      } catch (ex) {
        console.error(ex);
      }
    }
    return output;
  }

  /**
   * Main KNX API call helper (via WebSocket -> Kotlin/Calimero backend).
   *
   * Improvements vs older implementation:
   * - Exponential backoff + jitter
   * - Better connection state handling (bridgeOn / ipConnected)
   * - Preserves multi-line payload for memory/property dump APIs
   * - Single place to interpret common backend error strings
   * - Aborts fast when user requested stopOperationCommand
   */
  async fetchAPISocket(apiName: string, relativeUrl: string) {
    let output = "";
    let text = "";

    if (!(window as any).webSocketServer) return output;

    const maxRetries = Math.max(1, this.maxReqRetries);
    let delayMs = 400;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      if (this.stopOperationCommand && apiName !== "getDevicesInProgMode" && apiName !== "pingGateway") {
        throw new Error("Operation stopped");
      }

      try {
        if (this.currentOperationSummary) this.currentOperationSummary.backendCalls += 1;
        // Send API request via WebSocket
        text = await (window as any).knxApiCallFromUrl(relativeUrl);
      } catch (err) {
        console.warn(`[KnxBridge] API transport error (${apiName}) attempt ${attempt}/${maxRetries}`, err);
        text = "failure"; // no itx
      }

      // ---- Connection state bookkeeping ----
      // Backend uses these sentinel strings frequently.
      if (
        text === "Disconnected" ||
        text.includes("Network is unreachable") ||
        text.includes("Communication failure on connect")
      ) {
        setIp("");
        setIpConnected(false);
        setBridgeStatus(false);
      } else {
        // We got *some* response. The bridge is alive.
        setBridgeStatus(true);
        if (apiName === "getDevicesInProgMode" && !text.startsWith("Error")) {
          setIpConnected(true);
        }
      }

      // ---- Map common backend errors to UI-friendly statuses ----
      if (text.includes("timeout waiting for data response") || text.includes("send data connected failed")) {
        output = DeviceNotReachable;
      } else if (text.includes("could not read memory from")) {
        output = MemoryNotReachable;
      }

      // ---- Multi-line APIs ----
      if (apiName === "readAllDeviceProperties" || apiName === "readCompleteDeviceMemory") {
        // These endpoints intentionally return multi-line blobs.
        // Caller handles empty/error semantics.
        if (text && text !== "failure" && text !== "Disconnected") return text;
      }

      // ---- Normal single-line parsing ----
      if (text && text !== "failure" && text !== "Disconnected") {
        if (text.startsWith("Error:")) {
          console.error(text.split("Error: ")[1]);
          output = "";
        } else if (text.startsWith("Error")) {
          console.error("Error in response:" + text);
          output = "";
        } else {
          const lines = text
            .split("\n")
            .map((l: string) => l.trim())
            .filter(Boolean);
          if (lines.length >= 1) {
            // Most endpoints return exactly one line.
            output = lines[0];
          } else {
            output = "";
          }
        }
      }

      // ---- Retry decision ----
      const shouldRetry =
        text === "" ||
        text === "failure" ||
        (apiName !== "getDevicesInProgMode" && (!ipConnected || text === "Disconnected"));

      if (!shouldRetry) break;

      if (attempt < maxRetries) {
        if (this.currentOperationSummary) this.currentOperationSummary.retries += 1;
        const jitter = Math.floor(Math.random() * 180);
        await sleep(delayMs + jitter);
        delayMs = Math.min(8000, Math.floor(delayMs * 1.6));
      } else {
        this.failureCount++;
        console.log(`[KnxBridge API Failure]: APIName: ${apiName}, RelativeURL: ${relativeUrl}`);
      }
    }

    return output;
  }

  //////////////////////
  //// Ping Gateway
  //////////////////////

  // 22.01.2026 - Kept for testing purpose
  async pingGateway() {
    let status = false;
    if ((window as any).webSocketServer) {
      try {
        const response = await fetch(`http://${(window as any).webSocketServer}:8080/test`);
        if (response?.status == 200 && response?.statusText != "") {
          status = true;
          const res = await response.text();
          if (res != "" && this.isValidConn(res)) {
            setIp(res);
          } else {
            setIp(""); // else case also handles // if (res == "NotConnected") setIpConnected(false);
          }
          // if (!this.downloadInProgress && !this.unloadInProgress && !this.operationInProgress)
          //   showToast({ text: `Gateway connected to IP ${this.ip}.` });
        } else if (response?.status == 503) {
          setBridgeStatus(false); // Service Unavailable
        }
      } catch (ex) {
        console.error(ex);
      }
    }
    setBridgeStatus(status);
    return status;
  }

  async pingGatewaySocket() {
    let status = false;

    if ((window as any).webSocketServer) {
      try {
        const res = await (window as any).knxApiCall({
          command: "test",
        });

        if (res !== undefined && res !== null) {
          status = true;

          if (res !== "" && this.isValidConn(res)) {
            setIp(res);
          } else {
            setIp(""); // handles "NotConnected"
          }
        }
      } catch (ex) {
        console.error(ex);
        setBridgeStatus(false);
      }
    }

    setBridgeStatus(status);
    return status;
  }

  setOperationInProgress(value: boolean, deviceAddress?: string) {
    this.operationInProgress = value;
    this.operatingDevice = deviceAddress;
    state.programStat.operationInProgress = value;
    if (!value) {
      this.progressLowerBound = 0;
      this.progressUpperBound = 100;
    }
    knxbridgeEmitter.emit("operationUpdate", value);
  }

  private emitOperationSummary() {
    if (!this.currentOperationSummary && !this.lastOperationSummary) return;
    knxbridgeEmitter.emit("operationSessionUpdate", this.currentOperationSummary ?? this.lastOperationSummary);
  }

  private startOperationSummary(
    operationType: ProgrammingOperationType,
    device: Device,
    stage: string,
    message: string,
  ) {
    const now = Date.now();
    this.currentOperationSummary = {
      id: `${operationType}-${++this.operationSequence}`,
      deviceAddress: device.fullPhysicalAddress,
      deviceName: `${device.fullTag} ${device.fullname}`.trim(),
      operationType,
      status: "running", // no itx
      startedAt: now,
      durationMs: 0,
      percent: 0,
      currentStage: stage,
      currentMessage: message,
      backendCalls: 0,
      retries: 0,
      warnings: 0,
      errors: 0,
      failureCount: 0,
      lastError: "",
      eventLog: [],
    };
    this.pushOperationLog("info", stage, message, 0);
  }

  private finishOperationSummary(status: ProgrammingOperationStatus, error?: unknown) {
    if (!this.currentOperationSummary) return;
    const now = Date.now();
    this.currentOperationSummary.status = status;
    this.currentOperationSummary.finishedAt = now;
    this.currentOperationSummary.durationMs = now - this.currentOperationSummary.startedAt;
    this.currentOperationSummary.failureCount = this.failureCount;
    if (error) {
      const errorText = error instanceof Error ? error.message : `${error}`;
      this.currentOperationSummary.lastError = errorText;
      if (status === "failed") this.pushOperationLog("error", "Error", errorText, this.percentUI);
      else if (status === "stopped") this.pushOperationLog("warn", "Stopped", errorText, this.percentUI);
    }
    if (status === "success") {
      this.currentOperationSummary.percent = 100;
      this.currentOperationSummary.currentMessage = itx(
        "KnxBridge.operationfinishedsuccessfully",
        "Operation finished successfully",
      );
      this.currentOperationSummary.currentStage = "Completed"; // no itx
      this.pushOperationLog("info", "Completed", "Operation finished with status success", 100);
    }
    this.lastOperationSummary = JSON.parse(JSON.stringify(this.currentOperationSummary));
    this.currentOperationSummary = undefined;
    this.emitOperationSummary();
  }

  private pushOperationLog(
    level: "info" | "warn" | "error",
    stage: string,
    message: string,
    percent: number,
    detail?: string,
  ) {
    const target = this.currentOperationSummary ?? this.lastOperationSummary;
    if (!target) return;
    const normalizedPercent = Number.isFinite(percent) ? Math.max(0, Math.min(100, Number(percent.toFixed(1)))) : 0;
    target.percent = normalizedPercent;
    target.currentStage = stage;
    target.currentMessage = message;
    if (level === "warn") target.warnings += 1;
    if (level === "error") target.errors += 1;
    const prev = target.eventLog[target.eventLog.length - 1];
    if (prev && prev.level === level && prev.stage === stage && prev.message === message) {
      prev.repeatCount += 1;
      prev.timestamp = new Date().toLocaleString();
      prev.percent = normalizedPercent;
      if (detail) prev.detail = detail;
    } else {
      target.eventLog.push({
        timestamp: new Date().toLocaleString(),
        level,
        stage,
        message,
        percent: normalizedPercent,
        repeatCount: 1,
        detail,
      });
    }
    this.emitOperationSummary();
  }

  private inferOperationStage(text: string, postfix?: string) {
    const normalized = `${text} ${postfix ?? ""}`.toLowerCase();
    if (normalized.includes("verify") && normalized.includes("application")) return "Preflight";
    if (normalized.includes("construct") || normalized.includes("creating loadprocedure")) return "Prepare Procedure";
    if (normalized.includes("executing loadprocedure") && normalized.includes("connect")) return "Connect";
    if (normalized.includes("compare") || normalized.includes("device properties")) return "Read Device State";
    if (normalized.includes("unload")) return "Unload Existing Data";
    if (normalized.includes("address table")) return "Address Table";
    if (normalized.includes("association")) return "Association Table";
    if (
      normalized.includes("comobject") ||
      normalized.includes("communication object") ||
      normalized.includes("com object")
    )
      return "Communication Objects"; // no itx
    if (normalized.includes("parameter")) return "Parameters";
    if (normalized.includes("restart")) return "Restart";
    if (normalized.includes("finaliz")) return "Finalize";
    if (normalized.includes("broadcast")) return "Broadcast";
    if (normalized.includes("assigning new individual address")) return "Assign Address";
    if (normalized.includes("unloading device address")) return "Unload Address";
    if (normalized.includes("downloading")) return "Download";
    if (normalized.includes("verifying")) return "Verify";
    return "Operation"; // no itx
  }

  private setProgressRange(lower: number, upper: number) {
    this.progressLowerBound = Math.max(0, Math.min(100, lower));
    this.progressUpperBound = Math.max(this.progressLowerBound, Math.min(100, upper));
  }

  private computeProgressFromStep(stepIndex: number, totalSteps: number) {
    if (totalSteps <= 0) return this.progressUpperBound;
    const ratio = Math.max(0, Math.min(1, stepIndex / totalSteps));
    return this.progressLowerBound + (this.progressUpperBound - this.progressLowerBound) * ratio;
  }

  setStopOperationCommand(value: boolean) {
    this.stopOperationCommand = value;
    knxbridgeEmitter.emit("stopOperationCommand", value);
  }

  checkDeviceReachable(res: string, deviceAddress: string) {
    if (res == DeviceNotReachable) {
      this.lastDeviceUnreachable = deviceAddress;
      // throw Error(DeviceNotReachable);
    } else this.lastDeviceUnreachable = "";
  }

  //////////////////////
  //// Restart Device
  //////////////////////
  // 20-05-2025: Special handlings added for Theben and GVS device restarts
  async restartDevice(deviceAddress: string, manId: string) {
    const res = await this.fetchAPI("restartDevice", `restartDevice?device=${deviceAddress}&manId=${manId}`);
    this.checkDeviceReachable(res, deviceAddress);
    return this.checkConnectionStatus(res == "success");
  }

  //////////////////////
  //// Reset Device
  //////////////////////
  // Initiates a master reset of the controller of a communication partner.
  // Available erase codes:
  // 1: confirmed restart (basic restart with confirmation)
  // 2: factory reset (used together with channel)
  // 3: reset the device individual address to its default
  // 4: reset application program memory to default application
  // 5: reset application parameter memory (used together with channel)
  // 6: reset links (used together with channel)
  // 7: factory reset without resetting the device individual address (used together with channel)

  // channel param – the number of application channel that shall be reset and application parameters set to default values,
  // use 0 to clear all link information in the group address table and group object association table and reset all application parameters
  async resetDevice(deviceAddress: string, code: string, channel: string) {
    const res = await this.fetchAPI(
      "resetDevice", // no itx
      `resetDevice?device=${deviceAddress}&code=${code}&channel=${channel}`,
    );
    this.checkDeviceReachable(res, deviceAddress);
    return this.checkConnectionStatus(res == "success");
  }

  //////////////////////
  //// Get / Set Device Programming Mode
  //////////////////////
  async getDevicesInProgramMode() {
    const res = await this.fetchAPI("getDevicesInProgMode", `getDevicesInProgMode`);
    return this.checkConnectionStatus(res);
  }

  async setDeviceProgramMode(deviceAddress: string, status: string) {
    const res = await this.fetchAPI("setDeviceProgMode", `setDeviceProgMode?device=${deviceAddress}&status=${status}`);
    this.checkDeviceReachable(res, deviceAddress);
    return this.checkConnectionStatus(res == "success");
  }

  //////////////////////
  //// Line Scan
  //////////////////////
  // 20-05-2025: Special handlings added for Theben and GVS device restarts
  async scanLine(area: string, line: string) {
    this.scanLineMode = true;
    this.scanline = line;
    this.scanarea = area;
    const relativeUrl = `scanLine?area=${area}&line=${line}`;
    const response = await fetch(`http://${(window as any).webSocketServer}:8080/${relativeUrl}`);
    const text = await response.text();
    console.log(text);
    this.scanLineMode = false;
    return text;
  }

  async scanLineSocket(area: string, line: string) {
    this.scanLineMode = true;
    this.scanline = line;
    this.scanarea = area;

    try {
      const text = await (window as any).knxApiCall({
        command: "scanLine",
        area,
        line,
      });

      console.log(text);
      return text;
    } catch (err) {
      console.error("scanLine failed", err);
      return "";
    } finally {
      this.scanLineMode = false;
    }
  }

  //////////////////////
  //// Network Monitor
  //////////////////////
  // 20-05-2025: Special handlings added for Theben and GVS device restarts
  async networkMonitorRun(status: boolean) {
    const command = status ? "start" : "stop";
    const relativeUrl = `networkMonitor?command=${command}`;
    const response = await fetch(`http://${(window as any).webSocketServer}:8080/${relativeUrl}`);
    const text = await response.text();
    console.log(text);
    this.networkMonitorRunning = status;
    return text;
  }

  async networkMonitorRunSocket(status: boolean) {
    const command = status ? "start" : "stop";
    this.networkMonitorRunning = status; // optimistically set
    try {
      const text = await (window as any).knxApiCall({
        command: "networkMonitor",
        action: command,
      });

      console.log(text);
      return text;
    } catch (err) {
      console.error("networkMonitorRun failed", err);
      return "";
    }
  }

  /**
   * Helper to build relativeUrl strings with proper encoding.
   * Ensures binary/hex payloads won't break query parsing.
   */
  private buildUrl(path: string, params?: Record<string, any>): string {
    if (!params) return path;
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v === undefined || v === null) return;
      qs.append(k, String(v));
    });
    const query = qs.toString();
    return query ? `${path}?${query}` : path;
  }

  //////////////////////
  // DeviceMemory
  //////////////////////

  async readFromDeviceMemory(
    deviceAddress: string,
    offsetAddress: string,
    noOfBytes: number,
    totalNumberOfBytes?: number,
  ): Promise<string> {
    const relativeUrl = this.buildUrl(
      "getDeviceMemory", // no itx
      {
        device: deviceAddress,
        offset: offsetAddress,
        bytes: noOfBytes.toString(),
        totalbytes: (totalNumberOfBytes || noOfBytes).toString(),
      },
    );

    const res = await this.fetchAPI("readFromDeviceMemory", relativeUrl);
    this.checkDeviceReachable(res, deviceAddress);
    return this.checkConnectionStatus(res);
  }

  async writeToDeviceMemory(deviceAddress: string, offsetAddress: string, data: string) {
    // NOTE: data can be a long hex string; ensure encoding won't break query parsing.
    const relativeUrl = this.buildUrl(
      "setDeviceMemory", // no itx
      {
        device: deviceAddress,
        offset: offsetAddress,
        data,
      },
    );

    const res = await this.fetchAPI("writeToDeviceMemory", relativeUrl);
    this.checkDeviceReachable(res, deviceAddress);
    return this.checkConnectionStatus(res == "success");
  }

  //////////////////////
  // DeviceProperty

  //////////////////////

  async readDeviceProperty(
    deviceAddress: string,
    objectTypeId: string,
    propertyId: string,
    start?: string,
    elements?: string,
  ) {
    const relativeUrl = this.buildUrl("getDeviceProperty", {
      device: deviceAddress,
      objectTypeId,
      propertyId,
      start,
      elements,
    });

    const res = await this.fetchAPI("readDeviceProperty", relativeUrl);
    this.checkDeviceReachable(res, deviceAddress);
    return this.checkConnectionStatus(res);
  }

  async writeDeviceProperty(
    deviceAddress: string,
    objectTypeId: string,
    propertyId: string,
    value: string,
    start?: string,
    elements?: string,
  ) {
    const relativeUrl = this.buildUrl("setDeviceProperty", {
      device: deviceAddress,
      objectTypeId,
      propertyId,
      value,
      start: start ? start : "1",
      elements: elements ? elements : "1",
    });

    const res = await this.fetchAPI("writeDeviceProperty", relativeUrl);
    this.checkDeviceReachable(res, deviceAddress);
    return this.checkConnectionStatus(res == "success");
  }

  async readAllDeviceProperties(deviceAddress: string) {
    const res = await this.fetchAPI("readAllDeviceProperties", `getDeviceProperty?device=${deviceAddress}`);
    this.checkDeviceReachable(res, deviceAddress);
    return this.checkConnectionStatus(res);
  }

  ////////////////////////
  // Read Write Group Address
  ////////////////////////
  async readGroupAddress(sourceAddress: string, destinationAddress: string, dpt: string) {
    const res = await this.fetchAPI(
      "readGroupAddress", // no itx
      `readWriteGrpAddr?operation=read&sourceaddress=${sourceAddress}&destinationaddress=${destinationAddress}&dpt=${dpt}`,
    );
    this.checkDeviceReachable(res, destinationAddress);
    return this.checkConnectionStatus(res);
  }

  async writeGroupAddress(sourceAddress: string, destinationAddress: string, dpt: string, value: String) {
    const res = await this.fetchAPI(
      "writeGroupAddress", // no itx
      `readWriteGrpAddr?operation=write&sourceaddress=${sourceAddress}&destinationaddress=${destinationAddress}&dpt=${dpt}&value${value}`,
    );
    this.checkDeviceReachable(res, destinationAddress);
    return this.checkConnectionStatus(res);
  }

  async broadcastIndividualAddress(deviceAddress: string) {
    return this.runDeviceCommand("broadcastIndividualAddress", deviceAddress);
  }

  async assignNewIndividualAddress(deviceAddress: string) {
    return this.runDeviceCommand("assignNewIndividualAddress", deviceAddress);
  }

  async isDevicePresentInNetwork(deviceAddress: string) {
    return this.runDeviceCommand("isDevicePresentInNetwork", deviceAddress);
  }

  async isDeviceInProgramMode(deviceAddress: string) {
    return this.runDeviceCommand("isDeviceInProgramMode", deviceAddress);
  }

  async runDeviceCommand(command: DeviceAction, deviceAddress: string) {
    const res = await this.fetchAPI(command, `${command}?device=${deviceAddress}`);
    this.checkDeviceReachable(res, deviceAddress);
    return this.checkConnectionStatus(res);
  }

  async readDeviceAddressUsingSerial(serialNumber: string) {
    const res = await this.fetchAPI(
      "readDeviceAddressUsingSerial", // no itx
      `readDeviceAddressUsingSerial?serial=${serialNumber}`,
    );
    return this.checkConnectionStatus(res);
  }

  async writeNewDeviceAddressUsingSerial(serialNumber: string) {
    const res = await this.fetchAPI(
      "writeNewDeviceAddressUsingSerial", // no itx
      `writeNewDeviceAddressUsingSerial?serial=${serialNumber}`,
    );
    return this.checkConnectionStatus(res);
  }

  ////////////////////////////
  // MemoryBlock - Read/Write
  ////////////////////////////

  // For each Mem operation, max maxBytesReadable=52 bytes is (R)eadable/(W)ritable at a time
  // Cascading operations for more R/W bytes

  private isLikelyApduLengthError(msg: string): boolean {
    const m = (msg || "").toLowerCase();
    return (
      m.includes("apdu") ||
      m.includes("too long") ||
      m.includes("too large") ||
      m.includes("frame size") ||
      m.includes("max apdu") ||
      m.includes("payload") ||
      m.includes("length")
    );
  }

  private reduceChunkSize(reason: string) {
    const prev = this.maxBytesReadable;
    let next = Math.max(12, Math.floor(prev * 0.75));
    if (next === prev && prev > 12) next = prev - 1;
    this.maxBytesReadable = next;
    this.maxNibblesReadable = this.maxBytesReadable * 2;
    console.warn(`[KnxBridge] Reducing chunk size ${prev} -> ${next} due to: ${reason}`);
  }

  async readMemoryBlockFromDevice(
    deviceAddress: string,
    memoryOffset: string,
    addressSpace: number,
    showToast: boolean,
    duration?: number,
  ) {
    // var memoryOut = await this.readFromDeviceMemory(deviceAddress, memoryOffset, addressSpace);
    // return memoryOut;

    const targetPercent = duration ? this.percentUI + duration : 90;
    const percentDiff = targetPercent - this.percentUI;
    const noOfCalls = Math.ceil(addressSpace / this.maxBytesReadable);
    const percentIncrement = percentDiff / noOfCalls;

    let memoryOutput = "0x"; // no itx
    if (addressSpace > this.maxBytesReadable) {
      const startAddress = parseInt(memoryOffset, 16);
      for (let startIndex = 0; startIndex < addressSpace; startIndex += this.maxBytesReadable) {
        const offsetAddress = this.prefixHexType(this.decimalToHex(startAddress + startIndex));
        const output = await this.etsRetry(async () => {
          const out = await this.readFromDeviceMemory(
            deviceAddress,
            offsetAddress,
            Math.min(this.maxBytesReadable, addressSpace - startIndex),
          );
          if (!out || out.startsWith("Error") || !this.validHexTable(out)) {
            const msg = String(out || itx("KnxBridge.emptyresponse", "empty response"));
            if (this.isLikelyApduLengthError(msg) && this.maxBytesReadable > 12) {
              this.reduceChunkSize(msg);
            }
            throw new Error(`Memory_Read failed: ${msg}`);
          }
          return out;
        }, `Memory_Read @${offsetAddress}`);
        if (showToast) {
          this.increasePercent(Math.min(targetPercent, this.percentUI + percentIncrement));
        }
        memoryOutput += output.substring(2);
      }
    } else {
      memoryOutput = await this.etsRetry(async () => {
        const out = await this.readFromDeviceMemory(deviceAddress, memoryOffset, addressSpace);
        if (!out || out.startsWith("Error") || !this.validHexTable(out)) {
          const msg = String(out || itx("KnxBridge.emptyresponse", "empty response"));
          if (this.isLikelyApduLengthError(msg) && this.maxBytesReadable > 12) {
            this.reduceChunkSize(msg);
          }
          throw new Error(`Memory_Read failed: ${msg}`);
        }
        return out;
      }, `Memory_Read @${memoryOffset}`);
      if (showToast) {
        this.increasePercent();
      }
    }
    return memoryOutput;
  }

  async readMemoryBlockFromDevice2(
    deviceAddress: string,
    memoryOffset: string,
    addressSpace: number,
    showToast: boolean,
  ) {
    let memoryOutput = "0x"; // no itx

    const startAddress = parseInt(memoryOffset, 16);

    const offsetAddress = this.prefixHexType(this.decimalToHex(startAddress));
    memoryOutput = await this.readFromDeviceMemory(
      deviceAddress,
      offsetAddress,
      Math.min(this.maxBytesReadable, addressSpace),
      addressSpace,
    );
    if (showToast) {
      this.increasePercent();
    }
    if (!this.validHexTable(memoryOutput)) return "";

    return memoryOutput;
  }

  async writeMemoryBlockToDevice(
    deviceAddress: string,
    offsetAddress: string,
    data: string,
    showToast: boolean,
    duration?: number,
  ) {
    // await this.writeToDeviceMemory(deviceAddress, offsetAddress, data);
    // return;
    const ETS_MAX_CHUNK = this.strictETSMode ? 50 : 64;
    const targetPercent = duration ? this.percentUI + duration : 90;
    const percentDiff = targetPercent - this.percentUI;
    const noOfCalls = Math.ceil(data.length / this.maxNibblesReadable);
    const percentIncrement = percentDiff / noOfCalls;

    // 4 bit is 1 hex character hence multiply by 2 to get 1 byte
    if (data.length > this.maxNibblesReadable) {
      const offsetAddressInt = parseInt(offsetAddress, 16);
      for (let startIndex = 0, i = 0; startIndex < data.length; startIndex += this.maxNibblesReadable, i++) {
        //Byte = 2 charaters/nibbles, hence dividing position by two to get byte position
        const startAddress = this.prefixHexType(this.decimalToHex(offsetAddressInt + startIndex / 2));
        const dataChunk =
          data.length - startIndex > this.maxNibblesReadable
            ? data.substring(startIndex, startIndex + this.maxNibblesReadable)
            : data.substring(startIndex);
        await this.etsRetry(async () => {
          const ok = await this.writeToDeviceMemory(deviceAddress, startAddress, dataChunk);
          if (!ok) {
            const msg = itx("KnxBridge.writefailed", "write failed");
            if (this.isLikelyApduLengthError(msg) && this.maxBytesReadable > 12) this.reduceChunkSize(msg);
            throw new Error(`Memory_Write failed @${startAddress}`);
          }
          return ok;
        }, `Memory_Write @${startAddress}`);

        if (showToast) {
          this.increasePercent(Math.min(targetPercent, this.percentUI + percentIncrement));
        }
      }
    } else {
      const startAddress = this.prefixHexType(parseInt(offsetAddress, 16).toString(16).padStart(4, "0"));
      await this.etsRetry(async () => {
        const ok = await this.writeToDeviceMemory(deviceAddress, startAddress, data);
        if (!ok) {
          const msg = itx("KnxBridge.writefailed", "write failed");
          if (this.isLikelyApduLengthError(msg) && this.maxBytesReadable > 12) this.reduceChunkSize(msg);
          throw new Error(`Memory_Write failed @${startAddress}`);
        }
        return ok;
      }, `Memory_Write @${startAddress}`);
      if (showToast) {
        this.increasePercent();
      }
    }
    // this.toastTextUI = "";
  }

  // Read All Three Tables - (Address, Association, ComObj + Params) at once
  async readCompleteDeviceMemory(deviceAddress: string) {
    return await this.readMemoryBlockFromDevice(deviceAddress, this.deviceMemoryOffset, this.deviceMemorySize, false);
  }

  async getTableSizeList(
    deviceAddress: string,
    tableOffsets: TableOffsets,
    memorySegments: Array<any>,
    AP: any,
    LpStyle: string,
    setOfAddresses: Set<string>,
    associationList: Array<AssociationListEntry>,
  ) {
    let tableSizes: TableSizes = {
      address: 0,
      association: 0,
      groupObj: 0,
      param: 0,
      param2: 0,
    };

    const Static = firstChildWithTag(AP, "Static"); // no itx
    const sortedHexAddresses = Array.from(setOfAddresses).sort();

    // Address Table
    const countBytes = 2;
    if (LpStyle == MergedProcedure) {
      const newAddressTable = this.getAddressTableFromEstada(deviceAddress, setOfAddresses, LpStyle);
      tableSizes.address = newAddressTable.length / 2;
    } else {
      const addressCountHex = await this.readFromDeviceMemory(deviceAddress, tableOffsets.address, countBytes);
      if (addressCountHex) {
        const addressCount = parseInt(addressCountHex, 16);
        tableSizes.address = addressCount * 2 + countBytes;
      } else {
        const AddressTable = firstChildWithTag(Static, "AddressTable"); // no itx
        if (AddressTable && AddressTable?.a.MaxEntries) {
          tableSizes.address = parseInt(AddressTable?.a.MaxEntries);
        }
      }
    }

    // Association Table
    if (LpStyle == MergedProcedure) {
      const newAssociationTable = this.getAssociationTableFromEstada(sortedHexAddresses, associationList, LpStyle);
      tableSizes.association = newAssociationTable.length / 2;
    } else {
      const assoctableEntrySize = 4;
      const associationCountHex = await this.readFromDeviceMemory(deviceAddress, tableOffsets.association, countBytes);
      if (associationCountHex) {
        const associationCount = parseInt(associationCountHex, 16);
        tableSizes.association = associationCount * assoctableEntrySize + countBytes;
      } else {
        const AssociationTable = firstChildWithTag(Static, "AssociationTable"); // no itx
        if (AssociationTable && AssociationTable?.a.MaxEntries) {
          tableSizes.association = parseInt(AssociationTable?.a.MaxEntries);
        }
      }
    }

    // GrpObj Table
    // const comObjSegment = memorySegments.filter((item) => item?.LoadStateMachine == 3 || item.Id.startsWith("RS-03-"));
    // if (comObjSegment) {
    //   tableSizes.groupObj = comObjSegment[0].size;
    // } else {
    const objtableEntrySize = 2;
    const comObjSegment = memorySegments.filter((item) => item?.LoadStateMachine == 3 || item.Id.startsWith("RS-03-"));
    if (comObjSegment.length > 0) {
      tableSizes.groupObj = comObjSegment[0]?.size
        ? comObjSegment[0]?.size
        : comObjSegment[0]?.Size
          ? comObjSegment[0]?.Size
          : 0;
    } else if (LpStyle == MergedProcedure) {
      const comObjTable = AP.Static?.ComObjectTable;
      if (comObjTable) {
        let comObjTableSize = comObjTable.items.length;
        let maxComObjIndex = 0;
        comObjTable.items.forEach((item) => {
          const comObjIndex = parseInt(item.a.Number);
          if (maxComObjIndex < comObjIndex) maxComObjIndex = comObjIndex;
        });
        tableSizes.groupObj = maxComObjIndex * objtableEntrySize + countBytes;
      }
    } else {
      const groupObjCountHex = await this.readFromDeviceMemory(deviceAddress, tableOffsets.groupObj, countBytes);
      if (groupObjCountHex) {
        const groupObjCount = parseInt(groupObjCountHex, 16);
        tableSizes.groupObj = groupObjCount * objtableEntrySize + countBytes;
      } else {
        const ComObjectTable = firstChildWithTag(Static, "ComObjectTable"); // no itx
        if (ComObjectTable && ComObjectTable?.a.MaxEntries) {
          tableSizes.groupObj = parseInt(ComObjectTable?.a.MaxEntries);
        }
      }
    }

    // }

    // Param Table
    const paramSegment = memorySegments.filter((item) => item?.LoadStateMachine == 4 || item.Id.startsWith("RS-04-"));
    if (paramSegment.length > 0) {
      tableSizes.param = paramSegment[0]?.size
        ? paramSegment[0]?.size
        : paramSegment[0]?.Size
          ? paramSegment[0]?.Size
          : 0;
    }

    // Param2 Table
    const param2Segment = memorySegments.filter((item) => item?.LoadStateMachine == 5 || item.Id.includes("RS-05-"));
    if (param2Segment.length > 0) {
      tableSizes.param2 = param2Segment[0]?.size
        ? param2Segment[0]?.size
        : param2Segment[0]?.Size
          ? param2Segment[0]?.Size
          : 0;
    }

    return tableSizes;
  }

  //////////////////////
  // Read the 3 tables
  //////////////////////
  async readAddressTableFromDevice(deviceAddress: string, addressTableOffset: string, LpStyle: string) {
    let addressTable = "";
    if ((window as any).webSocketServer) {
      // AddressTable
      // For MergedProcedure count is stored in 2 Bytes, for ProductProcedure only one byte used
      // Rest is same
      const countBytes = LpStyle == MergedProcedure ? 2 : 1;
      const addressCountHex = await this.readFromDeviceMemory(deviceAddress, addressTableOffset, countBytes);
      if (addressCountHex) {
        const addressCount = parseInt(addressCountHex, 16);
        const addressSpace = addressCount * 2 + countBytes;
        addressTable = await this.readMemoryBlockFromDevice(deviceAddress, addressTableOffset, addressSpace, true, 5);
      } else {
        console.error("readAddressTableFromDevice error");
      }
    }
    return addressTable.substring(2);
  }

  async readAssociationTableFromDevice(deviceAddress: string, associationTableOffset: string, LpStyle: string) {
    let associationTable = "";
    if ((window as any).webSocketServer) {
      // AssociationTable
      // For MergedProcedure count is stored in 2 Bytes, for ProductProcedure only one byte used
      const countBytes = LpStyle == MergedProcedure ? 2 : 1;
      const tableEntrySize = LpStyle == MergedProcedure ? 4 : 2;
      const associationCountHex = await this.readFromDeviceMemory(deviceAddress, associationTableOffset, countBytes);
      if (associationCountHex) {
        const associationCount = parseInt(associationCountHex, 16);
        // For each table entry, MergedProcedure uses 4 bytes while ProductProcedure uses 2 bytes
        const addressSpace = associationCount * tableEntrySize + countBytes;
        associationTable = await this.readMemoryBlockFromDevice(
          deviceAddress,
          associationTableOffset,
          addressSpace,
          true,
          5,
        );
      }
    }
    return associationTable.substring(2);
  }

  async readGroupObjTableFromDevice(
    deviceAddress: string,
    groupObjTableOffset: string,
    LpStyle: string,
    size?: number,
  ) {
    let groupObjTable = "";
    if ((window as any).webSocketServer) {
      // GroupObjTable
      const countBytes = LpStyle == MergedProcedure ? 2 : 1;
      const tableEntrySize = LpStyle == MergedProcedure ? 2 : 4; // ProductProcedure uses 4 bytes
      const groupObjCountHex = await this.readFromDeviceMemory(deviceAddress, groupObjTableOffset, countBytes);
      if (groupObjCountHex) {
        const groupObjCount = parseInt(groupObjCountHex, 16);
        const addressSpace = groupObjCount * tableEntrySize + countBytes;

        groupObjTable = await this.readMemoryBlockFromDevice(
          deviceAddress,
          groupObjTableOffset,
          size ? size : addressSpace,
          true,
          5,
        );
      }
    }
    return groupObjTable.substring(2);
  }

  /////////////////////////////
  // Write/update the 3 tables
  /////////////////////////////
  async writeAllThreeTablesToDevice(
    device: Device,
    associationList: Array<AssociationListEntry>,
    memorySegments: Array<any>,
  ) {
    // Writing three segments of device memory:
    // 1. AddressTable
    // 2. AssociationTable
    // 3. ComObjTable

    const deviceAddress = device.fullPhysicalAddress; //"1.1.2"
    const setOfAddresses = new Set<string>();
    const setOfCobs = new Set<number>();
    let sortedHexAddresses: string[];
    let tableOffsets: TableOffsets;

    const AP = device.getApplicationProgram();
    const LpStyle = AP.a?.LoadProcedureStyle;

    const activeDeviceObjTypes = await this.getDeviceObjectTypes(device);
    console.log("[KnxBridge] Active Interface Object Types =", activeDeviceObjTypes);
    tableOffsets = await this.getMemTableOffsetsFromDevice(deviceAddress, AP, LpStyle, activeDeviceObjTypes);

    associationList.forEach((item) => {
      if (item.addr) {
        const hex = this.groupAddressToHex(item.addr);
        setOfAddresses.add(hex);
      }
      setOfCobs.add(item.cob);
    });

    // Get Device Descriptor (MaskVersion) From Device
    // If the Mask Supports AbsoluteSegments update the 3 Tables in sequence
    const maskValue = await this.getDescriptorFromDevice(deviceAddress);
    const validMask = maskValue ? this.BimM112Masks.some((mask) => mask == maskValue) : false;
    // const validMask = await this.isDeviceMaskWithAbsSegment(deviceAddress);
    if (validMask) {
      // Update 1: AddressTable
      await this.writeOrVerifyAddrTableToMemory(deviceAddress, tableOffsets.address, setOfAddresses, LpStyle);
      // Update 2: AssociationTable
      const sortedHexAddresses = Array.from(setOfAddresses).sort();
      await this.writeOrVerifyAssocTableToMemory(
        deviceAddress,
        tableOffsets.association,
        sortedHexAddresses,
        associationList,
        LpStyle,
        maskValue,
        AP,
      );
      // Update 3: GroupObjTable
      await this.writeOrVerifyComObjTableToMemory(
        device,
        deviceAddress,
        tableOffsets.groupObj,
        setOfCobs,
        associationList,
        memorySegments,
        LpStyle,
        maskValue,
      );
    }
  }

  getAddressTableFromEstada(deviceAddress: string, setOfAddresses: Set<string>, LpStyle: string): string {
    let addressTable = "";
    const isMerged = LpStyle == MergedProcedure;
    const sortedHexAddresses = Array.from(setOfAddresses).sort();
    const countBytes = isMerged ? 2 : 1;
    const deviceAddrCount = isMerged ? 0 : 1;
    const addressLengthHex = (sortedHexAddresses.length + deviceAddrCount).toString(16).padStart(countBytes * 2, "0"); // "00 05 => 2 bytes data or string length 2*2 = 4"
    const deviceAddressHex = this.deviceAddressToHex(deviceAddress);
    addressTable = addressLengthHex + (isMerged ? "" : deviceAddressHex) + sortedHexAddresses.join("");
    return addressTable;
  }

  getAssociationTableFromEstada(
    sortedHexAddresses: string[],
    associationList: Array<AssociationListEntry>,
    LpStyle: string,
  ): string {
    let associationTable = "";
    const countBytes = LpStyle == MergedProcedure ? 2 : 1;
    const tableEntrySize = LpStyle == MergedProcedure ? 4 : 2;

    const sortedAssociations = associationList
      .filter((item) => item.addr !== "")
      .sort((a, b) => a.cob - b.cob || a.addr.localeCompare(b.addr));
    const hexAssociations = sortedAssociations.map((assoc) => ({
      index: sortedHexAddresses.indexOf(this.groupAddressToHex(assoc.addr)) + 1,
      cob: assoc.cob,
      addr: assoc.addr,
      hexAddr: this.groupAddressToHex(assoc.addr),
      hexAssoc: `${this.decimalToHex(
        sortedHexAddresses.indexOf(this.groupAddressToHex(assoc.addr)) + 1,
        tableEntrySize,
      )}${this.decimalToHex(assoc.cob, tableEntrySize)}`,
    }));

    let firstLinks = new Array<string>();
    let additionalLinks = new Array<string>();

    let prevAssoc: any = { cob: -1 };
    hexAssociations.forEach((assoc) => {
      if (prevAssoc?.cob == assoc.cob) {
        additionalLinks.push(assoc.hexAssoc);
      } else {
        firstLinks.push(assoc.hexAssoc);
      }
      prevAssoc = assoc;
    });

    const assocLengthHex = associationList
      .filter((item) => item.addr !== "")
      .length.toString(16)
      .padStart(countBytes * 2, "0");
    associationTable = assocLengthHex + [...firstLinks, ...additionalLinks].join("");
    return associationTable;
  }

  async writeOrVerifyAddrTableToMemory(
    deviceAddress: string,
    addressTableOffset: string,
    setOfAddresses: Set<string>,
    LpStyle: string,
    mode?: any,
  ) {
    // Update Table 1: AddressTable
    // (includes addresses linked to signals)
    // Structure: Address count (n) followed by n addresses

    // 1st Byte : Address count (n)
    // Next Byte : Device Address
    // Next (n-1) Bytes: Group Addresses
    // Subsequent non zero values are garbage values.

    let addressTable = "";
    const isMerged = LpStyle == MergedProcedure;
    const sortedHexAddresses = Array.from(setOfAddresses).sort();
    const countBytes = isMerged ? 2 : 1;
    const deviceAddrCount = isMerged ? 0 : 1;
    const addressLengthHex = (sortedHexAddresses.length + deviceAddrCount).toString(16).padStart(countBytes * 2, "0"); // "00 05 => 2 bytes data or string length 2*2 = 4"
    const deviceAddressHex = this.deviceAddressToHex(deviceAddress);
    addressTable = addressLengthHex + (isMerged ? "" : deviceAddressHex) + sortedHexAddresses.join("");

    let addressTableSizeInNibbles = 2;
    // if (mode == downloadMode.parametersVerifyOnly) {
    //   const addressCountHex = await this.readFromDeviceMemory(deviceAddress, addressTableOffset, countBytes);
    //   if (addressCountHex) {
    //     addressTableSizeInNibbles = (parseInt(addressCountHex, 16) * 2 + countBytes) * 2;
    //   }
    // }
    if (addressTable.length <= addressTableSizeInNibbles && this.maxNibblesReadable >= addressTableSizeInNibbles) {
      if (addressTable.length < addressTableSizeInNibbles) {
        addressTable += "0".repeat(addressTableSizeInNibbles - addressTable.length);
      }
    }
    // else if (addressTable.length < this.maxNibblesReadable)
    //   addressTable += "0".repeat(this.maxNibblesReadable - addressTable.length);

    if (mode != downloadMode.parametersVerifyOnly) {
      //  && parseInt(addressLengthHex, 16) != 0

      await this.writeMemoryBlockToDevice(deviceAddress, addressTableOffset, addressTable.substring(0, 2), true, 1);
      addressTableOffset = this.prefixHexType((parseInt(addressTableOffset) + 3).toString(16));
      addressTable = addressTable.substring(6);
      // if (addressTableOffset == "0x4000") {
      //   addressTableOffset = "0x4003";
      // }
      if (addressTable.length > 0)
        await this.writeMemoryBlockToDevice(deviceAddress, addressTableOffset, addressTable, true, 1);
    }

    // Verify that memory block is correctly written
    if (mode == downloadMode.parametersVerifyOnly || mode == downloadMode.parametersDownloadAndVerify) {
      this.toastTextUI = state.translate(
        "knxBridge.verifyAddrTableMemory",
        "Executing LoadProcedure: Verifying Addresses",
      );
      this.increasePercent();
      const addressTableFromMemory = await this.readAddressTableFromDevice(deviceAddress, addressTableOffset, LpStyle);
      console.log("[KnxBridge] addressTable differences w/ current memory:");
      if (addressTableFromMemory.length == 0) {
        this.lastDeviceMemoryStats.addrMemDiff = -1;
      } else {
        if (addressTableFromMemory.length < addressTable.length) {
          addressTable = addressTable.substring(0, addressTableFromMemory.length);
        }
        const diffAddressTable = this.diffChecker(addressTableFromMemory, addressTable);
        console.log(diffAddressTable);
        this.lastDeviceMemoryStats.addrMemDiff = this.diffCount(addressTableFromMemory, addressTable);
        this.lastDeviceMemoryStats.totalMemDiff += this.lastDeviceMemoryStats.addrMemDiff;
      }
    }
    // this.toastTextUI = "";

    return Number.isNaN(parseInt(addressTableOffset))
      ? ""
      : this.decimalToHex(parseInt(addressTableOffset) + (addressTable.length + 2) / 2);
  }

  async writeOrVerifyAssocTableToMemory(
    deviceAddress: string,
    associationTableOffset: string,
    sortedHexAddresses: string[],
    associationList: Array<AssociationListEntry>,
    LpStyle: string,
    maskVersion: string,
    AP: any,
    mode?: any,
  ) {
    // Update Table 2: AssociationTable
    // (includes all signal-groupAddress associations)
    // Structure: Association count (n) followed by n associations

    // 1st Byte : Association count (n)
    // Next (n*2) Bytes: Pair of bytes
    // - In each pair, First byte is signal number
    // - Second byte is Group Address Index from Address Table

    // Entries Sorted according to signal number
    // All 1st links for each signal are added into table, followed by additional links of signals that have > 1 group address links
    // Subsequent non zero values are garbage values.

    let associationTable = "";
    const countBytes = LpStyle == MergedProcedure ? 2 : 1;
    const tableEntrySize = LpStyle == MergedProcedure ? 4 : 2;

    // Association Table
    const assoctableEntrySize = 4;
    let assocTableSizeInNibbles = 4;

    // if (mode == downloadMode.parametersVerifyOnly) {
    //   const associationCountHex = await this.readFromDeviceMemory(deviceAddress, associationTableOffset, countBytes);
    //   if (associationCountHex) {
    //     assocTableSizeInNibbles = (parseInt(associationCountHex, 16) * assoctableEntrySize + countBytes) * 2;
    //   }
    // }

    const sortedAssociations = associationList
      .filter((item) => item.addr !== "")
      .sort((a, b) => a.cob - b.cob || a.addr.localeCompare(b.addr));
    const hexAssociations = sortedAssociations.map((assoc) => ({
      index: sortedHexAddresses.indexOf(this.groupAddressToHex(assoc.addr)) + 1,
      cob: assoc.cob,
      addr: assoc.addr,
      hexAddr: this.groupAddressToHex(assoc.addr),
      hexAssoc: `${this.decimalToHex(
        sortedHexAddresses.indexOf(this.groupAddressToHex(assoc.addr)) + 1,
        tableEntrySize,
      )}${this.decimalToHex(assoc.cob, tableEntrySize)}`,
    }));

    let firstLinks = new Array<string>();
    let additionalLinks = new Array<string>();
    let firstLinksMap: Record<string, string>;

    let prevAssoc: any = { cob: -1 };
    hexAssociations.forEach((assoc) => {
      if (prevAssoc?.cob == assoc.cob) {
        additionalLinks.push(assoc.hexAssoc);
      } else {
        firstLinks.push(assoc.hexAssoc);
      }
      prevAssoc = assoc;
    });

    const isBcu2Mask = this.Bcu2Masks.some((mask) => mask.toLowerCase() == maskVersion.toLowerCase());
    if (isBcu2Mask) {
      // In use for MV-0021 devices; potentially applicable for other unsupported devices with older masks
      firstLinksMap = firstLinks.reduce(
        (acc, item) => {
          const value = item.slice(0, 2); // first 2 characters as addressTable Index
          const key = item.slice(2); // last 2 characters as cob
          acc[key] = value;
          return acc;
        },
        {} as Record<string, string>,
      );

      const Static = firstChildWithTag(AP, "Static"); // no itx
      const ComObjectTable = firstChildWithTag(Static, "ComObjectTable"); // no itx
      let comObjTableSize = ComObjectTable.items.length;
      let maxComObjIndex = 0;
      let tableContent = "";

      ComObjectTable.items.sort((a: any, b: any) => {
        const indexA = parseInt(a.a.Number, 10);
        const indexB = parseInt(b.a.Number, 10);
        return indexA - indexB; // ascending
      });

      ComObjectTable.items.forEach((item) => {
        const comObjIndex = parseInt(item.a.Number);
        const comObjIndexHex = this.decimalToHex(comObjIndex, 2);
        if (maxComObjIndex < comObjIndex) maxComObjIndex = comObjIndex;
        if (firstLinksMap.hasOwnProperty(comObjIndexHex)) {
          tableContent += firstLinksMap[comObjIndexHex] + comObjIndexHex;
        } else {
          tableContent += "fe" + comObjIndexHex; // FE indicates no association
        }
      });
      tableContent = tableContent.toLowerCase();

      const assocLengthHex = this.decimalToHex(maxComObjIndex + 1, 2);
      associationTable = assocLengthHex + tableContent;
    } else {
      const assocLengthHex = associationList
        .filter((item) => item.addr !== "")
        .length.toString(16)
        .padStart(countBytes * 2, "0");
      associationTable = assocLengthHex + [...firstLinks, ...additionalLinks].join("");
    }

    // console.log("[KnxBridge] Constructed associationTable = " + associationTable);
    // Purpose of writing 0's here is to clean garbage values in memory
    // Garbage values can conflict with actual addresses, leading to invalid memory#

    if (associationTable.length <= assocTableSizeInNibbles && this.maxNibblesReadable >= assocTableSizeInNibbles) {
      if (associationTable.length < assocTableSizeInNibbles) {
        associationTable += "0".repeat(assocTableSizeInNibbles - associationTable.length);
      }
    }

    // else if (associationTable.length < this.maxNibblesReadable)
    //   associationTable += "0".repeat(this.maxNibblesReadable - associationTable.length);
    // else if (associationTable.length < this.maxNibblesReadable * 2)
    //   associationTable += "0".repeat(this.maxNibblesReadable * 2 - associationTable.length);

    if (mode != downloadMode.parametersVerifyOnly) {
      //  && parseInt(assocLengthHex, 16) != 0
      await this.writeMemoryBlockToDevice(deviceAddress, associationTableOffset, associationTable, true, 5);
    }

    // Verify that memory block is correctly written
    if (mode == downloadMode.parametersVerifyOnly || mode == downloadMode.parametersDownloadAndVerify) {
      this.toastTextUI = state.translate(
        "knxBridge.verifyAssocTableMemory",
        "Executing LoadProcedure: Verifying Address Associations",
      );
      this.increasePercent();
      const associationTableFromMemory = await this.readAssociationTableFromDevice(
        deviceAddress,
        associationTableOffset,
        LpStyle,
      );
      console.log("[KnxBridge] associationTable differences w/ current memory:");

      if (associationTableFromMemory.length == 0) {
        this.lastDeviceMemoryStats.assocMemDiff = -1;
      } else {
        if (associationTableFromMemory.length < associationTable.length) {
          associationTable = associationTable.substring(0, associationTableFromMemory.length);
        }
        const diffAssociationTable = this.diffChecker(associationTableFromMemory, associationTable);
        console.log(diffAssociationTable);
        this.lastDeviceMemoryStats.assocMemDiff = this.diffCount(associationTableFromMemory, associationTable);
        this.lastDeviceMemoryStats.totalMemDiff += this.lastDeviceMemoryStats.assocMemDiff;
      }
    }
    // this.toastTextUI = "";
  }

  async writeOrVerifyComObjTableToMemory(
    device: Device,
    deviceAddress: string,
    groupObjTableOffset: string,
    setOfCobs: Set<number>,
    associationList: Array<AssociationListEntry>,
    memorySegments: Array<any>,
    LpStyle: string,
    maskVersion: string,
    mode?: any,
  ) {
    // Update Table 3: GroupObjTable
    // (includes all comObjects information, including disabled ComObjs)
    // Structure: comObjects count (n) followed by n comObjects

    ///////////////////////////////////////////////
    // For Bimm112 Masks:
    // 1st Byte : comObjects count (n)
    // Next 3 Bytes: RAM Init Flags

    // Next (n*4) Bytes:
    // - 1st Byte: Data Pointer (referenced from AbsoluteSegment base64 data in device's applicationProgramXml)
    // - 2nd byte: ConfigOctet
    //    Bit 7 Update Enable
    //    6 Transmit Enable
    //    5 Segment Selector Type Enable
    //    4 Write Enable
    //    3 Read Enable
    //    2 Communication Enable
    //    1-0 Transmission Priority 0
    // - 3rd byte: TypeOctet (Object Size index value referenced from TypeOctetTable)
    // - 4th byte: 07 or 08 (again referenced from base64)

    // We modify only ConfigOctet and TypeOctet as per Device Signal Information
    // Rest of ComObjTable remains unmodified for now

    // Below issues are now fixed so now there is perfect match of constructed ComObjTable with memory map.
    // Unidentified Flags in Application (All Fixed)
    // - Segment Selector Type (5th Bit) (FIXED . 12.08.2024)
    // - Read Flag (3rd Bit)             (FIXED . 16.08.2024)

    ///////////////////////////////////////////////
    // For Bcu2 Masks:

    // 1st Byte : comObjects count (n)
    // Next Byte: RAM Init Flags (For now found as 00)

    // Next (n*3) Bytes:
    // - 1st Byte: ComObject Number (0 to n-1)
    // - 2nd byte: ConfigOctet
    //    Bit 7 Update Enable
    //    6 Transmit Enable
    //    5 Segment Selector Type Enable
    //    4 Write Enable
    //    3 Read Enable
    //    2 Communication Enable
    //    1-0 Transmission Priority 0
    // - 3rd byte: set as 00 except last byte set as 07 (referenced from base64)
    // TypeOctet ???

    let groupObjTableString = "";
    const applicationProgram = device.getApplicationProgram();
    if (applicationProgram) {
      const Static = firstChildWithTag(applicationProgram, "Static"); // no itx

      // This will work only for ProductProcedure i.e. with AbsoluteSegments in "Code" of ApplicationProgram.
      let SegmentType = "0";
      let ComObjTableSize;
      if (applicationProgram.a.LoadProcedureStyle == ProductProcedure) {
        const LPs = firstChildWithTag(Static, "LoadProcedures"); // no itx
        const LP = firstChildWithTag(LPs, "LoadProcedure"); // no itx
        if (LP?.LdCtrlAbsSegment) {
          const segment = LP?.LdCtrlAbsSegment;
          SegmentType = segment.a.SegType;
          if (segment.a.Size) ComObjTableSize = parseInt(segment.a.Size);
        }
      }

      const ComObjectTable = firstChildWithTag(Static, "ComObjectTable"); // no itx
      const CodeSegmentId = ComObjectTable.a.CodeSegment;
      let comObjCode: any;
      if (CodeSegmentId) {
        comObjCode = memorySegments.find((i) => i.Id == CodeSegmentId);
      } else {
        comObjCode = memorySegments.find((i) => i.Id == "RS-03-00000");
      }
      console.log("[KnxBridge] comObjCode =", comObjCode);
      if (comObjCode && comObjCode?.Data) {
        groupObjTableString = this.uint8ArrayToHex(comObjCode.Data);
      }

      // Get maximum ComObject Index
      let comObjTableSize = ComObjectTable.items.length;
      let maxComObjIndex = 0;
      ComObjectTable.items.forEach((item) => {
        const comObjIndex = parseInt(item.a.Number);
        if (maxComObjIndex < comObjIndex) maxComObjIndex = comObjIndex;
      });

      if (groupObjTableString) {
        let groupObjTable = groupObjTableString;
        // const oldGroupObjTable = groupObjTable;
        // // console.log(oldGroupObjTable);

        if (groupObjTable) {
          const commEnableBitmask: number = 0x04;
          const commDisableBitmask: number = ~commEnableBitmask;

          const isBcu2Mask = this.Bcu2Masks.some((mask) => mask.toLowerCase() == maskVersion.toLowerCase());
          const cobStartOffset = isBcu2Mask ? 4 : 8;
          const cobNibbles = isBcu2Mask ? 6 : 8;

          for (
            let cob = 0, cobStartAddr = cobStartOffset;
            cobStartAddr < groupObjTable.length;
            cob++, cobStartAddr += cobNibbles
          ) {
            const configOctetOffset = cobStartAddr + 2;
            const configOctet = groupObjTable.substring(configOctetOffset, configOctetOffset + 2);
            let hexNumber: number = parseInt(configOctet, 16);
            let newConfigOctet;
            let newTypeOctet = "";

            // const oldConfigOctetHex = parseInt(configOctet, 16);
            const oldSegSelbit5 = (hexNumber >> 5) & 1;
            const oldTransmitbit6 = (hexNumber >> 6) & 1;

            if (setOfCobs.has(cob)) {
              const signal = associationList.find((item) => item.cob == cob);
              if (signal) {
                // Construct Configuration Octet using asoociated ComObject flags
                // (Fixed 12.08.2024) MWE 09.08.2024: Set Actual SegmentSelectorType
                // Now setting SegType from LoadProcedure
                // (Fixed 16.08.2024) MWE 09.08.2024: Set Actual ReadFlag
                // Earlier we set Read Flag as true for identified signals with group Addresses association
                // Now setting actual ReadFlag from ComObj

                const UpdateFlag7 = signal.a?.UpdateFlag.toLowerCase() == "enabled" ? "1" : "0";
                const TransmitFlag6 = signal.a?.TransmitFlag.toLowerCase() == "enabled" ? "1" : "0";
                const SegmentSelectorTypeFlag5 = isBcu2Mask ? "1" : SegmentType;
                const WriteFlag4 = signal.a?.WriteFlag.toLowerCase() == "enabled" ? "1" : "0"; // signal.addr ? "1" : "0";
                const ReadFlag3 = signal.a?.ReadFlag.toLowerCase() == "enabled" ? "1" : "0"; // signal.addr ? "1" : "0";
                const CommunicationFlag2 = signal.addr ? "1" : "0";
                const TransmitPriority10 = this.hexToBinaryString(configOctet).substring(6, 8); // Using Value from groupObjTable
                // majorly it is low priority i.e. value "11" (although no Reference found in applicationProgramXml)

                const configOctetBin =
                  UpdateFlag7 +
                  TransmitFlag6 +
                  SegmentSelectorTypeFlag5 +
                  WriteFlag4 +
                  ReadFlag3 +
                  CommunicationFlag2 +
                  TransmitPriority10;
                newConfigOctet = this.binaryToHex(configOctetBin);

                const ObjectSizeIndex = this.typeOctetTable.indexOf(signal.a?.ObjectSize.toLowerCase());
                if (ObjectSizeIndex > -1) {
                  newTypeOctet = this.decimalToHex(ObjectSizeIndex);
                }

                // Modifying config and type Octet
                if (newTypeOctet && !isBcu2Mask) {
                  groupObjTable =
                    groupObjTable.slice(0, configOctetOffset) +
                    newConfigOctet +
                    newTypeOctet +
                    groupObjTable.slice(configOctetOffset + 4);
                } else {
                  groupObjTable =
                    groupObjTable.slice(0, configOctetOffset) +
                    newConfigOctet +
                    groupObjTable.slice(configOctetOffset + 2);
                }
              }
            } else {
              if (cobStartAddr <= ComObjTableSize || cob <= maxComObjIndex) {
                if (isBcu2Mask) {
                  const transmitBitMask = 1 << 6; // sixth bit
                  hexNumber &= ~transmitBitMask; // clear bit data
                }
                newConfigOctet = this.decimalToHex(hexNumber & commDisableBitmask);
              } else {
                newConfigOctet = this.decimalToHex(hexNumber);
              }
              groupObjTable =
                groupObjTable.slice(0, configOctetOffset) + newConfigOctet + groupObjTable.slice(configOctetOffset + 2);
            }
          }

          if (isBcu2Mask) {
            groupObjTable = groupObjTable.slice(0, groupObjTable.length - 2) + "d3"; // no itx
          }

          if (mode != downloadMode.parametersVerifyOnly) {
            await this.writeMemoryBlockToDevice(deviceAddress, groupObjTableOffset, groupObjTable, true, 20);
          }
        }

        // Verify that memory block is correctly written
        if (mode == downloadMode.parametersVerifyOnly || mode == downloadMode.parametersDownloadAndVerify) {
          this.toastTextUI = state.translate(
            "knxBridge.verifyComObjTableMemory",
            "Executing LoadProcedure: Verifying ComObjects",
          );
          this.increasePercent();
          const groupObjTableFromMemory = await this.readGroupObjTableFromDevice(
            deviceAddress,
            groupObjTableOffset,
            LpStyle,
            comObjCode.size,
          );
          // console.log("[KnxBridge] Constructed groupObjTable= " + groupObjTable);
          console.log("[KnxBridge] groupObjTable differences w/ current memory:");

          if (groupObjTableFromMemory.length == 0) {
            this.lastDeviceMemoryStats.comObjMemDiff = -1;
          } else {
            if (groupObjTableFromMemory.length < groupObjTable.length) {
              groupObjTable = groupObjTable.substring(0, groupObjTableFromMemory.length);
            }
            const diffGroupObjTable = this.diffChecker(groupObjTableFromMemory, groupObjTable);
            console.log(diffGroupObjTable);
            this.lastDeviceMemoryStats.comObjMemDiff = this.diffCount(groupObjTableFromMemory, groupObjTable);
            this.lastDeviceMemoryStats.totalMemDiff += this.lastDeviceMemoryStats.comObjMemDiff;
          }
        }
        // this.toastTextUI = "";
      }
    }
  }

  async writeOrVerifyComObjTableToRelMemory(
    device: Device,
    deviceAddress: string,
    AP: any,
    groupObjTableOffset: string,
    memorySegments: Array<any>,
    setOfCobs: Set<number>,
    associationList: Array<AssociationListEntry>,
    LpStyle: string,
    mode?: any,
  ) {
    // Update Table 3: GroupObjTable (Applicable for only MergedProcedures)
    // (includes all active comObjects information, no info on disabled ComObjs)
    // Structure: comObjects count (n) followed by n comObjects

    // First 2 Bytes : comObjects count (n)

    // Next (n*2) Bytes:
    // - 1st byte: ConfigOctet
    //    Bit 7 Update Enable
    //    6 Transmit Enable
    //    5 Segment Selector Type Enable
    //    4 Write Enable
    //    3 Read Enable
    //    2 Communication Enable
    //    1-0 Transmission Priority 0
    // - 2nd byte: TypeOctet (Object Size index value referenced from TypeOctetTable)

    // We modify ConfigOctet and TypeOctet as per Device Signal Information
    // If signal active we write octets to memory otherwise both octets are erased (i.e. 00 00 written instead)

    // Below issues are now fixed so now there is perfect match of constructed ComObjTable with memory map.

    // NEU 22.05.2025: Changes regarding relative memory segments

    let comSegment;
    let groupObjTableFromAP = "";
    // LoadStateMachine = 3  stores comObjects
    comSegment = memorySegments.find((item) => item?.LoadStateMachine == 3 || item.Id.split("-")[1] == "03");

    if (comSegment && comSegment?.Data) {
      // If table exist as Relative Segment
      let groupObjTableBin = comSegment?.Data;
      groupObjTableFromAP = this.uint8ArrayToHex(groupObjTableBin);
    } else {
      // If not then construct table using ComObject items under Static section of Application XML
      const comObjTable = AP.Static?.ComObjectTable;
      let comObjTableSize = comObjTable.items.length;
      let maxComObjIndex = 0;
      comObjTable.items.forEach((item) => {
        const comObjIndex = parseInt(item.a.Number);
        if (maxComObjIndex < comObjIndex) maxComObjIndex = comObjIndex;
      });
      const tableSizeHex = maxComObjIndex.toString(16).padStart(4, "0");
      groupObjTableFromAP = tableSizeHex + "0000".repeat(maxComObjIndex);
    }

    if (groupObjTableFromAP.length == 0) {
      this.lastDeviceMemoryStats.comObjMemDiff = -1;
    } else {
      let groupObjTableString = (" " + groupObjTableFromAP).slice(1); // Creating a deepcopy so original string does not get modified.
      const applicationProgram = device.getApplicationProgram();
      if (applicationProgram) {
        let SegmentType = "0"; // For MergedProcedure it is always 0
        if (groupObjTableString) {
          let groupObjTable = groupObjTableString;
          const oldGroupObjTable = groupObjTable;

          if (groupObjTable) {
            const commEnableBitmask: number = 0x04;
            const commDisableBitmask: number = ~commEnableBitmask;

            for (let cob = 1, cobStartAddr = 4; cobStartAddr < groupObjTable.length; cob++, cobStartAddr += 4) {
              const configOctetOffset = cobStartAddr;
              const configOctet = groupObjTable.substring(configOctetOffset, configOctetOffset + 2);
              let hexNumber: number = parseInt(configOctet, 16);
              let newConfigOctet;
              let newTypeOctet = "";

              if (setOfCobs.has(cob)) {
                const signal = associationList.find((item) => item.cob == cob);
                if (signal) {
                  const UpdateFlag7 = signal.a?.UpdateFlag.toLowerCase() == "enabled" ? "1" : "0";
                  const TransmitFlag6 = signal.a?.TransmitFlag.toLowerCase() == "enabled" ? "1" : "0";
                  const SegmentSelectorTypeFlag5 = SegmentType;
                  const WriteFlag4 = signal.a?.WriteFlag.toLowerCase() == "enabled" ? "1" : "0"; // signal.addr ? "1" : "0";
                  const ReadFlag3 = signal.a?.ReadFlag.toLowerCase() == "enabled" ? "1" : "0"; // signal.addr ? "1" : "0";
                  const CommunicationFlag2 = signal.addr ? "1" : "0";
                  const priority = signal.a?.Priority ? this.priorityIndex[signal.a?.Priority] : this.priorityIndex.Low;
                  const TransmitPriority10 = priority ? priority : this.priorityIndex.Low;
                  // By default it is low priority i.e. value "11"

                  const configOctetBin =
                    UpdateFlag7 +
                    TransmitFlag6 +
                    SegmentSelectorTypeFlag5 +
                    WriteFlag4 +
                    ReadFlag3 +
                    CommunicationFlag2 +
                    TransmitPriority10;
                  newConfigOctet = this.binaryToHex(configOctetBin);

                  const ObjectSizeIndex = this.typeOctetTable.indexOf(signal.a?.ObjectSize.toLowerCase());
                  if (ObjectSizeIndex > -1) {
                    newTypeOctet = this.decimalToHex(ObjectSizeIndex);
                  }

                  // Modifying config and type Octet
                  if (newTypeOctet) {
                    groupObjTable =
                      groupObjTable.slice(0, configOctetOffset) +
                      newConfigOctet +
                      newTypeOctet +
                      groupObjTable.slice(configOctetOffset + 4);
                  } else {
                    groupObjTable =
                      groupObjTable.slice(0, configOctetOffset) +
                      newConfigOctet +
                      groupObjTable.slice(configOctetOffset + 2);
                  }
                }
              }
              // else {
              //   groupObjTable =
              //     groupObjTable.slice(0, configOctetOffset) + "0000" + groupObjTable.slice(configOctetOffset + 4); // configandType Octet erased as cob inactive
              // }
            }
            if (mode != downloadMode.parametersVerifyOnly) {
              await this.writeMemoryBlockToDevice(deviceAddress, groupObjTableOffset, groupObjTable, true, 20);
            }
          }

          // Verify that memory block is correctly written
          if (mode == downloadMode.parametersVerifyOnly || mode == downloadMode.parametersDownloadAndVerify) {
            this.toastTextUI = state.translate(
              "knxBridge.verifyComObjTableMemory",
              "Executing LoadProcedure: Verifying ComObjects",
            );
            this.increasePercent();
            const groupObjTableFromMemory = await this.readGroupObjTableFromDevice(
              deviceAddress,
              groupObjTableOffset,
              LpStyle,
            );

            console.log("[KnxBridge] Constructed groupObjTable= " + groupObjTable);
            console.log("[KnxBridge] groupObjTable differences w/ current memory:");
            if (groupObjTableFromMemory.length < groupObjTable.length) {
              groupObjTable = groupObjTable.substring(0, groupObjTableFromMemory.length);
            }
            const diffGroupObjTable = this.diffChecker(groupObjTableFromMemory, groupObjTable);
            console.log(diffGroupObjTable);
            this.lastDeviceMemoryStats.comObjMemDiff = this.diffCount(groupObjTableFromMemory, groupObjTable);
            this.lastDeviceMemoryStats.totalMemDiff += this.lastDeviceMemoryStats.comObjMemDiff;
          }
          // this.toastTextUI = "";
        }
      }
    }
  }

  async writeAbsoluteSegmentSliceToMemory(
    deviceAddress: string,
    absSegment: CodeSegment,
    writeAddress: number,
    size: number,
    verify: boolean,
    strictETS = false,
  ) {
    const baseAddr = absSegment.Address;
    const offset = writeAddress - baseAddr;

    if (absSegment?.Data) {
      const dataSlice = absSegment.Data.slice(offset, offset + size);
      const maskSlice = absSegment.Mask ? absSegment.Mask.slice(offset, offset + size) : undefined;

      let finalData = new Uint8Array(size);

      const needsMerge = strictETS && maskSlice && maskSlice.some((m) => m !== 0xff && m !== 0x00);

      if (needsMerge) {
        // ETS-style: read existing memory first
        const memHex = await this.etsRetry(
          () =>
            this.readMemoryBlockFromDevice(
              deviceAddress,
              this.prefixHexType(writeAddress.toString(16).padStart(4, "0")),
              size,
              true,
              10,
            ),
          `MemoryExtended_Read @${writeAddress.toString(16)}`,
        );

        const memBytes = this.hexToUint8Array(this.removePrefixHexType(memHex));

        for (let i = 0; i < size; i++) {
          const mask = maskSlice[i];
          finalData[i] = (memBytes[i] & ~mask) | (dataSlice[i] & mask);
        }
      } else {
        // Fast path (ETS does this too)
        for (let i = 0; i < size; i++) {
          finalData[i] = maskSlice ? dataSlice[i] & maskSlice[i] : dataSlice[i];
        }
      }

      const hexData = this.uint8ArrayToHex(finalData);

      await this.writeMemoryBlockToDevice(
        deviceAddress,
        this.prefixHexType(writeAddress.toString(16).padStart(4, "0")),
        hexData,
        verify,
        30,
      );
    }
  }

  async writeOrVerifyActualParamsToMemory(
    device: Device,
    deviceAddress: string,
    paramSegmentAddr: string,
    memorySegments: Array<CodeSegment>,
    LpStyle: string,
    maskVersion: string,
    paramTableInMem?: string,
    mode?: any,
  ) {
    let paramSegment: CodeSegment | undefined;
    let paramAddrHex = "?????"; // no itx

    if (LpStyle == MergedProcedure) {
      // LoadStateMachine = 4  stores Device Parameters
      paramSegment = memorySegments.find((item) => item?.LoadStateMachine == 4 || item.Id.split("-")[1] == "04");
      paramAddrHex = paramSegmentAddr;
    } else if (LpStyle == ProductProcedure) {
      paramSegment = memorySegments.find((item) => item.Address.toString() == paramSegmentAddr.toString());
      if (paramSegment) {
        paramAddrHex = this.prefixHexType(paramSegment.Id.split("-")[1]);
      }
    }

    if (paramSegment && paramSegment?.Data) {
      let actualData = paramSegment.Data; // Current configurations
      // const baseData = paramSegment.BaseData; // From AP (default data)
      let paramTable: any;
      let startIndex = 0;
      if (LpStyle == MergedProcedure) {
        this.toastTextUI = state.translate(
          "knxBridge.verifyParamsTableMemory",
          "Executing LoadProcedure: Verifying Parameters",
        );
        this.increasePercent();
        paramTable = this.uint8ArrayToHex(actualData);

        if (mode == downloadMode.parametersVerifyOnly) {
          if (!paramTableInMem) {
            paramTableInMem = await this.readMemoryBlockFromDevice(
              deviceAddress,
              paramAddrHex,
              actualData.length,
              true,
              10,
            );
          }
          if (paramTableInMem.length == 0) {
            this.lastDeviceMemoryStats.paramMemDiff = -1;
          } else {
            paramTableInMem = this.removePrefixHexType(paramTableInMem);
            paramTable = this.uint8ArrayToHex(actualData);

            const paramTableInMemBin = this.hexToUint8Array(paramTableInMem);

            paramSegment.debugDifferences(paramTableInMemBin);

            console.log("[KnxBridge] Diff of actual params vs params in memory: ");
            if (paramTableInMem.length < paramTable.length) {
              console.log(`[KnxBridge] Table Lengths: ${paramTableInMem.length} ${paramTable.length}`);
              paramTable = paramTable.substring(0, paramTableInMem.length);
            } else {
              paramTable = paramTable + paramTableInMem.substring(paramTable.length);
            }
            const differenceStr1 = this.diffChecker(paramTableInMem, paramTable);
            console.log(differenceStr1);
            this.lastDeviceMemoryStats.paramMemDiff = this.diffCount(paramTableInMem, paramTable);
            this.lastDeviceMemoryStats.totalMemDiff += this.lastDeviceMemoryStats.paramMemDiff;

            const differenceStr = this.diffCheckerBin(paramTableInMemBin, actualData);
            console.log("[KnxBridge] Difference: Param In Actual Device Memory (LEFT) vs Param In Estada (RIGHT): ");
            console.log(differenceStr);

            return;
          }
        }

        // const currentMemTableBin = this.hexToUint8Array(paramTableInMem);
        // for (const [i, value] of currentMemTableBin.entries()) {
        //   if (value != 0) {
        //     // if (startIndex == 0) startIndex = i;
        //     actualData[i] = actualData[i] | currentMemTableBin[i];
        //   }
        // }
      } else {
        paramTable = this.uint8ArrayToHex(actualData);
      }
      console.log(paramTable);
      this.paramTableString = paramTable;
      this.absoluteSegmentId = paramSegment.Id;
      const isBcu2Mask = this.Bcu2Masks.some((mask) => mask.toLowerCase() == maskVersion.toLowerCase());
      if (isBcu2Mask) {
        paramTable = paramTable.slice(0, paramTable.length - 2) + "75"; // Setting last byte to 0x75 as seen in memory dumps
      }

      if (mode != downloadMode.parametersVerifyOnly) {
        this.toastTextUI = state.translate(
          "knxBridge.writeActualParamsToMemory",
          "Executing LoadProcedure: Writing Parameters to Memory",
        );
        if (LpStyle == MergedProcedure) {
          const paramAddrHexNew = this.prefixHexType(
            (parseInt(paramAddrHex, 16) + startIndex).toString(16).padStart(4, "0"),
          );
          await this.writeMemoryBlockToDevice(
            deviceAddress,
            paramAddrHexNew,
            paramTable.substring(startIndex * 2),
            true,
            30,
          );
        } else {
          await this.writeMemoryBlockToDevice(deviceAddress, paramAddrHex, paramTable, true, 30);
        }

        // const newTableInMem = await this.readMemoryBlockFromDevice(
        //   deviceAddress,
        //   paramAddrHex,
        //   paramTable.length / 2,
        //   true,
        //   10
        // );
        // console.log(newTableInMem);
      }

      // Verify that memory block is correctly written
      if (
        LpStyle != MergedProcedure &&
        (mode == downloadMode.parametersVerifyOnly || mode == downloadMode.parametersDownloadAndVerify)
      ) {
        this.toastTextUI = state.translate(
          "knxBridge.verifyParamsTableMemory",
          "Executing LoadProcedure: Verifying Parameters",
        );
        this.increasePercent();
        let newMemTableFromMem = await this.readMemoryBlockFromDevice(
          deviceAddress,
          paramAddrHex,
          actualData.length,
          true,
          10,
        );
        newMemTableFromMem = this.removePrefixHexType(newMemTableFromMem);

        console.log("[KnxBridge] Diff of actual params vs params in memory: ");
        if (newMemTableFromMem.length == 0) {
          this.lastDeviceMemoryStats.paramMemDiff = -1;
        } else {
          if (newMemTableFromMem.length < paramTable.length) {
            paramTable = paramTable.substring(0, newMemTableFromMem.length);
          }
          const differenceStr1 = this.diffChecker(newMemTableFromMem, paramTable);
          console.log(differenceStr1);
          this.lastDeviceMemoryStats.paramMemDiff = this.diffCount(newMemTableFromMem, paramTable);
          this.lastDeviceMemoryStats.totalMemDiff += this.lastDeviceMemoryStats.paramMemDiff;

          const paramTableInMemBin = this.hexToUint8Array(newMemTableFromMem);
          paramSegment.debugDifferences(paramTableInMemBin);

          const differenceStr = this.diffCheckerBin(paramTableInMemBin, actualData);
          console.log("[KnxBridge] Difference: Param In Actual Device Memory (LEFT) vs Param In Estada (RIGHT): ");
          console.log(differenceStr);
        }
      }
      // this.toastTextUI = "";
    }
  }

  //
  // ------------------------------
  // Download/Verify pipeline helpers
  // ------------------------------

  private ensureNotStopped() {
    if (this.stopOperationCommand) {
      throw new Error("Operation stopped"); // no itx
    }
    if (!ipConnected) {
      throw new Error("KNX link disconnected"); // no itx
    }
  }

  private async withInterfaceLock<T>(deviceAddress: string, fn: () => Promise<T>): Promise<T> {
    await this.lockInterface(deviceAddress);
    try {
      this.ensureNotStopped();
      return await fn();
    } finally {
      // Always attempt to unlock to avoid "stuck locked interface" state
      try {
        await this.unlockInterface(deviceAddress);
      } catch (e) {
        console.warn("[KnxBridge] unlockInterface failed:", e);
      }
    }
  }

  private buildAssociationSets(associationList: Array<AssociationListEntry>) {
    const setOfAddresses = new Set<string>();
    const setOfCobs = new Set<number>();
    associationList.forEach((item) => {
      if ((item as any).addr) {
        const hex = this.groupAddressToHex((item as any).addr);
        setOfAddresses.add(hex);
      }
      setOfCobs.add((item as any).cob);
    });
    const sortedHexAddresses = Array.from(setOfAddresses).sort();
    return { setOfAddresses, setOfCobs, sortedHexAddresses };
  }

  /**
   * Preflight reads + validation for both download and verify.
   * Performs independent PID reads in parallel for lower latency.
   */
  private async preflightLoadProcedure(
    device: Device,
    APOnlyDownload: boolean,
  ): Promise<{
    deviceAddress: string;
    productData: any;
    AP: any;
    LpStyle: string;
    memorySegments: CodeSegment[];
    activeDeviceObjTypes: any;
    appIdFromAP: string;
    manIdFromMem: string;
    maskVersionFromMem: string;
    isValidIPRouterMask: boolean;
    LPs: any;
    tableOffsets: TableOffsets;
    ok: boolean;
  }> {
    const deviceAddress = device.fullPhysicalAddress;
    const productData = device.template?.knxProductData;
    if (!productData) throw new Error("Missing productData"); // no itx

    const activeDeviceObjTypes = await this.getDeviceObjectTypes(device);
    const AP = device.getApplicationProgram();
    const LpStyle = AP?.a?.LoadProcedureStyle;
    const memorySegments = device.programming.getKnxMemorySegments();

    // 0.0 LoadNetworkConfiguration (ETS-like)
    if (!APOnlyDownload) {
      await this.setDeviceProgMode(deviceAddress, 1);
      await this.broadcastIndividualAddress(deviceAddress);
    }
    await this.setDeviceProgMode(deviceAddress, 0);

    this.showProgressToast(
      state.translate("knxBridge.ReadDeviceProps", "Reading device's Property IDs (PIDs)"),
      this.percentUI,
    );

    const appIdFromAP = ProductStorageHandler.getApplicationIdFromCatalogId(productData.productInfo.CatalogId);

    // ManufacturerId can be missing on some devices => fallback to catalog
    const manIdFallback = ProductStorageHandler.getManufacturerIdFromCatalogId(
      productData.productInfo.CatalogId,
    ).toLowerCase();

    // Parallel PID reads (independent)
    const [manIdRaw, descriptorRaw, serialRaw] = await Promise.all([
      this.getManIdFromDevice(deviceAddress).catch(() => ""), // PID 12
      this.getDescriptorFromDevice(deviceAddress).catch(() => ""), // PID 83
      this.getSerialFromDevice(deviceAddress).catch(() => ""), // PID 11
    ]);

    const manIdFromMem = (manIdRaw || manIdFallback).toLowerCase();

    // Important: Max APDU impacts our chunk sizing. Keep after manId to apply vendor special cases.
    await this.getMaxAPDULengthFromDevice(deviceAddress, manIdFromMem, this.defaultAddressTableOffset).catch(() => 0);

    // Restart device (ETS does this around here for many targets)
    this.showProgressToast(state.translate("knxBridge.RestartDevice", "Restarting Device"), this.percentUI);
    await this.restartDevice(deviceAddress, manIdFromMem);

    // MaskVersion handling (legacy quirks)
    let maskVersionFromMem = descriptorRaw || "";
    const maskVersionFromAppXml = productData.productInfo.MaskVersion
      ? productData.productInfo.MaskVersion.split("-")[1]
      : "";

    if (maskVersionFromMem === "" || (maskVersionFromAppXml != "091A" && maskVersionFromMem !== "")) {
      maskVersionFromMem = maskVersionFromAppXml;
    }

    const isValidIPRouterMask = this.IPRouterMask.some(
      (mask) => mask.toLowerCase() == maskVersionFromAppXml.toLowerCase(),
    );

    const validMaskVersion = maskVersionFromMem
      ? this.BimM112Masks.some((mask) => mask.toLowerCase() == maskVersionFromMem.toLowerCase()) ||
        this.SystemBMasks.some((mask) => mask.toLowerCase() == maskVersionFromMem.toLowerCase()) ||
        this.Bcu2Masks.some((mask) => mask.toLowerCase() == maskVersionFromMem.toLowerCase()) ||
        isValidIPRouterMask
      : false;

    const manIdFromAP = productData.productInfo.ManufacturerId.split("-")[1];
    const manIdAltFromAP = productData.productInfo.OriginalManufacturer
      ? productData.productInfo.OriginalManufacturer.split("-")[1]
      : "";
    const validManufacturer =
      manIdFromAP.toLowerCase() == manIdFromMem.toLowerCase() ||
      manIdAltFromAP.toLowerCase() == manIdFromMem.toLowerCase();

    // Additional resources (MergedProcedure) – keep, but avoid blocking for IPRouter quirks
    let additionalResourceOk = true;
    if (LpStyle === MergedProcedure && !isValidIPRouterMask) {
      const [versionFromMem, orderInfoFromMem] = await Promise.all([
        this.getVersionFromDevice(deviceAddress).catch(() => ""),
        this.getOrderInfoFromDevice(deviceAddress).catch(() => ""),
      ]);
      additionalResourceOk = Boolean(versionFromMem) && Boolean(orderInfoFromMem);
    }

    const LPs = device.programming.getLoadProcedures();

    // Table offsets (avoid for router masks where not supported)
    const tableOffsets: TableOffsets = !isValidIPRouterMask
      ? await this.getMemTableOffsetsFromDevice(deviceAddress, AP, LpStyle, activeDeviceObjTypes)
      : { address: "", association: "", groupObj: "", param: "" };

    // reset device control for clean state
    await this.setDeviceControl(deviceAddress, "00").catch(() => "");

    const ok = Boolean(validMaskVersion && validManufacturer && appIdFromAP && LPs && additionalResourceOk);

    return {
      deviceAddress,
      productData,
      AP,
      LpStyle,
      memorySegments,
      activeDeviceObjTypes,
      appIdFromAP,
      manIdFromMem,
      maskVersionFromMem,
      isValidIPRouterMask,
      LPs,
      tableOffsets,
      ok,
    };
  }

  ////////////////////////////////////
  // Download Device (Default Mode=All)
  //////////////////////////////////////
  // Adr Individual Address    (Table1 - Only Physical Address)               1 5
  // Prg ComObj AP	           (Table3 - AP)                                  3 5
  // Par Params	   	           (Table3 - Params)                              3 5
  // Grp Grp Addr-Association  (Table1 + Table2 + Table3 GroupObjTable)       1,2,3 5
  // Cfg MT Settings  (only for PL and RF)                                    - -

  // 5 download Modes:
  // Download All 					        - Adr, Prg, Par, Grp 	(1,2,3 AP)
  // Download Partial 				      - Par, Grp 				    (2,3)
  // Download Individual Address 	  - Adr					        (1)                 Load Address
  // Overwrite Individual Address 	- Adr					        (1)                 Write physical Address
  // Download Application			      - Prg					        (AP)

  async downloadDevice(device: Device, associationList: Array<AssociationListEntry>, APOnlyDownload: boolean) {
    try {
      await EP(
        "knx.downloadDevice", // no itx
      ).runAsync(async () => {
        // Group Address regeneration (silently) before downloading
        await generateGroupAddresses(undefined, undefined, true);

        const deviceAddress = device.fullPhysicalAddress;
        this.setOperationInProgress(true, deviceAddress);
        this.failureCount = 0;
        this.percentUI = 0;
        this.setProgressRange(0, 100);
        this.startOperationSummary(
          "download", // no itx
          device,
          "Started", // no itx
          itx("KnxBridge.starteddownloadoperation", "Started download operation"),
        );

        this.toastTextUI = state.translate("knxBridge.verifyingDeviceApplication", "Verifying Device Application");
        this.showProgressToast(this.toastTextUI, 0);

        await this.withInterfaceLock(deviceAddress, async () => {
          this.ensureNotStopped();

          const pre = await this.preflightLoadProcedure(device, APOnlyDownload);
          this.ensureNotStopped();

          if (!pre.ok) {
            console.error("[KnxBridge] Preflight failed: PID mismatch or missing LP/appId.");
            throw new Error("Preflight failed");
          }

          // Execute Load Procedure
          this.showProgressToast(state.translate("knxBridge.downloading", "Downloading"), this.percentUI);

          await this.executeLP(
            device,
            pre.AP,
            pre.appIdFromAP,
            associationList,
            pre.memorySegments,
            pre.LpStyle,
            pre.maskVersionFromMem,
            pre.tableOffsets,
            pre.isValidIPRouterMask,
          );

          if (device.installChecks?.setChecked) {
            device.installChecks.setChecked(CheckPointNames.programmingParameters, InstallationStates.operational);
          }
        });

        console.log(`Failure Count: ${this.failureCount}`);
        this.finishOperationSummary("success"); // no itx
      });
    } catch (error) {
      if (this.stopOperationCommand) {
        showErrToast(
          itx("KnxBridge.processStoppedBefore", "Process stopped before completion"),
          itx("KnxBridge.downloadIncomplete", "Download Incomplete"),
        );
      } else if (device.fullPhysicalAddress == this.lastDeviceUnreachable) {
        showErrToast(
          itx("KnxBridge.deviceUnreachable", "Device Unreachable"),
          itx("KnxBridge.downloadFailed", itx("KnxBridge.downloadFailed", "Download Failed")),
        );
      } else if (!ipConnected) {
        showErrToast(
          itx("KnxBridge.knxLinkDisconnected", "KNX link disconnected during download Operation"),
          itx("KnxBridge.downloadFailed", "Download Failed"),
        );
      } else {
        showErrToast("An unexpected error occured while executing Load Procedure.", "Download Failed");
        this.operationFailure = true;
      }
      this.finishOperationSummary(this.stopOperationCommand ? "stopped" : "failed", error);
    } finally {
      this.setOperationInProgress(false);
    }
  }

  async downloadDeviceIndividualAddress(device: Device, overwrite: boolean, newPhysicalAddress: string) {
    // From the ETS Logs, following Read Opertations are performed during Device Individual (Physical) Address Download:
    // ADDED LoadNetworkConfiguration                          - PID=12; Match should be identical (Eg. device: 131 == product data: 131; In Hex Data=0083)
    // ADDED ReadIdentificationDuringNetworkConfiguration      - PID=83; Match should be identical (Eg. device: MV-0705 == product data: MV-0705)

    try {
      const deviceAddress = device.fullPhysicalAddress;
      this.setOperationInProgress(true, deviceAddress);
      this.failureCount = 0;
      this.percentUI = 0;
      this.setProgressRange(0, 100);
      this.startOperationSummary(
        "individualAddress", // no itx
        device,
        "Started", // no itx
        "Started individual address download operation",
      );
      this.showProgressToast(state.translate("knxBridge.verifyingDevice", "Verifying Device"), 0);

      const productData = device.template?.knxProductData;
      let LpStyle = DefaultProcedure;

      await this.lockInterface(deviceAddress);

      const activeDeviceObjTypes = await this.getDeviceObjectTypes(device);
      console.log("[KnxBridge] Active Interface Object Types =", activeDeviceObjTypes);

      if (productData) {
        const AP = device.getApplicationProgram();
        LpStyle = AP.a?.LoadProcedureStyle;
      }

      this.showProgressToast(
        state.translate("knxBridge.assignNewIndividualAddress", "Assigning New Individual Address", deviceAddress),
        this.percentUI + 25,
      );
      // 0 Assign New address to device in Programming mode
      const status = await this.assignNewIndividualAddress(deviceAddress);
      if (status != "success") {
        // Error types handlings here
        showToast({
          text: state.translate("knxBridge.iAAssignmentFailed", "Unable to assign new IndividualAddress to device."),
        });
        return;
      }

      // 1 LoadNetworkConfiguration
      // Write from source deviceAddress to 0/0/0	(Broadcast)
      this.showProgressToast(
        state.translate("knxBridge.broadcastIndividualAddress", "Broadcasting Address", deviceAddress),
        this.percentUI + 25,
      );
      await this.setDeviceProgMode(deviceAddress, 1);
      await this.broadcastIndividualAddress(deviceAddress);
      await this.setDeviceProgMode(deviceAddress, 0);

      // 2 ReadIdentificationDuringNetworkConfiguration
      // Validate Mask Versions, APDU Length and KNX Serial No.
      this.showProgressToast(
        state.translate("knxBridge.verifyingDeviceProperties", "Verifying Device Properties"),
        this.percentUI + 25,
      );
      let maskVersionFromMem = await this.getDescriptorFromDevice(deviceAddress); // PID 83 // Get Device Descriptor (MaskVersion) From Device
      let validMaskVersion = false;
      if (maskVersionFromMem && LpStyle == ProductProcedure) {
        validMaskVersion =
          this.BimM112Masks.some((mask) => mask.toLowerCase() == maskVersionFromMem.toLowerCase()) ||
          this.Bcu2Masks.some((mask) => mask.toLowerCase() == maskVersionFromMem.toLowerCase());
      } else {
        if (maskVersionFromMem == "") {
          maskVersionFromMem = productData?.productInfo.MaskVersion
            ? productData.productInfo.MaskVersion.split("-")[1]
            : "";
        }
        validMaskVersion = this.SystemBMasks.some((mask) => mask.toLowerCase() == maskVersionFromMem.toLowerCase());
      }

      const manIdFromMem = await this.getManIdFromDevice(deviceAddress); // PID 12 (Not checked during IndAddrRead)
      const apduLengthFromMem = await this.getMaxAPDULengthFromDevice(
        deviceAddress,
        manIdFromMem,
        this.defaultAddressTableOffset,
      ); // PID 56
      const serialFromMem = await this.getSerialFromDevice(deviceAddress); // PID 11

      this.showProgressToast(state.translate("knxBridge.restartDevice", "Restarting Device"), this.percentUI + 15);
      await this.restartDevice(deviceAddress, manIdFromMem);
      const verifyStatus = serialFromMem && apduLengthFromMem && validMaskVersion;
      if (!verifyStatus) {
        console.error("Error while downloading device Individual Address PID(s) mismatch with AppProgram properties");
      } else if (overwrite) {
        if (LpStyle == ProductProcedure) {
          this.showProgressToast(state.translate("knxBridge.finalizing", "Finalizing"), this.percentUI + 5);
          const addressTableOffset = await this.tableOffsetFromDevice(deviceAddress, activeDeviceObjTypes.AddressTable);
          if (addressTableOffset && this.prefixHexType(addressTableOffset) == this.defaultAddressTableOffset)
            this.writeMemoryBlockToDevice(
              deviceAddress,
              addressTableOffset ? this.prefixHexType(addressTableOffset) : "0x4001",
              this.deviceAddressToHex(newPhysicalAddress),
              false,
              5,
            );
        } else if (LpStyle == MergedProcedure) {
        }
      }

      if (device.installChecks.setChecked) {
        device.installChecks.setChecked(
          CheckPointNames.programmingPhysicalAddress,
          InstallationStates.operational,
          newPhysicalAddress,
        );
      }

      await this.unlockInterface(deviceAddress);
      console.log(`Failure Count: ${this.failureCount}`);
      this.finishOperationSummary("success"); // no itx
      showToast({ text: state.translate("knxBridge.addressAssigned", "New address assigned to device") });
    } catch (error) {
      if (this.stopOperationCommand) {
        showErrToast(
          itx("KnxBridge.processStoppedBefore", "Process stopped before completion"),
          itx("KnxBridge.downloadIncomplete", "Download Incomplete"),
        );
      } else if (device.fullPhysicalAddress == this.lastDeviceUnreachable) {
        showErrToast(
          itx("KnxBridge.deviceUnreachable", "Device Unreachable"),
          itx("KnxBridge.downloadFailed", "Download Failed"),
        );
      } else if (!ipConnected) {
        showErrToast(
          itx("KnxBridge.knxLinkDisconnected", "KNX link disconnected during download Operation"),
          itx("KnxBridge.downloadFailed", "Download Failed"),
        );
      } else {
        showErrToast("An unexpected error occured while executing Load Procedure.", "Download Failed");
        this.operationFailure = true;
      }
      this.finishOperationSummary(this.stopOperationCommand ? "stopped" : "failed", error);
    } finally {
      this.setOperationInProgress(false);
    }
  }

  async verifyDeviceMemory(deviceProgrammingInfo: DeviceProgrammingInfo, associationList: Array<AssociationListEntry>) {
    const device = deviceProgrammingInfo.device;
    try {
      await EP(
        "knx.verifyDeviceMemory", // no itx
      ).runAsync(async () => {
        const deviceAddress = device.fullPhysicalAddress;
        this.setOperationInProgress(true, deviceAddress);
        this.failureCount = 0;
        this.percentUI = 0;
        this.setProgressRange(0, 100);
        this.startOperationSummary(
          "verify", // no itx
          device,
          "Started", // no itx
          itx("KnxBridge.startedverifyoperation", "Started verify operation"),
        );

        // MemoryStats tracking
        this.lastDeviceMemoryStats = deviceProgrammingInfo.memoryStats;
        this.lastDeviceMemoryStats.totalMemDiff = 0;

        this.toastTextUI = state.translate("knxBridge.verifyingDeviceApplication", "Verifying Device Application");
        this.showProgressToast(this.toastTextUI, 0);

        await this.withInterfaceLock(deviceAddress, async () => {
          this.ensureNotStopped();

          const pre = await this.preflightLoadProcedure(device, true /* APOnlyDownload doesn't matter for verify */);
          this.ensureNotStopped();

          if (!pre.ok) {
            console.error("[KnxBridge] Preflight failed: PID mismatch or missing LP/appId.");
            throw new Error("Preflight failed");
          }

          // Build association sets once
          const { setOfAddresses, setOfCobs, sortedHexAddresses } = this.buildAssociationSets(associationList);

          // Stage A: verify address table
          this.showProgressToast(
            state.translate("knxBridge.verifyAddressTable", "Verifying Address Table"),
            this.percentUI,
          );

          let adjustedAssocOffset = await this.writeOrVerifyAddrTableToMemory(
            pre.deviceAddress,
            pre.tableOffsets.address,
            setOfAddresses,
            pre.LpStyle,
            downloadMode.parametersVerifyOnly,
          );

          this.ensureNotStopped();

          // Stage B: verify association table (depends on address table for MV-0021 quirk)
          this.showProgressToast(
            state.translate("knxBridge.verifyAssociationTable", "Verifying Association Table"),
            this.percentUI,
          );

          await this.writeOrVerifyAssocTableToMemory(
            pre.deviceAddress,
            pre.maskVersionFromMem == "0021" && adjustedAssocOffset
              ? this.prefixHexType(adjustedAssocOffset)
              : pre.tableOffsets.association,
            sortedHexAddresses,
            associationList,
            pre.LpStyle,
            pre.maskVersionFromMem,
            pre.AP,
            downloadMode.parametersVerifyOnly,
          );

          this.ensureNotStopped();

          // Stage C: verify com object table
          this.showProgressToast(
            state.translate("knxBridge.verifyComObjectTable", "Verifying Communication Object Table"),
            this.percentUI,
          );

          if (pre.LpStyle == MergedProcedure) {
            await this.writeOrVerifyComObjTableToRelMemory(
              device,
              pre.deviceAddress,
              pre.AP,
              pre.tableOffsets.groupObj,
              pre.memorySegments,
              setOfCobs,
              associationList,
              pre.LpStyle,
              downloadMode.parametersVerifyOnly,
            );
          } else {
            await this.writeOrVerifyComObjTableToMemory(
              device,
              pre.deviceAddress,
              pre.tableOffsets.groupObj,
              setOfCobs,
              associationList,
              pre.memorySegments,
              pre.LpStyle,
              pre.maskVersionFromMem,
              downloadMode.parametersVerifyOnly,
            );
          }

          this.ensureNotStopped();

          // Stage D: verify parameters
          this.showProgressToast(state.translate("knxBridge.verifyParameters", "Verifying Parameters"), this.percentUI);

          const LPs = pre.LPs;
          if (pre.LpStyle == ProductProcedure) {
            // For product procedure we verify parameter segments indicated by LP
            for (let i in LPs.items) {
              const LP = LPs.items[i];
              if (!LP) continue;

              for (let procedureIndex = 0; procedureIndex < LP.items.length; procedureIndex++) {
                const procedure = LP.items[procedureIndex];
                const tag = procedure["#name"];
                if (tag != "LdCtrlAbsSegment") continue;

                const LsmIdx = procedure.a.LsmIdx;
                if (LsmIdx != "3") continue;

                const MemType = procedure.a.MemType;
                if (MemType != "3") continue;

                const groupObjTableAddress = parseInt(pre.tableOffsets.groupObj, 16);
                const Address = parseInt(procedure.a.Address);
                if (Address > groupObjTableAddress) {
                  await this.writeOrVerifyActualParamsToMemory(
                    device,
                    pre.deviceAddress,
                    Address.toString(),
                    pre.memorySegments,
                    pre.LpStyle,
                    pre.maskVersionFromMem,
                    undefined,
                    downloadMode.parametersVerifyOnly,
                  );
                }
                this.ensureNotStopped();
              }
            }
          } else if (pre.LpStyle == MergedProcedure) {
            const tableSizes: TableSizes = await this.getTableSizeList(
              pre.deviceAddress,
              pre.tableOffsets,
              pre.memorySegments,
              pre.AP,
              pre.LpStyle,
              setOfAddresses,
              associationList,
            );

            const paramTableInMemory = await this.readMemoryBlockFromDevice(
              pre.deviceAddress,
              pre.tableOffsets.param,
              tableSizes.param,
              true,
              10,
            );

            await this.writeOrVerifyActualParamsToMemory(
              device,
              pre.deviceAddress,
              pre.tableOffsets.param,
              pre.memorySegments,
              pre.LpStyle,
              pre.maskVersionFromMem,
              paramTableInMemory,
              downloadMode.parametersVerifyOnly,
            );
          }

          if (!this.stopOperationCommand) {
            showToast({ text: state.translate("knxBridge.verificationComplete", "Verification Complete.") });
          }
        });

        console.log(`Failure Count: ${this.failureCount}`);
        this.finishOperationSummary("success"); // no itx
      });
    } catch (error) {
      if (this.stopOperationCommand) {
        showErrToast(
          itx("KnxBridge.processStoppedBefore", "Process stopped before completion"),
          itx("KnxBridge.verificationIncomplete", "Verification Incomplete"),
        );
      } else if (device.fullPhysicalAddress == this.lastDeviceUnreachable) {
        showErrToast(
          itx("KnxBridge.deviceUnreachable", "Device Unreachable"),
          itx("KnxBridge.verificationFailed", "Verification Failed"),
        );
      } else if (!ipConnected) {
        showErrToast(
          itx("KnxBridge.knxLinkDisconnected", "KNX link disconnected during verification Operation"),
          itx("KnxBridge.verificationFailed", "Verification Failed"),
        );
      } else {
        showErrToast("An unexpected error occured during verification", "Verification Failed");
        this.operationFailure = true;
      }
      this.finishOperationSummary(this.stopOperationCommand ? "stopped" : "failed", error);
    } finally {
      this.setOperationInProgress(false);
      this.updateTotalMemoryDiff();
    }
  }

  async unloadDevice(device: Device): Promise<Boolean> {
    // From the ETS Logs, following Read Opertations are performed during Device Unload:

    // Waiting for device in programming mode
    // Device in programming mode: 1.1.7 (Example)
    // Executing Unload ApplicationProgram procedure
    // Read serial number System.Byte[]
    // Switch prog mode off (not needed any more)
    // Unload IndividualAddress by serial number 0083:83AA23A2 to 15.15.255

    try {
      const deviceAddress = device.fullPhysicalAddress; //"1.1.2"
      this.setOperationInProgress(true, deviceAddress);

      this.failureCount = 0;
      this.percentUI = 0;
      this.setProgressRange(0, 100);
      this.startOperationSummary(
        "unload", // no itx
        device,
        "Started", // no itx
        itx("KnxBridge.startedunloadoperation", "Started unload operation"),
      );

      this.unloadInProgress = true;
      this.showProgressToast(state.translate("knxBridge.verifyingDevice", "Verifying Device"), 0);

      const productData = device.template?.knxProductData;
      let LpStyle = DefaultProcedure;
      if (productData) {
        LpStyle = productData.productInfo.LoadProcedureStyle;
      }

      const status = await this.isDeviceInProgramMode(deviceAddress); // PID 56
      const programMode = status == "success";
      if (!programMode) {
        showToast({ text: status });
        return false;
      }

      // 1 LoadNetworkConfiguration
      // Write from source deviceAddress to 0/0/0	(Broadcast)
      // await this.setDeviceProgMode(deviceAddress, 1);
      this.showProgressToast(
        state.translate("knxBridge.broadcastIndividualAddress", "Broadcasting Address", deviceAddress),
        this.percentUI + 10,
      );
      await this.broadcastIndividualAddress(deviceAddress);

      // 2 ReadIdentificationDuringNetworkConfiguration
      // Validate Mask Versions, APDU Length and KNX Serial No.
      this.showProgressToast(
        state.translate("knxBridge.verifyingDeviceProperties", "Verifying Device Properties"),
        this.percentUI + 10,
      );
      let maskVersionFromMem = await this.getDescriptorFromDevice(deviceAddress); // PID 83 // Get Device Descriptor (MaskVersion) From Device
      let validMaskVersion = false;
      if (maskVersionFromMem && LpStyle == ProductProcedure) {
        validMaskVersion =
          this.BimM112Masks.some((mask) => mask.toLowerCase() == maskVersionFromMem.toLowerCase()) ||
          this.Bcu2Masks.some((mask) => mask.toLowerCase() == maskVersionFromMem.toLowerCase());
      } else {
        if (maskVersionFromMem == "") {
          maskVersionFromMem = productData?.productInfo.MaskVersion
            ? productData.productInfo.MaskVersion.split("-")[1]
            : "";
        }
        validMaskVersion = this.SystemBMasks.some((mask) => mask.toLowerCase() == maskVersionFromMem.toLowerCase());
      }

      const manIdFromMem = await this.getManIdFromDevice(deviceAddress); // PID 12
      const apduLengthFromMem = await this.getMaxAPDULengthFromDevice(
        deviceAddress,
        manIdFromMem,
        this.defaultAddressTableOffset,
      ); // PID 56

      for (let objId = 1; objId <= 5; objId++) {
        if (LpStyle == ProductProcedure && objId == 5) break;

        const postfix =
          objId == 1
            ? "AddressTable" // no itx
            : objId == 2
              ? "AssociationTable" // no itx
              : objId == 3
                ? "ComObjectTable" // no itx
                : objId == 4 || objId == 5
                  ? "ParameterTable" // no itx
                  : "";

        this.showProgressToast(state.translate("knxBridge.unloading", "Unloading"), this.percentUI + 10, postfix);
        await this.setDeviceProperty(
          deviceAddress,
          this.writableLoadEvent(LoadEvent.Unload, maskVersionFromMem, objId.toString()),
          PIDList.LoadStateControl,
          objId.toString(),
        ); // LoadEvent LE=4 Outputs LoadState LS=0 (Unloaded)
      }

      this.showProgressToast(
        state.translate("knxBridge.unloadingAddress", "Unloading Device Address"),
        this.percentUI + 10,
        deviceAddress,
      );
      // Read serial number System.Byte[]
      const serialFromMem = await this.getSerialFromDevice(deviceAddress); // PID 11

      // Switch prog mode off (not needed any more)
      await this.setDeviceProgMode(deviceAddress, 0); // PID 54

      // Unload IndividualAddress by serial number (E.g. 0083:83AA23A2) to Factory Reset Address (15.15.255)
      const writeStatus = await this.writeNewDeviceAddressUsingSerial(serialFromMem);
      if (writeStatus == "success") {
        console.log("[KnxBridge] writeDeviceAddressUsingSerial success");
      }
      const newDeviceAddress = await this.readDeviceAddressUsingSerial(serialFromMem);
      if (newDeviceAddress == "15.15.255") {
        console.log("[KnxBridge] readDeviceAddressUsingSerial success");
      }

      this.showProgressToast(state.translate("knxBridge.finalizing", "Finalizing"), 90);

      // await this.restartDevice(deviceAddress);
      // const verifyStatus = serialFromMem && apduLengthFromMem && validMaskVersion;
      // if (!verifyStatus) { }
      // showToast({ text: state.translate("knxBridge.unloadComplete", "Unload Complete.") });

      if (device.installChecks.setChecked) {
        device.installChecks.setChecked(CheckPointNames.programmingParameters, InstallationStates.unchecked);
        device.installChecks.setChecked(
          CheckPointNames.programmingPhysicalAddress,
          InstallationStates.unchecked,
          "15.15.255", // no itx
        );
      }

      this.finishOperationSummary("success"); // no itx
      this.setOperationInProgress(false);
      console.log(`Failure Count: ${this.failureCount}`);
      showToast({ text: state.translate("knxBridge.unloadComplete", "Unload Complete") });
      return true;
    } catch (ex) {
      if (this.stopOperationCommand) {
        showErrToast(
          itx("KnxBridge.processStoppedBefore", "Process stopped before completion"),
          itx("KnxBridge.unloadIncomplete", "Unload Incomplete"),
        ); //
      } else if (device.fullPhysicalAddress == this.lastDeviceUnreachable) {
        showErrToast(
          itx("KnxBridge.deviceUnreachable", "Device Unreachable"),
          itx("KnxBridge.unloadFailed", itx("KnxBridge.unloadFailed", "Unload Failed")),
        );
      } else if (!ipConnected) {
        showErrToast(
          itx("KnxBridge.knxLinkDisconnected", "KNX link disconnected during Unload Operation"),
          "Unload Failed",
        );
      }
      console.error(ex);
      this.finishOperationSummary(this.stopOperationCommand ? "stopped" : "failed", ex);
      this.setOperationInProgress(false);
      return false;
    }
  }

  async stopDeviceOperation() {
    if (this.operationInProgress) {
    }
  }

  async finalLP(
    device: Device,
    AP: any,
    applicationId: any,
    associationList: Array<AssociationListEntry>,
    memorySegments: Array<any>,
    LpStyle: string,
    maskVersion: string,
    tableOffsets: TableOffsets,
  ) {
    const deviceAddress = device.fullPhysicalAddress; //"1.1.2"
    const setOfAddresses = new Set<string>();
    const setOfCobs = new Set<number>();

    let PeiType = AP.a.PeiType;
    let AppId = applicationId;
    let AppIdHex = this.appIdAsHex(AppId);

    const isBcu2Mask = this.Bcu2Masks.some((mask) => mask.toLowerCase() == maskVersion.toLowerCase());

    const productData = device.template?.knxProductData;
    const manIdHex = productData?.productInfo.ManufacturerId
      ? productData?.productInfo.ManufacturerId.split("-")[1]
      : "";
    associationList.forEach((item) => {
      if (item.addr) {
        const hex = this.groupAddressToHex(item.addr);
        setOfAddresses.add(hex);
      }
      setOfCobs.add(item.cob);
    });

    const LPs = await device.programming.getLoadProcedures(); // Get LoadProcedure from device's AP (Type: ProductProcedure)
    const defaultProcedure = await this.getDefaultProcedureFromMasterXml(maskVersion); // LP from master xml (Type: DefaultProcedure)
    console.log("[KnxBridge] DefaultProcedure: ");
    console.log(defaultProcedure);

    let finalProcedure = defaultProcedure;

    if (LpStyle == ProductProcedure) {
      finalProcedure = LPs;
    } else if (LpStyle == MergedProcedure) {
      //Use Combination of DefaultProcedure and MergedProcedure to create ProductProcedure
      let mergedProcedure: any = { items: [] }; // This will be the final procedure

      const tableSizes: TableSizes = await this.getTableSizeList(
        deviceAddress,
        tableOffsets,
        memorySegments,
        AP,
        LpStyle,
        setOfAddresses,
        associationList,
      );

      let maskVersionFromAppXml = productData?.productInfo.MaskVersion
        ? productData.productInfo.MaskVersion.split("-")[1]
        : "";
      const isIPRouterMask = this.IPRouterMask.some(
        (mask) => mask.toLowerCase() == maskVersionFromAppXml.toLowerCase(),
      );

      finalProcedure = await this.createFinalProcedure(
        device,
        AP,
        AppIdHex,
        applicationId,
        LPs,
        maskVersion,
        isIPRouterMask,
        tableSizes,
        defaultProcedure,
      );
    }
    console.log("[KnxBridge] FinalProcedure: ");
    console.log(finalProcedure);
    return finalProcedure;
  }

  fixWriteRelMemOrdering(LP: any) {
    const items = LP.items;

    // 1. collect only LdCtrlWriteRelMem
    const writeMemItems = items
      .filter((x) => x["#name"] === "LdCtrlWriteRelMem")
      .sort((a, b) => Number(a.a?.ObjIdx) - Number(b.a?.ObjIdx));

    // 2. replace in place while keeping other tags untouched
    let i = 0;
    const fixed = items.map((x) => (x["#name"] === "LdCtrlWriteRelMem" ? writeMemItems[i++] : x));

    LP.items = fixed;
    return LP;
  }

  stringToHex(str: string, totalLength: number = 30): string {
    let result = "";

    for (let i = 0; i < str.length; i++) {
      const hex = str.charCodeAt(i).toString(16);
      result += hex.length === 2 ? hex : "0" + hex;
    }

    while (result.length / 2 < totalLength) {
      result += "00";
    }

    return result;
  }

  async getPreProcedure(device: Device, maskVersion: string, ipRouterMask: boolean) {
    // Added 21.01.2026 (WIP - Not final)
    // For Router devices in general e.g. Jung IPR300SREG, GIRA 2167 00, LP from IP Router is preceded by a common pre-procedure.
    let preProcList: any = [];

    // Applicable to Router Devices
    if (ipRouterMask == true || maskVersion == "091A") {
      // Some IPRouter have a different mask than 091A e.g. MDt Router displays BCU Mask 0012 in memory
      // <LdCtrlLoadImageProp ObjType="11" PropId="64" /> Read MacAddress
      // 0022D10450F3  Jung IPR300SREG
      // 000AB3375C56  GIRA 2167 00
      // This Property shall contain the MAC address (48 bit) of the KNXnet/IP device.
      // The value shall be set by the manufacturer and shall be read-only
      preProcList.push({ "#name": "LdCtrlLoadImageProp", a: { ObjType: "11", PropId: "64" } }); // MAC Address

      // <LdCtrlWriteProp ObjType="11" PropId="51" Verify="true" />
      // Data=0000 ProjectInstallation Identification for both Jung IPR300SREG and GIRA 2167 00
      // The 16 bit value shall contain the project number in the upper 12 bits and the installation number in the
      // lower 4 bits. The factory default value for this property shall be set to 0000h
      preProcList.push({
        "#name": "LdCtrlWriteProp", // no itx
        a: { ObjType: "11", PropId: "51", Verify: "true", InlineData: "0000" },
      }); // MAC Address

      // <LdCtrlWriteProp ObjType="11" PropId="66" Verify="true" />
      // From Documentation:
      // his Property shall contain the KNXnet/IP System Setup Multicast Address.
      // The value of this Property shall be fixed at 224.0.23.12
      // Data=E000170C  (224.0.23.12) for both Jung IPR300SREG and GIRA 2167 00

      // read 65 write 66
      preProcList.push({
        "#name": "LdCtrlWriteProp", // no itx
        a: { ObjType: "11", PropId: "66", Verify: "true", InlineData: "E000170C" },
      }); // Routing Multicast Address

      // <LdCtrlWriteProp ObjType="11" PropId="67" Verify="true" />
      // Data=20 (32) for both Jung IPR300SREG and GIRA 2167 00
      // The default value shall be set to 16.
      preProcList.push({
        "#name": "LdCtrlWriteProp", // no itx
        a: { ObjType: "11", PropId: "67", Verify: "true", InlineData: "20" },
      }); // Time to Live

      // <LdCtrlWriteProp ObjType="11" PropId="76" Count="30" Verify="true" />
      // Data=2D 41 31 33 20 2D 20 4A 75 6E 67 20 49 50 52 33 30 30 53 52 45 47 00 00 00 00 00 00 00 00 (-A13 - Jung IPR300SREG)
      // Data=2D 41 31 32 20 2D 20 32 31 36 37 20 30 30 20 47 49 52 41 00 00 00 00 00 00 00 00 00 00 00 ( -A12 - 2167 00 GIRA)
      preProcList.push({
        "#name": "LdCtrlWriteProp", // no itx
        a: {
          ObjType: "11",
          PropId: "76",
          Count: "30",
          Verify: "true", // no itx
          InlineData: this.stringToHex(`${device.fullTag} ${device.fullname}`, 30),
        },
      }); // Friendly Name

      // <LdCtrlWriteProp ObjType="11" PropId="55" Verify="true" />
      // Data=04 DHCP (Auto) for both Jung IPR300SREG and GIRA 2167 00
      preProcList.push({
        "#name": "LdCtrlWriteProp", // no itx
        a: { ObjType: "11", PropId: "55", Verify: "true", InlineData: "04" },
      }); // IP Assignment Method

      //  TODO
      //  <LdCtrlWriteProp ObjType="11" PropId="53" Count="8" Verify="true" />
      //  <LdCtrlWriteProp ObjType="11" PropId="53" Count="4" Verify="true" />
      //  Data=11FF 11FF 11FF 11FF 11FF 11FF 11FF 11FF ........ for Jung IPR300SREG
      //  Data=1108 1109 110A 110B ................... for GIRA 2167 00
      //  Idea is to use next free IAs in the Area.Line
      //  Using addresses starting from x.y.220 - 220 + n ;  n  is max tunnneling conenctions

      let max_connections = 4; // usually 4 or 8 is maximum; default is 4;
      let inlineData = ""; //"11dc11dd11de11df";
      let count = 0; // for consecutive free addresses
      for (let i = 0; i < 256; i++) {
        const area = parseInt(device.areaAddress);
        const line = parseInt(device.lineAddress);
        const segment = (i + 200) % 255;
        const physAddr = `${area}.${line}.${segment}`;
        const res = await KnxBridge.isDevicePresentInNetwork(physAddr);
        if (res != "success") {
          count++;
          inlineData += `${area.toString(16)}${line.toString(16)}${segment.toString(16)}`;
          if (count == max_connections) {
            break;
          }
        } else {
          count = 0;
          inlineData = "";
        }
      }

      preProcList.push({
        "#name": "LdCtrlWriteProp", // no itx
        a: { ObjType: "11", PropId: "53", Count: "8", Verify: "true", InlineData: inlineData },
      }); // Additional Individual Addresses

      // 2026-01-20 10:14:46,679 [5] DEBUG Knx.Ets.Hawk.DeviceImage - Begin AddIpConfigDataTask
      // 2026-01-20 10:14:46,685 [5] DEBUG Knx.Ets.Hawk.Impl.AddIpConfigDataTask - Adding Read(PID_MAC_ADDRESS)
      // 2026-01-20 10:14:46,694 [5] DEBUG Knx.Ets.Hawk.Impl.AddIpConfigDataTask - Adding PID_ROUTING_MULTICAST_ADDRESS 224.0.23.12
      // 2026-01-20 10:14:46,695 [5] DEBUG Knx.Ets.Hawk.Impl.AddIpConfigDataTask - Adding PID_TTL 32
      // 2026-01-20 10:14:46,697 [5] DEBUG Knx.Ets.Hawk.Impl.AddIpConfigDataTask - Adding PID_FRIENDLY_NAME -A13 - Jung IPR300SREG
      // 2026-01-20 10:14:46,699 [5] DEBUG Knx.Ets.Hawk.Impl.AddIpConfigDataTask - Adding PID_IP_ASSIGNMENT_METHOD Auto
      // 2026-01-20 10:14:46,700 [5] DEBUG Knx.Ets.Hawk.Impl.AddIpConfigDataTask - Adding PID_ADDITIONAL_INDIVIDUAL_ADDRESSES 11 FF 11 FF 11 FF 11 FF 11 FF 11 FF 11 FF 11 FF
      // 2026-01-20 10:14:46,701 [5] DEBUG Knx.Ets.Hawk.DeviceImage - End AddIpConfigDataTask
      // Get LP of additional function for this order number
      // const idList = await ProductStorageHandler.fetchFilteredProductIds(exceptionOrderNo);
      // if (idList.length > 1) {
      //   const additionalFunctionProductId = idList.find((id) => id !== catalogId);
      //   if (additionalFunctionProductId) {
      //     let appXmlData = await ProductStorageHandler.loadCachedProductDataAsObject(additionalFunctionProductId);
      //     if (!appXmlData) {
      //       appXmlData = await ProductStorageHandler.downloadProductData(additionalFunctionProductId);
      //     }
      //     if (appXmlData) {
      //       const KNX = appXmlData?.KNX;
      //       const ManufacturerData = firstChildWithTag(KNX, "ManufacturerData");
      //       const Manufacturer = firstChildWithTag(ManufacturerData, "Manufacturer");
      //       const ApplicationPrograms = firstChildWithTag(Manufacturer, "ApplicationPrograms");
      //       if (ApplicationPrograms && ApplicationPrograms?.items?.length == 1) {
      //         const ApplicationProgram = ApplicationPrograms.items[0];
      //         const Static = firstChildWithTag(ApplicationProgram, "Static"); // no itx
      //         const LoadProcedures = firstChildWithTag(Static, "LoadProcedures"); // no itx^
      //         res = LoadProcedures;
      //       }
      //     }
      //   }
      // }

      //     	Line 1451: 2026-01-22 15:26:54,203 [21] INFO  Knx.Ets.Hawk.Impl.LoadProcedure -   <LdCtrlMapError OriginalError="3221498661" MappedError="0" /><!-- Secure only -->
      // Line 1452: 2026-01-22 15:26:54,204 [21] INFO  Knx.Ets.Hawk.Impl.LoadProcedure -   <LdCtrlInvokeFunctionProp ObjType="11" PropId="94" InlineData="00000500" /><!-- Secure only -->
      // Line 1453: 2026-01-22 15:26:54,204 [21] INFO  Knx.Ets.Hawk.Impl.LoadProcedure -   <LdCtrlMapError OriginalError="3221498661" MappedError="3221498661" /><!-- Secure only -->
      // Line 1454: 2026-01-22 15:26:54,205 [21] INFO  Knx.Ets.Hawk.Impl.LoadProcedure -   <LdCtrlMapError OriginalError="3221498661" MappedError="0" /><!-- Secure only -->
      // Line 1455: 2026-01-22 15:26:54,206 [21] INFO  Knx.Ets.Hawk.Impl.LoadProcedure -   <LdCtrlInvokeFunctionProp ObjType="11" PropId="94" InlineData="00000300" /><!-- Secure only -->
      // Line 1456: 2026-01-22 15:26:54,206 [21] INFO  Knx.Ets.Hawk.Impl.LoadProcedure -   <LdCtrlMapError OriginalError="3221498661" MappedError="3221498661" /><!-- Secure only -->
      // Line 1457: 2026-01-22 15:26:54,207 [21] INFO  Knx.Ets.Hawk.Impl.LoadProcedure -   <LdCtrlMapError OriginalError="3221498661" MappedError="0" /><!-- Secure only -->
      // Line 1458: 2026-01-22 15:26:54,207 [21] INFO  Knx.Ets.Hawk.Impl.LoadProcedure -   <LdCtrlInvokeFunctionProp ObjType="11" PropId="94" InlineData="00000400" /><!-- Secure only -->
      // Line 1459: 2026-01-22 15:26:54,208 [21] INFO  Knx.Ets.Hawk.Impl.LoadProcedure -   <LdCtrlMapError OriginalError="3221498661" MappedError="3221498661" /><!-- Secure only -->
    }
    return { items: preProcList };
  }

  async createFinalProcedure(
    device: Device,
    AP: any,
    AppIdHex: string | undefined,
    applicationId: any,
    LPs: any,
    maskVersion: string,
    isIPRouterMask: boolean,
    tableSizes: TableSizes,
    defaultProcedure: any,
  ) {
    // Use Combination of DefaultProcedure and MergedProcedure to create ProductProcedure
    const preProcedure = await this.getPreProcedure(device, maskVersion, isIPRouterMask);
    let completeDefaultProcedure = defaultProcedure.items;
    if (preProcedure) completeDefaultProcedure = [...preProcedure.items, ...defaultProcedure.items];

    let mergedProcedure: any = { items: [] }; // This will be the final procedure
    const appv2 = AP?.a.ApplicationVersion2;
    for (let Dprocedure of completeDefaultProcedure) {
      const Dtag = Dprocedure["#name"];

      // For xml tags LdCtrlMerge, replace it with it's corresponding entry in AP.
      if (Dtag == "LdCtrlMerge") {
        const mergeId = Dprocedure.a.MergeId;
        const productProcedures = LPs.items.filter((item) => item.a.MergeId == mergeId);
        if (productProcedures.length > 0) {
          for (let productProcedure of productProcedures) {
            for (let Pprocedure of productProcedure.items) {
              const Ptag = Pprocedure["#name"];
              if (Ptag == "choose" || Ptag == "when") {
                // TODO MWE 21.10.2024
              } else if (Ptag == "LdCtrlLoad" && Pprocedure.a?.LsmIdx == "5" && !appv2) {
                continue;
              } else if (Ptag == "LdCtrlRelSegment" || Ptag == "LdCtrlWriteRelMem") {
                if (Pprocedure.a?.AppliesTo && Pprocedure.a?.AppliesTo.includes("full")) {
                  mergedProcedure.items.push(Pprocedure);
                } else if (!Pprocedure.a?.AppliesTo) {
                  mergedProcedure.items.push(Pprocedure);
                }
              } else if (Ptag == "LdCtrlWriteProp") {
                if (Pprocedure.a.PropId == "13") {
                  if (AppIdHex?.length == 10 && Pprocedure.a.ObjIdx == "5" && appv2) {
                    Pprocedure.a[noItx("InlineData")] =
                      AppIdHex.substring(0, 8) + parseInt(appv2).toString(16).padStart(2, "0");
                  } else Pprocedure.a["InlineData"] = AppIdHex; // no itx
                }
                mergedProcedure.items.push(Pprocedure);
              } else if (
                Ptag == "LdCtrlMapError" ||
                Ptag == "LdCtrlWriteMem" ||
                Ptag == "LdCtrlCompareProp" ||
                Ptag == "LdCtrlCompareRelMem" ||
                Ptag == "LdCtrlLoadImageProp" ||
                Ptag == "LdCtrlDeclarePropDesc"
              ) {
                // One use case of LdCtrlCompareProp is to display error message (saved in AP) if prop mismatch
                mergedProcedure.items.push(Pprocedure);
              } else {
                console.log("[KnxBridge] Unhandled Merged Procedure Tag: ", Ptag);
              }
            }
          }
        }
      } else {
        // Else directly add to final procedure list (with a few xml attribute updates)
        if (Dtag == "LdCtrlRelSegment" && !isIPRouterMask) {
          if (Dprocedure.a.LsmIdx == "1") {
            Dprocedure.a.Size = tableSizes.address.toString();
          } else if (Dprocedure.a.LsmIdx == "2") {
            Dprocedure.a.Size = tableSizes.association.toString();
          } else if (Dprocedure.a.LsmIdx == "3") {
            Dprocedure.a.Size = tableSizes.groupObj.toString();
          } else if (Dprocedure.a.LsmIdx == "4") {
            Dprocedure.a.Size = tableSizes.param.toString();
          } else if (Dprocedure.a.LsmIdx == "5") {
            Dprocedure.a.Size = tableSizes.param2.toString();
          }
        } else if (Dtag == "LdCtrlWriteRelMem" && !isIPRouterMask) {
          if (Dprocedure.a.ObjIdx == "1") {
            Dprocedure.a.Size = tableSizes.address.toString();
          } else if (Dprocedure.a.ObjIdx == "2") {
            Dprocedure.a.Size = tableSizes.association.toString();
          } else if (Dprocedure.a.ObjIdx == "3") {
            Dprocedure.a.Size = tableSizes.groupObj.toString();
          } else if (Dprocedure.a.ObjIdx == "4") {
            Dprocedure.a.Size = tableSizes.param.toString();
          } else if (Dprocedure.a.ObjIdx == "5") {
            Dprocedure.a.Size = tableSizes.param2.toString();
          }
        } else if (Dtag == "LdCtrlWriteProp") {
          if (Dprocedure.a.PropId == "13") {
            if (AppIdHex?.length == 10 && Dprocedure.a.ObjIdx == "5") {
              if (appv2) {
                Dprocedure.a["InlineData"] = AppIdHex.substring(0, 8) + parseInt(appv2).toString(16).padStart(2, "0");
              } else {
                continue;
              }
            } else Dprocedure.a["InlineData"] = AppIdHex; // no itx
          }
        } else if (Dtag == "LdCtrlLoad" && Dprocedure.a?.LsmIdx == "5" && !appv2) {
          continue;
        }

        mergedProcedure.items.push(Dprocedure);
      }
    }

    // mergedProcedure = this.fixWriteRelMemOrdering(mergedProcedure);
    // const disconnectTag = mergedProcedure.items.filter((item: any) => item["#name"] == "LdCtrlDisconnect");
    // if (disconnectTag.length == 0) {
    //   let newTag = mergedProcedure.items.filter((item: any) => item["#name"] == "LdCtrlConnect");
    //   if(newTag.length>0){
    //     newTag[0]["#name"] = "LdCtrlDisconnect";
    //     newTag[0]["tagname"] = "LdCtrlDisconnect";
    //     mergedProcedure.items.push(newTag[0]);
    //   }
    // }

    const finalProcedure = { items: [{ items: mergedProcedure.items }] };
    return finalProcedure;
  }

  async executeLP(
    device: Device,
    AP: any,
    applicationId: any,
    associationList: Array<AssociationListEntry>,
    memorySegments: Array<any>,
    LpStyle: string,
    maskVersion: string,
    tableOffsets: TableOffsets,
    isValidIPRouterMask: boolean,
  ) {
    this.setProgressRange(Math.max(this.percentUI, 12), 95);
    this.showProgressToast(
      state.translate("knxBridge.constructLoadProcedure", "Creating LoadProcedure"),
      this.computeProgressFromStep(1, 100),
    );

    const deviceAddress = device.fullPhysicalAddress; //"1.1.2"
    const setOfAddresses = new Set<string>();
    const setOfCobs = new Set<number>();

    let PeiType = AP.a.PeiType;
    let AppId = applicationId;
    let AppIdHex = this.appIdAsHex(AppId);

    const isBcu2Mask = this.Bcu2Masks.some((mask) => mask.toLowerCase() == maskVersion.toLowerCase());
    const isIPRouterMask = isValidIPRouterMask ?? false;

    const productData = device.template?.knxProductData;
    const manIdHex = productData?.productInfo.ManufacturerId
      ? productData?.productInfo.ManufacturerId.split("-")[1]
      : "";
    // const appNumHex = productData?.productInfo.ApplicationNumber
    //   ? parseInt(productData?.productInfo.ApplicationNumber).toString(16)
    //   : "";
    // const versionHex = productData?.productInfo.VersionNumber.split(".").join("");
    // let AppIdHex2 = manIdHex + appNumHex + versionHex;

    associationList.forEach((item) => {
      if (item.addr) {
        const hex = this.groupAddressToHex(item.addr);
        setOfAddresses.add(hex);
      }
      setOfCobs.add(item.cob);
    });

    // CONSTRUCT Simplified Load Procedure
    // There are 3 possible loading procedures:

    // 1. DefaultProcedure: The load procedure from knx_master is used
    // 2. ProductProcedure: The load procedure from the KnxProd is used
    // 3. MergeProcedure: A combination of the above two is used.

    // const LoadPreProcedure = await this.getPreProcedure(device, maskVersion, isValidIPRouterMask); // Get Preprocedure for special devices like IPR300SREG
    const LoadProcedures = device.programming.getLoadProcedures(); // Get LoadProcedure from device's AP (Type: ProductProcedure)
    const defaultProcedure = await this.getDefaultProcedureFromMasterXml(maskVersion); // LP from master xml (Type: DefaultProcedure)
    console.log("[KnxBridge] DefaultProcedure: ");
    console.log(defaultProcedure);

    let finalProcedure = defaultProcedure;
    let paramTableInMemory;

    if (LpStyle == ProductProcedure) {
      finalProcedure = LoadProcedures;
    } else if (LpStyle == MergedProcedure) {
      const tableSizes: TableSizes = !isIPRouterMask
        ? await this.getTableSizeList(
            deviceAddress,
            tableOffsets,
            memorySegments,
            AP,
            LpStyle,
            setOfAddresses,
            associationList,
          )
        : {
            address: 0,
            association: 0,
            groupObj: 0,
            param: 0,
            param2: 0,
          };
      // Get existing table sizes from memory
      // paramTableInMemory = await this.readMemoryBlockFromDevice(
      //   deviceAddress,
      //   tableOffsets.param,
      //   tableSizes.param,
      //   false,
      //  10
      // );
      finalProcedure = await this.createFinalProcedure(
        device,
        AP,
        AppIdHex,
        applicationId,
        LoadProcedures,
        maskVersion,
        isIPRouterMask,
        tableSizes,
        defaultProcedure,
      );
    }
    console.log("[KnxBridge] FinalProcedure: ");
    console.log(finalProcedure);

    this.showProgressToast(
      state.translate("knxBridge.executeLoadProcedure", "Executing LoadProcedure"),
      this.computeProgressFromStep(8, 100),
    );

    let VerifyModeOn = false;

    const flattenedProcedures = finalProcedure.items.flatMap((lp: any) => lp?.items ?? []).filter(Boolean);
    const totalProcedureSteps = Math.max(1, flattenedProcedures.length);
    let completedProcedureSteps = 0;
    const nextLPPercent = () => {
      completedProcedureSteps += 1;
      return this.computeProgressFromStep(completedProcedureSteps, totalProcedureSteps);
    };

    // Only used for MV-0021 devices where the association table starts right after address table ends
    let adjustedAssocOffset = tableOffsets.association;
    let adjustedAssocLength = 0;

    // EXECUTE LoadProcedure
    for (let i in finalProcedure.items) {
      const LoadProcedure = finalProcedure.items[i];
      if (LoadProcedure) {
        let procedureIndex = 0;
        for (procedureIndex; procedureIndex < LoadProcedure.items.length; procedureIndex++) {
          const procedure = LoadProcedure.items[procedureIndex];
          const tag = procedure["#name"];

          const tabId = procedure.a?.LsmIdx;
          const postfix =
            tabId == "1"
              ? "AddressTable" // no itx
              : tabId == "2"
                ? "AssociationTable" // no itx
                : tabId == "3"
                  ? "ComObjectTable" // no itx
                  : tabId == "4"
                    ? "ParameterTable" // no itx
                    : "";

          // Common / ProductProcedure Tags
          if (tag == "LdCtrlConnect") {
            this.showProgressToast(
              state.translate("knxBridge.LdCtrlConnect", "Executing LoadProcedure: Connecting to Device"),
              nextLPPercent(),
            );
            await sleep(3000);
            await this.setDeviceProgMode(deviceAddress, 0);
          } else if (tag == "LdCtrlCompareProp") {
            this.showProgressToast(
              state.translate("knxBridge.LdCtrlCompareProp", "Executing LoadProcedure: Verifying Device Properties"),
              nextLPPercent(),
            );

            const ObjId = procedure.a.ObjIdx; // Object Id
            const PropId = procedure.a.PropId; // Property Id or PID
            const InlineData = procedure.a?.InlineData ? procedure.a?.InlineData.toLowerCase() : undefined; // "Should be" Value at (ObjId,PID)

            // Note that InlineData may not be defined; instead Range is mentioned (e.g. Range="[4160,65535]u" in MDT VC-Easy.02)
            // MWE 22.10.2024 : Handle "Range" attribute
            if (InlineData) {
              const responseHex = await this.getDeviceProperty(deviceAddress, PropId, ObjId);
              if (!InlineData.startsWith(responseHex))
                console.log(
                  `[KnxBridge] LdCtrlCompareProp mismatch: ${ObjId} ${PropId} => ${InlineData} != ${responseHex}`,
                );
            } else {
              console.log(`[KnxBridge] InlineData not defined in AP; Range = ${procedure.a?.Range}`);
            }
          } else if (tag == "LdCtrlUnload") {
            this.showProgressToast(
              state.translate("knxBridge.LdCtrlUnload", "Executing LoadProcedure: Unloading"),
              nextLPPercent(),
              postfix,
            );
            const objId = procedure.a?.LsmIdx ?? procedure.a.ObjType;
            if (objId) {
              // await sleep(1000);
              await this.setDeviceProperty(
                deviceAddress,
                this.writableLoadEvent(LoadEvent.Unload, maskVersion, procedure.a.LsmIdx),
                PIDList.LoadStateControl,
                procedure.a.LsmIdx,
              ); // LoadEvent LE=4 Outputs LoadState LS=0 (Unloaded)
            }
          } else if (tag == "LdCtrlLoad") {
            if (maskVersion == "0021" && procedure.a.LsmIdx == "5") {
              console.log(
                "[KnxBridge] Skipping LdCtrlLoad for LsmIdx 5 on MaskVersion 0021 (Legacy object table address)",
              );
            } else {
              this.showProgressToast(
                state.translate("knxBridge.LdCtrlLoad", "Executing LoadProcedure: Loading"),
                nextLPPercent(),
                postfix,
              );

              // MWE dont set LsmId if manId = 0085 videostar
              // await sleep(1000);
              await this.setDeviceProperty(
                deviceAddress,
                this.writableLoadEvent(LoadEvent.StartLoading, maskVersion, procedure.a.LsmIdx),
                PIDList.LoadStateControl,
                procedure.a.LsmIdx,
              ); // LE=1 Outputs LS=2 (Loading)
            }
          } else if (tag == "LdCtrlAbsSegment") {
            // PRODUCT Only
            // 1. Write LoadEvents for All Segments - AddrTable, AssocTable, ComObjTable, Params, RAM and RAM2
            // 2. Write respective Tables (only for MemType=2, EEPROM)
            const LsmIdx = procedure.a.LsmIdx; // Load State Machine Id
            const loadEvent = LoadEvent.AdditionalLoadControls;

            let tableLength = parseInt(procedure.a?.Size || "0");
            if (isBcu2Mask) {
              if (LsmIdx == "1") {
                // Construct Address Table
                let addressTable = "";
                const isMerged = LpStyle == MergedProcedure;
                const sortedHexAddresses = Array.from(setOfAddresses).sort();
                const countBytes = isMerged ? 2 : 1;
                const deviceAddrCount = isMerged ? 0 : 1;
                const addressLengthHex = (sortedHexAddresses.length + deviceAddrCount)
                  .toString(16)
                  .padStart(countBytes * 2, "0"); // "00 05 => 2 bytes data or string length 2*2 = 4"
                const deviceAddressHex = this.deviceAddressToHex(deviceAddress);
                addressTable = addressLengthHex + (isMerged ? "" : deviceAddressHex) + sortedHexAddresses.join("");
                tableLength = (addressTable.length + 2) / 2; // in bytes

                adjustedAssocLength = parseInt(procedure.a?.Size || tableLength.toString()) - tableLength;
              } else if (LsmIdx == "2") {
                adjustedAssocLength += parseInt(procedure.a?.Size || "0");
                tableLength = adjustedAssocLength;
              }
            }

            // Construct LoadEvent Octets
            const Event1 = maskVersion == "0701" ? LsmIdx + loadEvent + "00" : loadEvent.padStart(2, "0"); // Constant Value  // no itx
            const SegType2 = this.decimalToHex(parseInt(procedure.a.SegType || "0"), 2);
            const Address3_4 =
              isBcu2Mask && LsmIdx == "2"
                ? adjustedAssocOffset
                : this.decimalToHex(parseInt(procedure.a.Address || "0"), 4);
            const Size5_6 = this.decimalToHex(tableLength, 4);
            const Access7 = this.decimalToHex(parseInt(procedure.a.Access || "0"), 2);
            const MemType8 = this.decimalToHex(parseInt(procedure.a.MemType || "0"), 2);
            const SegFlags9 = this.decimalToHex(parseInt(procedure.a.SegFlags || "0"), 2);
            const Reserved10 = "00"; // Reserved

            // Load Event should have 10 Octets i.e. length 20 (0705); 11 Octets/ Length 22 (0701)
            const finalLEOctet = Event1 + SegType2 + Address3_4 + Size5_6 + Access7 + MemType8 + SegFlags9 + Reserved10;

            const isPropertyModifiable =
              (maskVersion != "0701" && finalLEOctet.length == 20) ||
              (finalLEOctet.length == 22 &&
                maskVersion == "0701" &&
                (LsmIdx == "1" ||
                  LsmIdx == "2" ||
                  LsmIdx == "4" ||
                  LsmIdx == "5" ||
                  (LsmIdx == "3" && procedure.a.MemType == "3")));
            if (isPropertyModifiable) {
              this.showProgressToast(
                state.translate("knxBridge.LdCtrlAbsSegment", "Executing LoadProcedure: Writing LoadEvent for"),
                nextLPPercent(),
                postfix,
              );
              await this.setDeviceProperty(
                deviceAddress,
                this.prefixHexType(finalLEOctet),
                PIDList.LoadStateControl,
                procedure.a.LsmIdx,
              );
            }

            if (!VerifyModeOn) {
              await this.setDeviceVerify(deviceAddress, true);
              VerifyModeOn = true;
            }

            // MWE 27.08.2024 (Fixed): Actual Parameters write also handled
            if (LsmIdx == "1") {
              this.toastTextUI = state.translate(
                "knxBridge.writeAddrTableToMemory",
                "Executing LoadProcedure: Writing Addresses to Memory",
              );
              this.increasePercent();
              adjustedAssocOffset = await this.writeOrVerifyAddrTableToMemory(
                deviceAddress,
                tableOffsets.address,
                setOfAddresses,
                LpStyle,
              );
            } else if (LsmIdx == "2") {
              this.toastTextUI = state.translate(
                "knxBridge.writeAssocTableToMemory",
                "Executing LoadProcedure: Writing Address Associations to Memory",
              );
              this.increasePercent();
              const sortedHexAddresses = Array.from(setOfAddresses).sort();
              await this.writeOrVerifyAssocTableToMemory(
                deviceAddress,
                maskVersion == "0021" && adjustedAssocOffset
                  ? this.prefixHexType(adjustedAssocOffset)
                  : tableOffsets.association,
                sortedHexAddresses,
                associationList,
                LpStyle,
                maskVersion,
                AP,
              );
            } else if (LsmIdx == "3") {
              const groupObjTableAddress = parseInt(tableOffsets.groupObj, 16);
              const MemType = procedure.a.MemType;
              if (MemType == "3") {
                // EEPROM (Stores ComObj and Params)
                const Address = parseInt(procedure.a.Address);
                if (Address == groupObjTableAddress) {
                  this.toastTextUI = state.translate(
                    "knxBridge.writeComObjTableToMemory",
                    "Executing LoadProcedure: Writing ComObjects to Memory",
                  );
                  this.increasePercent();
                  // ComObj
                  await this.writeOrVerifyComObjTableToMemory(
                    device,
                    deviceAddress,
                    tableOffsets.groupObj,
                    setOfCobs,
                    associationList,
                    memorySegments,
                    LpStyle,
                    maskVersion,
                  );
                } else if (Address > groupObjTableAddress) {
                  this.toastTextUI = state.translate(
                    "knxBridge.writeActualParamsToMemory",
                    "Executing LoadProcedure: Writing Parameters to Memory",
                  );
                  this.increasePercent();
                  // Params
                  // (Fixed 10.09.2024) MWE 27.08.2024: Write actual parameters
                  await this.writeOrVerifyActualParamsToMemory(
                    device,
                    deviceAddress,
                    Address.toString(),
                    memorySegments,
                    LpStyle,
                    maskVersion,
                  );
                } else {
                  console.error("Unidentified Address for AbsSegment procedure: ", procedure);
                }
              } else if (MemType == "2") {
                // RAM
                const SegType = procedure.a.SegType;
                if (!(SegType == "0" || SegType == "1")) {
                  console.error("Unidentified SegType for AbsSegment procedure: ", procedure);
                }
              }
            }
            await sleep(1000);
          } else if (tag == "LdCtrlTaskSegment") {
            this.showProgressToast(
              state.translate("knxBridge.LdCtrlTaskSegment", "Executing LoadProcedure: Writing Task Segment for"),
              nextLPPercent(),
              postfix,
            );
            // PRODUCT Only
            const LsmIdx = procedure.a.LsmIdx; // Load State Machine Id
            const loadEvent = LoadEvent.AdditionalLoadControls;

            if (maskVersion == "0021" && LsmIdx == "5") {
              console.log(
                "[KnxBridge] Skipping LdCtrlTaskSegment for LsmIdx 5 on MaskVersion 0021 (Legacy object table address)",
              );
            } else {
              const Event1 = maskVersion == "0701" ? LsmIdx + loadEvent + "00" : loadEvent.padStart(2, "0"); // Constant Value  // no itx
              const Reserved2 = OutputLoadStates.Loading.padStart(2, "0"); // Constant (Verification required)
              const Address3_4 =
                isBcu2Mask && LsmIdx == "2"
                  ? adjustedAssocOffset
                  : procedure.a?.Address
                    ? this.decimalToHex(parseInt(procedure.a.Address), 4)
                    : "0000";
              const PeiType5 = PeiType ? this.decimalToHex(parseInt(PeiType)) : "00"; // PeiType  // no itx
              const AppId6_10 = procedure.a?.ApplicationID ? procedure.a?.ApplicationID : AppIdHex;

              // Load Event should have 10 Octets i.e. length 20 (0705); 11 Octets/ Length 22 (0701)
              const finalLEOctet = Event1 + Reserved2 + Address3_4 + PeiType5 + AppId6_10;

              const isPropertyModifiable =
                (maskVersion != "0701" && finalLEOctet.length == 20) ||
                (finalLEOctet.length == 22 && maskVersion == "0701" && LsmIdx != "3");

              if (isPropertyModifiable) {
                await this.setDeviceProperty(
                  deviceAddress,
                  this.prefixHexType(finalLEOctet),
                  PIDList.LoadStateControl,
                  procedure.a.LsmIdx,
                );
              }
            }
          } else if (tag == "LdCtrlLoadCompleted") {
            this.showProgressToast(
              state.translate("knxBridge.LdCtrlLoadCompleted", "Executing LoadProcedure: Completing"),
              nextLPPercent(),
              postfix,
            );
            await this.setDeviceProperty(
              deviceAddress,
              this.writableLoadEvent(LoadEvent.LoadCompleted, maskVersion, procedure.a.LsmIdx),
              PIDList.LoadStateControl,
              procedure.a.LsmIdx,
            ); // LE=2 Outputs LS=1 (Loaded)
          } else if (tag == "LdCtrlRestart") {
            this.showProgressToast(
              state.translate("knxBridge.LdCtrlRestart", "Executing LoadProcedure: Restarting Device"),
              nextLPPercent(),
            );
            if (this.percentUI > 90) this.increasePercent(95);
            await this.restartDevice(deviceAddress, manIdHex);
          } else if (tag == "LdCtrlDisconnect") {
            this.showProgressToast(
              state.translate("knxBridge.LdCtrlDisconnect", "Executing LoadProcedure: Finalizing"),
              nextLPPercent(),
            );
            // await this.setDeviceProgMode(deviceAddress,0);
          }

          // Merged Procedure Tags
          else if (tag == "LdCtrlMapError") {
            // No execution found during ETS Download
            // Used only in Seacure Device mode.
          }
          // else if (tag == "LdCtrlWriteMem") {
          //   // Tested On Hager TXM620D
          //   // E.g. <LdCtrlWriteMem Address="264" Size="14" Verify="true" /> in SCN-IP100.03 does not state InlineData / Relative Segment Data.
          //   // await this.setDeviceVerify(deviceAddress, procedure.a?.Verify == "true");
          //   if (!VerifyModeOn && procedure.a?.Verify == "true") {
          //     await this.setDeviceVerify(deviceAddress, true);
          //     VerifyModeOn = true;
          //   }
          // }
          else if (tag == "LdCtrlWriteMem") {
            const address = procedure.a?.Address ? parseInt(procedure.a.Address) : "";
            const size = procedure.a?.Size ? parseInt(procedure.a.Size) : "";
            const verify = procedure.a?.Verify == "true";
            const addressSpace = procedure.a?.AddressSpace;

            if (verify && !VerifyModeOn) {
              await this.setDeviceVerify(deviceAddress, true);
              VerifyModeOn = true;
            }

            if (address !== "" && size !== "") {
              // --- IP Router / AbsoluteSegment handling ---
              // Find AbsoluteSegment containing this address range
              const absSegment = memorySegments.find(
                (seg) => seg.Address <= address && seg.Address + seg.size >= address + size,
              );

              if (!absSegment) {
                console.error("[KnxBridge] LdCtrlWriteMem: No AbsoluteSegment found", procedure);
                continue;
              }

              this.toastTextUI = state.translate("knxBridge.writeMemory", "Executing LoadProcedure: Writing Memory");
              this.increasePercent();

              await this.writeAbsoluteSegmentSliceToMemory(deviceAddress, absSegment, address, size, verify);
            }
          } else if (tag == "LdCtrlWriteProp") {
            this.showProgressToast(
              state.translate("knxBridge.LdCtrlWriteProp", "Executing LoadProcedure: Verifying Device Properties"),
              nextLPPercent(),
            );

            if (!VerifyModeOn && procedure.a?.Verify == "true") {
              await this.setDeviceVerify(deviceAddress, true);
              VerifyModeOn = true;
            }

            const ObjId = procedure.a?.ObjIdx ?? procedure.a?.ObjType;
            const PropId = procedure.a.PropId;
            const StartElement = procedure.a?.StartElement ? procedure.a?.StartElement : "1";
            const Count = "1"; // procedure.a?.Count ? procedure.a?.Count : "1";

            let InlineData = procedure.a?.InlineData ? procedure.a?.InlineData.toLowerCase() : undefined;

            if (InlineData == undefined) {
              if (ObjId == "6" && PropId == "57") {
                // 00001000; Value for TP line medium
                // 11111000; Value for other medium types (PL, RF, IP Coupler)
                // for PL (Powerline) SNA required
                // for RF SNA required
                // for IP routing via coupler SNA Required
                // for TP1 alone disabled; for coupler roles enabled

                // Bit info:
                // 0 - EN_SNA_INCONSISTENCY_CHECK
                // 1 - EN_SNA_HEARTBEAT
                // 2 - EN_SNA_UPDATE_WRITE
                // 3 - EN_SNA_READ
                // for Bits 0-3, bit value 0 for TP1; value 1 for PL RF IP

                // 4 - EN_SUBLINE_STATUS // enabled irrespective of TP mode
                // 5-7 - Reserved (value 0)
                InlineData = device.template?.knxProductData.productInfo.MediumType != "TP" ? "f8" : "08";
              } else if (ObjId == "11") {
                if (PropId == "201" || PropId == "201") {
                  InlineData = "00000000";
                } else if (PropId == "203") {
                  InlineData = "00";
                }
              }
            }

            if (InlineData != undefined)
              InlineData = manIdHex == "0048" ? InlineData.substring(0, 16) : InlineData; // For Theben devices no reserved bytes
            else InlineData = "";

            if (InlineData != undefined && ObjId != undefined && PropId != undefined) {
              if (isBcu2Mask && PropId == "8" && ObjId == "0" && parseInt(InlineData, 16) == 0) {
                InlineData = "0000";
              }
              await this.setDeviceProperty(
                deviceAddress,
                this.prefixHexType(InlineData),
                PropId,
                ObjId,
                StartElement,
                Count,
              );
            }

            // if (InlineData) {
            //   const responseHex = await this.getDeviceProperty(deviceAddress, PropId, ObjId, StartElement, Count);
            //   if (InlineData != responseHex)
            //     await this.setDeviceProperty(deviceAddress, this.prefixHexType(InlineData), PropId, ObjId, StartElement, Count);
            // } else {
            //   console.log(`[KnxBridge] InlineData not defined in AP; Range = ${procedure.a?.Range}`);
            // }
          } else if (tag == "LdCtrlRelSegment") {
            // MERGED Only

            const Event1 = LoadEvent.AdditionalLoadControls.padStart(2, "0"); // Load Event  // no itx
            const Unknown2 = "0b"; // System B? (MWE 22.10.2024: Require verification)  // no itx
            const Address3_4 = procedure.a?.Address ? this.decimalToHex(parseInt(procedure.a.Address), 4) : "0000";
            const Size5_6 = procedure.a?.Size ? this.decimalToHex(parseInt(procedure.a.Size), 4) : "0000";
            const Mode7 = procedure.a?.Mode ? this.decimalToHex(parseInt(procedure.a.Mode), 2) : "00";
            const Fill8 = procedure.a?.Fill ? this.decimalToHex(parseInt(procedure.a.Fill), 2) : "00";
            const Reserved9_10 = "0000"; // Reserved

            const finalLEOctet = Event1 + Unknown2 + Address3_4 + Size5_6 + Mode7 + Fill8 + Reserved9_10; // LoadEvent is always 10 Octets
            if (
              (finalLEOctet.length == 22 && maskVersion == "0701") ||
              (finalLEOctet.length == 20 && maskVersion != "0701")
            ) {
              this.showProgressToast(
                state.translate("knxBridge.LdCtrlAbsSegment", "Executing LoadProcedure: Writing LoadEvent for"),
                nextLPPercent(),
                postfix,
              );
              // await sleep(1000);
              await this.setDeviceProperty(
                deviceAddress,
                this.prefixHexType(finalLEOctet),
                PIDList.LoadStateControl,
                procedure.a.LsmIdx,
              );
            }
          } else if (tag == "LdCtrlWriteRelMem") {
            // NEU change: Getting PID 7 here should yield actual memory offset
            await sleep(2000);
            const ObjIdx = procedure.a.ObjIdx;
            // if(ObjIdx == "3") await this.setDeviceVerify(deviceAddress, true);
            const offsetHex = await this.getDeviceProperty(deviceAddress, PIDList.TableReference, ObjIdx);

            // MERGEDProcedure Only
            if (procedure.a?.Verify == "true") {
              //!VerifyModeOn &&
              await this.setDeviceVerify(deviceAddress, true);
              VerifyModeOn = true;
            }

            if (offsetHex) {
              if (ObjIdx == "4") {
                this.toastTextUI = state.translate(
                  "knxBridge.writeActualParamsToMemory",
                  "Executing LoadProcedure: Writing Parameters to Memory",
                );
                await this.writeOrVerifyActualParamsToMemory(
                  device,
                  deviceAddress,
                  offsetHex,
                  memorySegments,
                  LpStyle,
                  maskVersion,
                  paramTableInMemory,
                );
              } else if (ObjIdx == "3") {
                this.toastTextUI = state.translate(
                  "knxBridge.writeComObjTableToMemory",
                  "Executing LoadProcedure: Writing ComObjects to Memory",
                );
                this.increasePercent();
                await this.writeOrVerifyComObjTableToRelMemory(
                  device,
                  deviceAddress,
                  AP,
                  offsetHex,
                  memorySegments,
                  setOfCobs,
                  associationList,
                  LpStyle,
                );
              } else if (ObjIdx == "2") {
                this.toastTextUI = state.translate(
                  "knxBridge.writeAssocTableToMemory",
                  "Executing LoadProcedure: Writing Address Associations to Memory",
                );
                this.increasePercent();
                const sortedHexAddresses = Array.from(setOfAddresses).sort();
                await this.writeOrVerifyAssocTableToMemory(
                  deviceAddress,
                  offsetHex,
                  sortedHexAddresses,
                  associationList,
                  LpStyle,
                  maskVersion,
                  AP,
                );
              } else if (ObjIdx == "1") {
                this.toastTextUI = state.translate(
                  "knxBridge.writeAddrTableToMemory",
                  "Executing LoadProcedure: Writing Addresses to Memory",
                );
                this.increasePercent();
                await this.writeOrVerifyAddrTableToMemory(deviceAddress, offsetHex, setOfAddresses, LpStyle);
              }
              await sleep(1000);
            }
          } else if (tag == "LdCtrlLoadImageProp") {
            const ObjId = procedure.a.ObjIdx;
            const PropId = procedure.a.PropId;
            const StartElement = procedure.a.StartElement;
            const Count = procedure.a.Count;
            const responseHex = await this.getDeviceProperty(deviceAddress, PropId, ObjId, StartElement, Count);
            console.log(responseHex);
          }

          // Newly Added Tags 15.09.2025
          else if (tag == "LdCtrlInvokeFunctionProp") {
            // Handlings for Hager device TXM620D
            const ObjId = procedure.a.ObjIdx;
            const PropId = procedure.a.PropId;
            const InlineData = procedure.a.InlineData;
            const AppliesTo = procedure.a.AppliesTo;
            if (ObjId && PropId) {
              if (InlineData == "04") {
                this.resetDevice(deviceAddress, "6", "0");
              } else if (InlineData == "10") {
                this.resetDevice(deviceAddress, "5", "0");
              }
            }
          } else if (tag == "LdCtrlMasterReset") {
            const EraseCode = procedure.a.EraseCode;
            const ChannelNumber = procedure.a.ChannelNumber;
            if (this.percentUI > 90) this.increasePercent(95);
            this.resetDevice(deviceAddress, EraseCode, ChannelNumber);
          } else if (tag == "LdCtrlTaskPtr") {
            // Tested on Merten Devices
            const Event1 = LoadEvent.AdditionalLoadControls.padStart(2, "0"); // Load Event  // no itx
            const TagCode2 = "03"; // Code for LdCtrlTaskPtr  // no itx
            const InitPtr3_4 = procedure.a?.InitPtr ? this.decimalToHex(parseInt(procedure.a.InitPtr), 4) : "0000";
            const SavePtr5_6 = procedure.a?.SavePtr ? this.decimalToHex(parseInt(procedure.a.SavePtr), 4) : "0000";
            const SerialPtr7_8 = procedure.a?.SerialPtr
              ? this.decimalToHex(parseInt(procedure.a.SerialPtr), 4)
              : "0000";
            const Reserved9_10 = "0000"; // Reserved

            const finalLEOctet = Event1 + TagCode2 + InitPtr3_4 + SavePtr5_6 + SerialPtr7_8 + Reserved9_10; // LoadEvent is always 10 Octets
            if (
              (finalLEOctet.length == 22 && maskVersion == "0701") ||
              (finalLEOctet.length == 20 && maskVersion != "0701")
            ) {
              this.showProgressToast(
                state.translate("knxBridge.LdCtrlTaskPtr", "Executing LoadProcedure: LdCtrlTaskPtr"),
                nextLPPercent(),
                postfix,
              );
              // await sleep(1000);
              await this.setDeviceProperty(
                deviceAddress,
                this.prefixHexType(finalLEOctet),
                PIDList.LoadStateControl,
                procedure.a.LsmIdx,
              );
            }
          } else if (tag == "LdCtrlTaskCtrl1") {
            // Tested on Merten Devices
            const Event1 = LoadEvent.AdditionalLoadControls.padStart(2, "0"); // Load Event  // no itx
            const TagCode2 = "04"; // Code for LdCtrlTaskCtrl1  // no itx
            const Address3_4 = procedure.a?.Address ? this.decimalToHex(parseInt(procedure.a.Address), 4) : "0000";
            const Count5 = procedure.a?.Count ? this.decimalToHex(parseInt(procedure.a.Count), 2) : "00";
            const Reserved6_10 = "0000000000"; // Reserved

            const finalLEOctet = Event1 + TagCode2 + Address3_4 + Count5 + Reserved6_10; // LoadEvent is always 10 Octets
            if (
              (finalLEOctet.length == 22 && maskVersion == "0701") ||
              (finalLEOctet.length == 20 && maskVersion != "0701")
            ) {
              this.showProgressToast(
                state.translate("knxBridge.LdCtrlTaskCtrl1", "Executing LoadProcedure: LdCtrlTaskCtrl1"),
                nextLPPercent(),
                postfix,
              );
              // await sleep(1000);
              await this.setDeviceProperty(
                deviceAddress,
                this.prefixHexType(finalLEOctet),
                PIDList.LoadStateControl,
                procedure.a.LsmIdx,
              );
            }
          } else if (tag == "LdCtrlTaskCtrl2") {
            // Tested on Merten Devices
            const Event1 = LoadEvent.AdditionalLoadControls.padStart(2, "0"); // Load Event  // no itx
            const TagCode2 = "05"; // Code for LdCtrlTaskCtrl2  // no itx
            const Callback3_4 = procedure.a?.Callback ? this.decimalToHex(parseInt(procedure.a.Callback), 4) : "0000";
            const Address5_6 = procedure.a?.Address ? this.decimalToHex(parseInt(procedure.a.Address), 4) : "0000";
            const Seg0_7_8 = procedure.a?.Seg0 ? this.decimalToHex(parseInt(procedure.a.Seg0), 4) : "0000";
            const Seg1_9_10 = procedure.a?.Seg1 ? this.decimalToHex(parseInt(procedure.a.Seg1), 4) : "0000";

            const finalLEOctet = Event1 + TagCode2 + Callback3_4 + Address5_6 + Seg0_7_8 + Seg1_9_10; // LoadEvent is always 10 Octets
            if (
              (finalLEOctet.length == 22 && maskVersion == "0701") ||
              (finalLEOctet.length == 20 && maskVersion != "0701")
            ) {
              this.showProgressToast(
                state.translate("knxBridge.LdCtrlTaskCtrl2", "Executing LoadProcedure: LdCtrlTaskCtrl2"),
                nextLPPercent(),
                postfix,
              );
              // await sleep(1000);
              await this.setDeviceProperty(
                deviceAddress,
                this.prefixHexType(finalLEOctet),
                PIDList.LoadStateControl,
                procedure.a.LsmIdx,
              );
            }
          } else if (tag == "LdCtrlDelay") {
            // E.g. <LdCtrlDelay MilliSeconds="1000" />
            const delayMs = procedure.a?.MilliSeconds ? parseInt(procedure.a.MilliSeconds) : 1000;
            await sleep(delayMs);
          } else if (tag == "LdCtrlClearLCFilterTable") {
            const interfaceObjectsCount = await this.getDeviceProperty(deviceAddress, "71", "0", "0", "1"); // PID 71, ObjId 0, StartElement 0, Count 1
            console.log("[KnxBridge] LdCtrlClearLCFilterTable: Interface Objects Count: ", interfaceObjectsCount);
            const countInt = parseInt(interfaceObjectsCount, 16);
            const interfaceObjects = await this.getDeviceProperty(deviceAddress, "71", "0", "1", countInt.toString()); // PID 71, ObjId 0, StartElement 1, Count countInt
            console.log("[KnxBridge] InterfaceObjects List: ", interfaceObjects);
            if (interfaceObjects) {
              const objList = interfaceObjects.match(/.{1,4}/g);
              const routerObjIndex = objList.indexOf("0006");
              if (routerObjIndex > 0) {
                // PID_ROUTETABLE_CONTROL (PID = 56)
                // ServiceID: 1 (SRVID_CLEAR_FILTERTABLE)
                // a) Write (A_FunctionPropertyCommand-PDU)
                // octet 10 octet 11
                // 00h 01h

                // FunctionPropertyCommand_req -> 1.1.0 (hc=6, S=2) ObjectIndex=1 PropertyId=56 Data=0001
                await this.setDeviceProperty(deviceAddress, "0001", "56", routerObjIndex.toString());
              }
            }
          }

          // else if (tag == "LdCtrlCompareRelMem") {
          // } else if (tag == "LdCtrlDeclarePropDesc") {
          // }
          // TODO: "LdCtrlCompareRelMem" "LdCtrlDeclarePropDesc"
          // LdCtrlSetControlVariable <LdCtrlSetControlVariable Name="EnableVerifyOnWriteDirect" Value="true" />
          // LdCtrlLoadImageMem <LdCtrlLoadImageMem Address="278" Size="1" />
          // LdCtrlClearLCFilterTable
          // <LdCtrlWriteMem AddressSpace="LcFilter" Address="512" Size="3584" Verify="true" />
          else {
            console.error("EXECUTE: Unidentified LoadProcedure item: ", tag);
          }
        }
        if (procedureIndex < LoadProcedure.items.length)
          console.error("Load Procedure unexpectedly terminated at step ", procedureIndex);
      }
    }
  }

  // Example ProductProcedure:
  // <LoadProcedure>
  //   <LdCtrlConnect />
  //   <LdCtrlCompareProp ObjIdx="0" PropId="78" InlineData="00000000022700000000" />
  //   <LdCtrlUnload LsmIdx="1" />
  //   <LdCtrlUnload LsmIdx="2" />
  //   <LdCtrlUnload LsmIdx="3" />
  //   <LdCtrlLoad LsmIdx="1" />
  //   <LdCtrlAbsSegment LsmIdx="1" SegType="0" Address="16384" Size="513" Access="255" MemType="3" SegFlags="128" />
  //   <LdCtrlTaskSegment LsmIdx="1" Address="16384" />
  //   <LdCtrlLoadCompleted LsmIdx="1" />
  //   <LdCtrlLoad LsmIdx="2" />
  //   <LdCtrlAbsSegment LsmIdx="2" SegType="0" Address="16897" Size="511" Access="255" MemType="3" SegFlags="128" />
  //   <LdCtrlTaskSegment LsmIdx="2" Address="16897" />
  //   <LdCtrlLoadCompleted LsmIdx="2" />
  //   <LdCtrlLoad LsmIdx="3" />
  //   <LdCtrlAbsSegment LsmIdx="3" SegType="0" Address="1792" Size="488" Access="0" MemType="2" SegFlags="0" />
  //   <LdCtrlAbsSegment LsmIdx="3" SegType="1" Address="2280" Size="1" Access="0" MemType="2" SegFlags="0" />
  //   <LdCtrlAbsSegment LsmIdx="3" SegType="0" Address="17408" Size="544" Access="255" MemType="3" SegFlags="128" />
  //   <LdCtrlAbsSegment LsmIdx="3" SegType="0" Address="17952" Size="1770" Access="255" MemType="3" SegFlags="128" />
  //   <LdCtrlTaskSegment LsmIdx="3" Address="17408" />
  //   <LdCtrlLoadCompleted LsmIdx="3" />
  //   <LdCtrlRestart />
  //   <LdCtrlDisconnect />
  // </LoadProcedure>

  // Example MergedProcedure:
  // <LoadProcedures>
  //   <LoadProcedure MergeId="2">
  // 	<LdCtrlRelSegment AppliesTo="full" LsmIdx="4" Size="752" Mode="1" Fill="0" />
  // 	<LdCtrlRelSegment AppliesTo="par" LsmIdx="4" Size="752" Mode="0" Fill="0" />
  //   </LoadProcedure>
  //   <LoadProcedure MergeId="4">
  // 	<LdCtrlWriteRelMem AppliesTo="full,par" ObjIdx="4" Offset="0" Size="752" Verify="true" />
  //   </LoadProcedure>
  //   <LoadProcedure MergeId="7">
  // 	<LdCtrlLoadImageProp ObjIdx="1" PropId="27" />
  // 	<LdCtrlLoadImageProp ObjIdx="2" PropId="27" />
  // 	<LdCtrlLoadImageProp ObjIdx="3" PropId="27" />
  // 	<LdCtrlLoadImageProp ObjIdx="4" PropId="27" />
  //   </LoadProcedure>
  // </LoadProcedures>

  // Example (Merged + DefaultProcedure):
  // <LdCtrlConnect />
  // <LdCtrlMapError OriginalError="3221498632" MappedError="0" />
  // <LdCtrlUnload LsmIdx="5" />
  // <LdCtrlMapError OriginalError="3221498632" MappedError="3221498632" />
  // <LdCtrlUnload LsmIdx="4" />
  // <LdCtrlUnload LsmIdx="3" />
  // <LdCtrlUnload LsmIdx="2" />
  // <LdCtrlUnload LsmIdx="1" />
  // <LdCtrlLoad LsmIdx="4" />
  // <LdCtrlRelSegment LsmIdx="4" Size="752" Mode="1" Fill="0" />
  // <LdCtrlLoad LsmIdx="3" />
  // <LdCtrlRelSegment LsmIdx="3" Size="446" Mode="0" Fill="0" />
  // <LdCtrlLoad LsmIdx="1" />
  // <LdCtrlRelSegment LsmIdx="1" Size="6" Mode="0" Fill="0" />
  // <LdCtrlLoad LsmIdx="2" />
  // <LdCtrlRelSegment LsmIdx="2" Size="10" Mode="0" Fill="0" />
  // <LdCtrlWriteRelMem ObjIdx="4" Offset="0" Size="752" Verify="true" />
  // <LdCtrlWriteRelMem ObjIdx="3" Offset="0" Size="446" Verify="true" />
  // <LdCtrlWriteRelMem ObjIdx="2" Offset="0" Size="10" Verify="true" />
  // <LdCtrlWriteRelMem ObjIdx="1" Offset="0" Size="6" Verify="true" />
  // <LdCtrlWriteProp ObjIdx="4" PropId="13" Verify="true" InlineData="0083013B33" />
  // <LdCtrlLoadCompleted LsmIdx="4" />
  // <LdCtrlLoadCompleted LsmIdx="3" />
  // <LdCtrlLoadCompleted LsmIdx="2" />
  // <LdCtrlLoadCompleted LsmIdx="1" />
  // <LdCtrlLoadImageProp ObjIdx="1" PropId="27" />
  // <LdCtrlLoadImageProp ObjIdx="2" PropId="27" />
  // <LdCtrlLoadImageProp ObjIdx="3" PropId="27" />
  // <LdCtrlLoadImageProp ObjIdx="4" PropId="27" />
  // <LdCtrlRestart />
  // <LdCtrlDisconnect />

  ///////////////////////////////
  // Getters - Device Properties
  ///////////////////////////////

  // Device Properties
  // ObjIdx= 0 ; PID = 11, 12, 14, 15, 54, 56, 78, 83
  // for Each getDeviceProperty response
  // Index 0 is Complete String response
  // Index 1 is Named Value
  // Index 2 is Hex Value
  async getSerialFromDevice(deviceAddress: string) {
    return await this.getDeviceProperty(deviceAddress, PIDList.KNXSerialNumber);
  }

  async getManIdFromDevice(deviceAddress: string) {
    return await this.getDeviceProperty(deviceAddress, PIDList.ManufacturerId);
  }

  async getControlStateFromDevice(deviceAddress: string) {
    return await this.getDeviceProperty(deviceAddress, PIDList.DeviceControl);
  }

  async getOrderInfoFromDevice(deviceAddress: string) {
    return await this.getDeviceProperty(deviceAddress, PIDList.OrderInfo);
  }

  async getMaxAPDULengthFromDevice(
    deviceAddress: string,
    manId: string,
    probeOffsetHex: string = this.defaultAddressTableOffset,
  ) {
    const res = await this.getDeviceProperty(deviceAddress, PIDList.MaxAPDULength);

    // USB / Serial interfaces typically use standard frames for management; keep conservative.
    if (connType === "usb" || connType === "serial") {
      this.maxBytesReadable = 12;
      this.maxNibblesReadable = this.maxBytesReadable * 2;
      return this.maxBytesReadable;
    }

    // PID 56 returns the *APDU length capability*. In practice, the reported value can be wrong by 1+ octet
    // (or reduced by couplers/interfaces). ETS therefore has fallback mechanisms.
    // We derive a safe *payload* size for memory/property services and probe it on the bus.
    const reportedApdu = this.isValidHex(res) ? parseInt(res, 16) : Number.parseInt(res || "0", 10);
    const apdu = Number.isFinite(reportedApdu) && reportedApdu > 0 ? reportedApdu : 15;

    // Reserve some bytes for management service overhead (TPCI/APCI/address fields).
    // This is intentionally conservative; we then probe and back off on failures.
    let candidate = MathUtils.clamp(apdu - 3, 12, 240);

    // Real-world quirk: some devices advertise high APDU but fail on large memory blocks.
    // Keep a sensible cap, then probe downward.
    const HARD_CAP = 50;
    candidate = Math.min(candidate, HARD_CAP);

    const probeBase = this.prefixHexType(probeOffsetHex || this.defaultAddressTableOffset);
    const attempts = Array.from(
      new Set([candidate, 50, 40, 32, 30, 24, 20, 16, 12].filter((x) => x <= candidate || x === 12)),
    ).sort((a, b) => b - a);

    for (const bytes of attempts) {
      try {
        const out = await this.readFromDeviceMemory(deviceAddress, probeBase, bytes, bytes);
        if (out && !out.startsWith("Error") && !out.includes("deviceNotReachable") && out.length >= bytes * 2) {
          this.maxBytesReadable = bytes;
          this.maxNibblesReadable = this.maxBytesReadable * 2;
          return this.maxBytesReadable;
        }
      } catch {
        // continue probing smaller sizes
      }
    }

    this.maxBytesReadable = 12;
    this.maxNibblesReadable = this.maxBytesReadable * 2;
    return this.maxBytesReadable;
  }

  async getProgModeFromDevice(deviceAddress: string): Promise<boolean> {
    const res = await this.getDeviceProperty(deviceAddress, PIDList.ProgrammingMode);
    return res ? parseInt(res) == 1 : false;
  }

  async getHardwareTypeFromDevice(deviceAddress: string) {
    return await this.getDeviceProperty(deviceAddress, PIDList.HardwareType);
  }

  async getVersionFromDevice(deviceAddress: string) {
    return await this.getDeviceProperty(deviceAddress, PIDList.Version);
  }

  async getDescriptorFromDevice(deviceAddress: string) {
    return await this.getDeviceProperty(deviceAddress, PIDList.DeviceDescriptor);
  }

  async tableOffsetFromDevice(deviceAddress: string, objId: any): Promise<string> {
    if (objId === undefined || objId == "") {
      return "";
    }
    const hexValue = await this.getDeviceProperty(deviceAddress, PIDList.TableReference, objId.toString());
    return hexValue && Number.parseInt(hexValue, 16) != 0 ? hexValue : "";
  }

  // Faster and More Robust Device Property Access (But only return Hexadecimal)
  async getDeviceProperty(
    deviceAddress: string,
    propertyId: string,
    objTypeId?: string,
    start?: string,
    elements?: string,
  ) {
    const objectTypeId = objTypeId ? objTypeId : StandardObjectTypeList.Device.toString();
    const res = await this.readDeviceProperty(deviceAddress, objectTypeId, propertyId, start, elements);
    if (!res?.startsWith("Error")) return res;
    return "";
  }

  async getPropertyParts(prop: string) {
    const parts: RegExpMatchArray | null = prop.match(/^(.+?)\s*\((.+?)\)$/);
    // E.g. Inactive (00)
    // 0: Complete response "Inactive (00)"
    // 1: Value             "Inactive"
    // 2: Hex (In Brackts)  "00"
    if (parts && parts.length == 3) return parts;
  }

  //////////////////////
  // Setters - Device Properties
  //////////////////////

  // Device Properties
  // ObjIdx= 0 ; PID = 11, 12, 14, 15, 54, 78
  // Values set in Hex (except form ProgMode)
  async setDeviceSerial(deviceAddress: string, value: string) {
    return await this.setDeviceProperty(deviceAddress, value, PIDList.KNXSerialNumber);
  }

  async setDeviceManufacturerId(deviceAddress: string, value: string) {
    return await this.setDeviceProperty(deviceAddress, value, PIDList.ManufacturerId);
  }

  async setDeviceControl(deviceAddress: string, value: string) {
    return await this.setDeviceProperty(deviceAddress, value, PIDList.DeviceControl);
  }

  async setDeviceOrderInfo(deviceAddress: string, value: string) {
    return await this.setDeviceProperty(deviceAddress, value, PIDList.OrderInfo);
  }

  async setDeviceProgMode(deviceAddress: string, value: number) {
    return await this.setDeviceProperty(deviceAddress, value, PIDList.ProgrammingMode);
  }

  async setDeviceHardwareType(deviceAddress: string, value: string) {
    return await this.setDeviceProperty(deviceAddress, value, PIDList.HardwareType);
  }

  ////////////////////////////////////
  // Device Property Helper Functions
  ////////////////////////////////////
  async isDeviceMaskWithAbsSegment(deviceAddress: string) {
    const property = await this.getDescriptorFromDevice(deviceAddress);
    return property
      ? this.BimM112Masks.some((mask) => mask == property) || this.Bcu2Masks.some((mask) => mask == property)
      : false;
  }

  // Faster and More Robust Device Property Access (But only return Hexadecimal)
  async setDeviceProperty(
    deviceAddress: string,
    value: any,
    propertyId: string,
    objTypeId?: string,
    start?: string,
    elements?: string,
  ) {
    const objectTypeId = objTypeId ? objTypeId : StandardObjectTypeList.Device.toString();
    let res = "";
    for (let retryCounter = 0; retryCounter < this.maxReqRetries; retryCounter += 1) {
      res = await this.writeDeviceProperty(deviceAddress, objectTypeId, propertyId, value.toString(), start, elements);
      if (res) break;
      // await sleep(1000);
    }
    return res;
  }

  async setDeviceVerify(deviceAddress: string, value: boolean) {
    // Set Verify mode = On under DeviceControl (PID=14, Index 2 from LSB)  Eg. 00000(1)01,  bracket encl. bit is Verify
    const deviceControl = await this.getControlStateFromDevice(deviceAddress);
    const memValue = parseInt(deviceControl, 16);
    const verifyModeBitMask = 4;
    let newState = value ? verifyModeBitMask : 0;

    if (deviceControl.length > 0) {
      const isThirdBitSet = (memValue & verifyModeBitMask) !== 0;
      if (value == isThirdBitSet) return;
      if (value) newState = memValue | verifyModeBitMask;
      else newState = memValue & ~verifyModeBitMask;
    }
    const newStateHex = this.decimalToHex(newState);
    await this.setDeviceProperty(deviceAddress, newStateHex, PIDList.DeviceControl);
    await sleep(1000);
  }

  ////////////////////////////////////////////
  ///////// Association Table functions
  ////////////////////////////////////////////

  parseAssociationTable(table: string): Association[] {
    const count = parseInt(table.slice(0, 2), 16);
    let associations: Association[] = [];
    for (let i = 1; i <= count; i++) {
      const startIndex = 2 + (i - 1) * 4;
      const endIndex = startIndex + 4;
      const association = table.slice(startIndex, endIndex);
      const addressIndex = parseInt(association.slice(0, 2), 16);
      const signalNumber = parseInt(association.slice(2, 4), 16);
      associations.push({ addressIndex, signalNumber, hex: association });
    }
    return associations;
  }

  addAssociation(associations: Association[], newAssociation: string): Association[] {
    const newAddressIndex = parseInt(newAssociation.slice(0, 2), 16);
    const newSignalNumber = parseInt(newAssociation.slice(2, 4), 16);

    let firstLinks: Association[] = [];
    let additionalLinks: Association[] = [];

    let prevAssoc: any = { signalNumber: -1 };
    let additionalUpdate = false;
    let isNewAssociationSignalUnique = true;
    associations.forEach((assoc) => {
      if (!additionalUpdate && prevAssoc?.signalNumber < assoc.signalNumber) {
        if (assoc.signalNumber == newSignalNumber) {
          isNewAssociationSignalUnique = false;
          additionalLinks.push(assoc);
        } else {
          firstLinks.push(assoc);
        }
      } else {
        additionalUpdate = true;
        additionalLinks.push(assoc);
      }
      prevAssoc = assoc;
    });

    const newItem = { addressIndex: newAddressIndex, signalNumber: newSignalNumber, hex: newAssociation };
    if (isNewAssociationSignalUnique) {
      firstLinks.push(newItem);
      firstLinks.sort((a, b) => a.signalNumber - b.signalNumber);
    } else {
      additionalLinks.push(newItem);
      additionalLinks.sort((a, b) => a.signalNumber - b.signalNumber);
    }

    return [...firstLinks, ...additionalLinks];
  }

  reconstructAssociationTable(associations: Association[]): string {
    const count = associations.length;
    let table = count.toString(16).padStart(2, "0");
    associations.forEach((assoc) => {
      table += assoc.hex;
    });
    return table;
  }

  updateAssociationTable(inputTable: string, newAssociation: string): string {
    let associations = this.parseAssociationTable(inputTable);
    associations = this.addAssociation(associations, newAssociation);
    return this.reconstructAssociationTable(associations);
  }

  //////////////////////////////////////////////
  /////////// Default Procedure from Master Xml
  //////////////////////////////////////////////
  async getKnxMasterXml() {
    const response = await fetch(state.hostBaseUrl + "data/knx_master.xml");
    const text = await response.text();

    const parser = XMLParserSingleton.Instance;

    var xml = parser.parse(text);
    return xml;
  }

  async getDefaultProcedureFromMasterXml(mask: string) {
    let defaultProcedure;
    if (mask == "") {
      mask = "07B0"; // no itx
    }
    var xml = await this.getKnxMasterXml();
    const MasterData = firstChildWithTag(xml.KNX, "MasterData"); // no itx
    const MaskVersions = firstChildWithTag(MasterData, "MaskVersions"); // no itx
    const MaskVersion = MaskVersions.items.filter(
      (item) => item.a.Id.split("-")[1].toLowerCase() == mask.toLowerCase(),
    );
    if (MaskVersion) {
      const HawkConfigurationData = MaskVersion[0].items.find(
        (item) => item["#name"] == "HawkConfigurationData" && item.a?.LegacyVersion != "1",
      ); // 4 masks: 07b0, 17b0, 091a, 0912
      const Procedures = firstChildWithTag(HawkConfigurationData, "Procedures"); // no itx
      const Procedure = Procedures.items.filter(
        (item) => item.a.ProcedureSubType == "all" && item.a.ProcedureType == "Load", // no itx
      );
      if (Procedure) defaultProcedure = Procedure[0];
    }
    return defaultProcedure;
  }

  ////////////////////////////////////////////
  /////////// Other Helper functions
  ////////////////////////////////////////////

  increasePercent(percent?: number) {
    const nextPercent = percent ?? Math.min(this.progressUpperBound, this.percentUI + 0.8);
    this.showProgressToast(this.toastTextUI, nextPercent);
  }

  showProgressToast(text: string, percent: number, postfix?: string, limit?: number) {
    let prefixtitle = this.batchOperationInProgress ? `Device ${this.batchIndex} of ${this.batchCount}` : "";
    let title = state.translate("knxBridge.operationInProgress", "Operation in Progress");
    if (this.deviceLoading) title = state.translate("knxBridge.checkingAvalibility", "Checking Device Availibility");
    else if (this.verifyInProgress) title = state.translate("knxBridge.verifyInProgress", "Verification in Progress");
    else if (this.downloadInProgress) title = state.translate("knxBridge.downloadInProgress", "Download in Progress");
    else if (this.unloadInProgress) title = state.translate("knxBridge.unloadInProgress", "Unload in Progress");

    const maxAllowed = limit ? limit : this.operationInProgress ? 99.5 : 100;
    const normalized = Number.isFinite(percent)
      ? Math.max(0, Math.min(maxAllowed, Number(percent.toFixed(1))))
      : this.percentUI;

    this.percentUI = Math.max(this.percentUI, normalized);
    this.percentText = `${this.operatingDevice ? `[${this.operatingDevice}] ` : ""}${prefixtitle ? `${prefixtitle}: ` : ""}${text}${postfix ? ` ${postfix}` : ""}...`;

    if (this.currentOperationSummary) {
      this.currentOperationSummary.durationMs = Date.now() - this.currentOperationSummary.startedAt;
      this.currentOperationSummary.failureCount = this.failureCount;
      const stage = this.inferOperationStage(text, postfix);
      this.pushOperationLog("info", stage, postfix ? `${text} ${postfix}` : text, this.percentUI);
    }

    knxbridgeEmitter.emit("percentUIUpdate", this.percentUI, this.percentText, title);
    updateToastProgress({ progress: this.percentUI });
  }

  async getMemTableOffsetsFromDevice(
    deviceAddress: string,
    AP: any,
    LpStyle: string,
    deviceObjects: Record<string, number>,
  ) {
    if (LpStyle == MergedProcedure || LpStyle == DefaultProcedure) {
      const AddressTableOffset = await this.tableOffsetFromDevice(deviceAddress, deviceObjects.AddressTable);
      const AssociationTableOffset = await this.tableOffsetFromDevice(deviceAddress, deviceObjects.AssociationTable);
      const ComObjectTableOffset = await this.tableOffsetFromDevice(deviceAddress, deviceObjects.GroupObjectTable);
      const ParamTableOffset = await this.tableOffsetFromDevice(deviceAddress, deviceObjects.ApplicationProgram);

      const memTableOffsets = {
        address: AddressTableOffset ? this.prefixHexType(AddressTableOffset) : "", // no itx
        association: AssociationTableOffset ? this.prefixHexType(AssociationTableOffset) : "", // no itx
        groupObj: ComObjectTableOffset ? this.prefixHexType(ComObjectTableOffset) : "", // no itx
        param: ParamTableOffset ? this.prefixHexType(ParamTableOffset) : "", // no itx
      };
      return memTableOffsets;
    } else {
      // ProductProcedure
      const Static = firstChildWithTag(AP, "Static"); // no itx
      const AddressTable = firstChildWithTag(Static, "AddressTable"); // no itx
      const AssociationTable = firstChildWithTag(Static, "AssociationTable"); // no itx
      const ComObjectTable = firstChildWithTag(Static, "ComObjectTable"); // no itx
      const memTableOffsets = {
        address: AddressTable?.a?.CodeSegment
          ? this.prefixHexType(AddressTable.a.CodeSegment.split("-")[1])
          : this.defaultAddressTableOffset,
        association: AssociationTable?.a?.CodeSegment
          ? this.prefixHexType(AssociationTable.a.CodeSegment.split("-")[1])
          : this.defaultAssociationTableOffset,
        groupObj: ComObjectTable?.a?.CodeSegment
          ? this.prefixHexType(ComObjectTable.a.CodeSegment.split("-")[1])
          : this.defaultComObjTableOffset,
        param: "",
      };
      return memTableOffsets;
    }
  }

  async getDeviceObjectTypes(device: Device): Promise<Record<string, number>> {
    if (Object.keys(device.deviceObjectTypes).length > 0) return device.deviceObjectTypes;
    const deviceAddress = device.fullPhysicalAddress;
    const result: Record<string, number> = {};
    try {
      let objectId = 0;
      while (objectId < 20) {
        const objectTypeValue = await this.readDeviceProperty(deviceAddress, objectId.toString(), "1"); // PID 1 = Interface Object Type

        if (objectTypeValue === "" || objectTypeValue === null || objectTypeValue === undefined) {
          break;
        }
        const objTypeInt = parseInt(objectTypeValue, 16);

        const objectTypeName = ObjectTypeById[objTypeInt] ?? `Unknown(${objTypeInt})`;
        result[objectTypeName] = objectId;
        objectId++;
      }
    } catch (error) {
      console.error("Error in getInterfaceObjects:", error);
    }
    return result;
  }

  prefixHexType(hex: string) {
    if (hex.startsWith("0x")) return hex;
    else return "0x" + hex; // no itx
  }

  removePrefixHexType(hex: string) {
    if (hex.startsWith("0x")) return hex.substring(2);
    else return hex; // no itx
  }

  writableLoadEvent(value: string, mask: string, lsmidx: string) {
    // Stricty, The load event write value shall always be 11 octets for 0701 & 10 octets for 0705, hence the adjustments
    let data = "0x0" + value + "00".repeat(9); // Default
    if (mask == "0701")
      // BimM112 Old Devices
      return "0x" + lsmidx + value + "00".repeat(10);
    else if (mask == "0705")
      // BimM112 New Devices
      return "0x0" + value + "00".repeat(9);
    return data;
  }

  appIdAsHex(appId: string) {
    // const regex = /M-(\d{4})_A-(\w{4})-(\d{2})/;
    const regex = /^M-([0-9A-Fa-f]{4})_A-([0-9A-Fa-f]{4})-([0-9A-Fa-f]{2})-([0-9A-Fa-f]{4})$/;
    const match = appId.match(regex);
    if (match) {
      const result = match[1] + match[2] + match[3];
      return result.toLowerCase();
    }
    return undefined;
  }

  groupAddressToHex(fullAddress: string) {
    const parts = fullAddress.split("/");
    const x = parseInt(parts[0], 10);
    const y = parseInt(parts[1], 10);
    const z = parseInt(parts[2], 10);
    const decimalValue = (x << 11) + (y << 8) + z;
    const hexValue = decimalValue.toString(16).padStart(4, "0").toLowerCase();
    return hexValue;
  }

  hexToGroupAddress(hex: string): string {
    const decimalValue = parseInt(hex, 16);
    const x = (decimalValue >> 11) & 0b11111; // top 5 bits
    const y = (decimalValue >> 8) & 0b111; // next 3 bits
    const z = decimalValue & 0xff; // last 8 bits
    return `${x}/${y}/${z}`;
  }

  deviceAddressToHex(deviceAddress: string) {
    const segments = deviceAddress.split(".").map((segment) => parseInt(segment, 10));
    const hexSegments = [segments[0].toString(16), segments[1].toString(16), segments[2].toString(16).padStart(2, "0")];
    return hexSegments.join("").toLowerCase();
  }

  hexToDeviceAddress(hex: string): string {
    const segment1 = parseInt(hex.slice(0, 2), 16);
    const segment2 = parseInt(hex.slice(2, 4), 16);
    const segment3 = parseInt(hex.slice(4, 6), 16);
    return `${segment1}.${segment2}.${segment3}`;
  }

  decimalToHex(decimalValue: number, padding?: number) {
    const hex = decimalValue.toString(16);
    const pad = padding ? padding : hex.length <= 2 ? 2 : hex.length <= 4 ? 4 : 8;
    return decimalValue.toString(16).padStart(pad, "0").toLowerCase();
  }

  binaryToHex(binaryString: string) {
    const intValue = parseInt(binaryString, 2);
    const hexString = intValue.toString(16).toUpperCase();
    const paddedHexString = hexString.padStart(Math.ceil(binaryString.length / 4), "0").toLowerCase();
    return paddedHexString;
  }

  //
  // input "1f"
  // output: "0001 1111"   // highes bit first
  //
  hexToBinaryString(hexString: string): string {
    const intValue = parseInt(hexString, 16);
    const binaryString = intValue.toString(2);
    const paddedBinaryString = binaryString.padStart(hexString.length * 4, "0");
    return paddedBinaryString;
  }

  validHexTable(table: string) {
    return table.startsWith("0x");
  }

  base64ToHex(base64: string): string {
    const raw = window.atob(base64);
    let result = "";
    for (let i = 0; i < raw.length; i++) {
      const hex = raw.charCodeAt(i).toString(16);
      result += hex.length === 2 ? hex : "0" + hex;
    }
    return result.toLowerCase();
  }

  uint8ArrayToBase64(uint8Array: Uint8Array): string {
    const binaryString = String.fromCharCode(...uint8Array);
    return window.btoa(binaryString);
  }

  uint8ArrayToHex(uint8Array: Uint8Array): string {
    return Array.from(uint8Array)
      .map((byte) => byte.toString(16).padStart(2, "0"))
      .join("");
  }

  hexToUint8Array(hexString: string): Uint8Array {
    if (hexString.substring(0, 2) == "0x") {
      hexString = hexString.substring(2);
    }
    const matches = hexString.match(/.{1,2}/g);
    if (matches) {
      return Uint8Array.from(matches.map((byte) => parseInt(byte, 16)));
    } else return new Uint8Array();
  }

  hexToIEEE754(hex: string): string {
    if (hex.length !== 8) {
      // Ensure the input is 8 characters long for a 32-bit float
      throw new Error("Invalid hex length. Must be 8 characters."); // no itx
    }
    let binary = parseInt(hex, 16).toString(2).padStart(32, "0");
    let sign = parseInt(binary.charAt(0), 2);
    let exponent = parseInt(binary.substring(1, 9), 2) - 127;
    let mantissa = "1" + binary.substring(9);
    let floatMantissa = 0;
    for (let i = 0; i < mantissa.length; i++) {
      floatMantissa += parseInt(mantissa.charAt(i), 2) * Math.pow(2, -i);
    }
    let floatValue = Math.pow(-1, sign) * floatMantissa * Math.pow(2, exponent);
    return floatValue.toFixed(2);
  }

  hexToAscii(input: string) {
    var hex = input.toString();
    var str = "";
    for (var i = 0; i < hex.length; i += 2) {
      const char = hex.substring(i, i + 2);
      if (char != "ff") {
        str += String.fromCharCode(parseInt(char, 16));
      }
    }
    return str;
  }

  diffCount(string1: any, string2: any) {
    const diff = Diff.diffChars(string1, string2);
    return diff.length - 1; // Math.floor((diff.length - 1) / 2);
  }

  diffChecker(string1: any, string2: any): string {
    const diff = Diff.diffChars(string1, string2);
    let result = "";
    diff.forEach((part) => {
      const color = part.added ? chalk.green : part.removed ? chalk.red : chalk.grey;
      result += color(part.value);
    });
    return result;
  }

  isDiffCheckEqual(string1: any, string2: any): boolean {
    const diff = Diff.diffChars(string1, string2);
    if (diff.length == 1) return true;
    else return false;
  }

  diffCheckerBin(arr1: Uint8Array, arr2: Uint8Array): string {
    let res = "";
    if (arr1.length != arr2.length) {
      res += `size different ${arr1.length} != ${arr2.length}\n`;
    }

    for (let i = 0; i < Math.min(arr1.length, arr2.length); i++) {
      if (arr1[i] != arr2[i]) {
        res += `${i}: ${arr1[i]} - ${arr2[i]}   hex: ${arr1[i].toString(16)} - ${arr2[i].toString(16)} \n`;
      }
    }
    return res;
  }

  updateTotalMemoryDiff() {
    if (this.stopOperationCommand) this.resetMemoryDiff;
    const totalDiff =
      this.lastDeviceMemoryStats.addrMemDiff +
      this.lastDeviceMemoryStats.assocMemDiff +
      this.lastDeviceMemoryStats.comObjMemDiff +
      this.lastDeviceMemoryStats.paramMemDiff;
    this.lastDeviceMemoryStats.totalMemDiff = totalDiff < 0 ? Math.min(-1, totalDiff) : totalDiff;
  }

  resetMemoryDiff() {
    this.lastDeviceMemoryStats.addrMemDiff = -1;
    this.lastDeviceMemoryStats.assocMemDiff = -1;
    this.lastDeviceMemoryStats.comObjMemDiff = -1;
    this.lastDeviceMemoryStats.paramMemDiff = -1;
  }

  isPhysicalAddressValid(address: string) {
    const regex = /^(\d+)\.(\d+)\.(\d+)$/;
    const match = address.match(regex);
    if (!match) return false;
    const [_, part1, part2, part3] = match;
    const num1 = parseInt(part1, 10);
    const num2 = parseInt(part2, 10);
    const num3 = parseInt(part3, 10);
    if (num1 < 0 || num1 > 15) return false;
    if (num2 < 0 || num2 > 15) return false;
    if (num3 < 0 || num3 > 255) return false;
    return true;
  }

  isAreaLineValid(address: string) {
    const regex = /^(?:[0-9]|1[0-5])\.(?:[0-9]|1[0-5])$/;
    const match = address.match(regex);
    if (!match) return false;
    else return true;
  }

  isValidHex(hex: string): boolean {
    return /^[0-9A-Fa-fx]+$/.test(hex);
  }

  isValidConn(conn: string): boolean {
    const ipv4Regex =
      /^(?=\d+\.\d+\.\d+\.\d+$)(?:(?:25[0-5]|2[0-4][0-9]|1[0-9]{2}|[1-9][0-9]|[0-9])\.){3}(?:25[0-5]|2[0-4][0-9]|1[0-9]{2}|[1-9][0-9]|[0-9])$/;
    const usbRegex = /^[0-9a-fA-F]{4}:[0-9a-fA-F]{4}$/;
    const serialRegex = /^(COM\d+|\/dev\/tty\w+)$/;

    connType = ipv4Regex.test(conn) ? "ip" : usbRegex.test(conn) ? "usb" : serialRegex.test(conn) ? "serial" : "ip";
    return ipv4Regex.test(conn) || usbRegex.test(conn) || serialRegex.test(conn);
  }

  isValidIP(conn: string): boolean {
    const ipv4Regex =
      /^(?=\d+\.\d+\.\d+\.\d+$)(?:(?:25[0-5]|2[0-4][0-9]|1[0-9]{2}|[1-9][0-9]|[0-9])\.){3}(?:25[0-5]|2[0-4][0-9]|1[0-9]{2}|[1-9][0-9]|[0-9])$/;
    return ipv4Regex.test(conn);
  }

  isValidUSB(conn: string): boolean {
    const usbRegex = /^[0-9a-fA-F]{4}:[0-9a-fA-F]{4}$/;
    return usbRegex.test(conn);
  }

  isValidSerial(conn: string): boolean {
    const serialRegex = /^(COM\d+|\/dev\/tty\w+)$/;
    return serialRegex.test(conn);
  }

  checkConnectionStatus(res: any) {
    if (ipConnected && !this.stopOperationCommand) return res;
    else throw Error("Disconnected"); // no itx
  }

  ////////////////////////////////////////////////////
  // Deprecated functions (kept as fallback functions)
  ////////////////////////////////////////////////////

  // Older Device Property Access Implementation that gives both Value and Hexadecimal
  async getDevicePropertyAlt(deviceAddress: string, propertyId: string, objTypeId?: string) {
    const objectTypeId = objTypeId ? objTypeId : StandardObjectTypeList.Device.toString();
    const res = await this.readDevicePropertyAlt(deviceAddress, objectTypeId, propertyId);
    if (res) {
      const property = await this.getPropertyParts(res);
      return property;
    }
    return undefined;
  }

  async readDevicePropertyAlt(deviceAddress: string, objectTypeId: string, propertyId: string) {
    const res = await this.fetchAPI(
      "readDevicePropertyAlt", // no itx
      `getDevicePropertyAlt?device=${deviceAddress}&objectTypeId=${objectTypeId}&propertyId=${propertyId}`,
    );
    this.checkDeviceReachable(res, deviceAddress);
    return this.checkConnectionStatus(res);
  }

  isDeviceProgrammable(deviceInfo: DeviceProgrammingInfo) {
    const device = deviceInfo.device;
    const productInfo = device.template?.knxProductData.productInfo;
    let isProgrammable = false;
    if (productInfo) {
      const mask = productInfo?.MaskVersion ? productInfo?.MaskVersion.split("-")[1] : "";
      if (
        productInfo.Programmable == "Verified" ||
        productInfo.Programmable == "Potential" ||
        this.SupportedMasks.includes(mask)
      )
        isProgrammable = true;
    }
    if (!isProgrammable && deviceInfo) {
      deviceInfo.deviceStatus = DeviceStatus.notSupported;
    }
    return isProgrammable;
  }

  isProductNotLoaded(device: Device) {
    const productInfo = device.template?.knxProductData.nonPersistentXmlData;
    if (productInfo) return false;
    return true;
  }

  async lockInterface(deviceAddress: string) {
    // For USB/Serial backends the interface must be locked to prevent concurrent access.
    // For IP backends, the lock is either not required or handled server-side, so we no-op.
    if (connType !== "usb" && connType !== "serial") {
      lockedState = false;
      return "success"; // no itx
    }

    try {
      const res = await this.fetchAPI("lockInterface", "lockInterface");
      lockedState = true;
      this.checkDeviceReachable(res, deviceAddress);
      return this.checkConnectionStatus(res);
    } catch (e) {
      console.warn("[KnxBridge] lockInterface failed", e);
      lockedState = false;
      return "failure"; // no itx
    }
  }

  async unlockInterface(deviceAddress: string) {
    if (connType !== "usb" && connType !== "serial") {
      lockedState = false;
      return "success"; // no itx
    }

    try {
      const res = await this.fetchAPI("unlockInterface", "unlockInterface");
      lockedState = false;
      this.checkDeviceReachable(res, deviceAddress);
      return this.checkConnectionStatus(res);
    } catch (e) {
      console.warn("[KnxBridge] unlockInterface failed", e);
      lockedState = false;
      return "failure"; // no itx
    }
  }

  // Improvements in exsiting logic from ChatGPT

  async etsRetry<T>(action: () => Promise<T>, description: string, retries = 3, delayMs = 250): Promise<T> {
    let lastError: any = undefined;
    const maxRetries = Math.max(1, retries);
    let backoff = Math.max(50, delayMs);

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      if (this.stopOperationCommand) throw new Error("Operation stopped");

      try {
        return await action();
      } catch (err) {
        lastError = err;
        console.warn(`[ETS-Retry] ${description} failed (attempt ${attempt}/${maxRetries})`, err);

        // If link is down, fail fast
        if (!ipConnected) break;

        if (attempt < maxRetries) {
          const jitter = Math.floor(Math.random() * 200);
          await sleep(backoff + jitter);
          backoff = Math.min(5000, Math.floor(backoff * 1.7));
        }
      }
    }

    throw lastError;
  }
}

const KnxBridge = new KnxBridgeClass();
export default KnxBridge;

////////////////////////////////////////////////////
// Commented functions kept for reference
// Not to be deleted yet, these are working functions and might be required in future
////////////////////////////////////////////////////

// Load States for Address, Association, GrpObj, AppProgram and AppProgram2
// ObjIdx= 1,2 - (Common), 3,4 (AP, AP2 - Product Procedure) 3,4,5 (GrpObj, AP, AP2 - Merged Procedure); PID 5
// async setDeviceAddrTableLoadState(deviceAddress: string, value: number) {
//   return await this.setDeviceProperty(deviceAddress, value, PIDList.LoadStateControl, ObjectTypeList.AddressTable);
// }

// async setDeviceAssocTableLoadState(deviceAddress: string, value: number) {
//   return await this.setDeviceProperty(
//     deviceAddress,
//     value,
//     PIDList.LoadStateControl,
//     ObjectTypeList.AssociationTable,
//   );
// }

// async setDeviceAPLoadState(deviceAddress: string, value: number) {
//   return await this.setDeviceProperty(
//     deviceAddress,
//     value,
//     PIDList.LoadStateControl,
//     ObjectTypeList.ApplicationProgram,
//   );
// }

// async setDeviceAP2LoadState(deviceAddress: string, value: number) {
//   return await this.setDeviceProperty(
//     deviceAddress,
//     value,
//     PIDList.LoadStateControl,
//     ObjectTypeList.ApplicationProgram2,
//   );
// }

// async setDeviceGrpObjTableLoadStateMP(deviceAddress: string, value: number) {
//   return await this.setDeviceProperty(
//     deviceAddress,
//     value,
//     PIDList.LoadStateControl,
//     ObjectTypeList.GroupObjectTable_MP,
//   );
// }

// async setDeviceAPLoadStateMP(deviceAddress: string, value: number) {
//   return await this.setDeviceProperty(
//     deviceAddress,
//     value,
//     PIDList.LoadStateControl,
//     ObjectTypeList.ApplicationProgram_MP,
//   );
// }

// async setDeviceAP2LoadStateMP(deviceAddress: string, value: number) {
//   return await this.setDeviceProperty(
//     deviceAddress,
//     value,
//     PIDList.LoadStateControl,
//     ObjectTypeList.ApplicationProgram2_MP,
//   );
// }

// Application Program Versions
// ObjIdx= 3,4 / 4,5 ; PID 6
// async setDeviceAPRunControl(deviceAddress: string, value: number) {
//   return await this.setDeviceProperty(deviceAddress, value, PIDList.RunControl, ObjectTypeList.ApplicationProgram);
// }

// async setDeviceAP2RunControl(deviceAddress: string, value: number) {
//   return await this.setDeviceProperty(deviceAddress, value, PIDList.RunControl, ObjectTypeList.ApplicationProgram2);
// }

// async setDeviceAPRunControlMP(deviceAddress: string, value: number) {
//   return await this.setDeviceProperty(deviceAddress, value, PIDList.RunControl, ObjectTypeList.ApplicationProgram_MP);
// }

// async setDeviceAP2RunControlMP(deviceAddress: string, value: number) {
//   return await this.setDeviceProperty(
//     deviceAddress,
//     value,
//     PIDList.RunControl,
//     ObjectTypeList.ApplicationProgram2_MP,
//   );
// }

// Load States for Address, Association, GrpObj, AppProgram and AppProgram2
// ObjIdx= 1,2 - (Common), 3,4 (AP, AP2 - Product Procedure), 3,4,5(GrpObj, AP, AP2 - Merged Procedure); PID 5
// async getAddrTableLoadStateFromDevice(deviceAddress: string) {
//   return await this.getDeviceProperty(deviceAddress, PIDList.LoadStateControl, ObjectTypeList.AddressTable);
// }

// async getAssocTableLoadStateFromDevice(deviceAddress: string) {
//   return await this.getDeviceProperty(deviceAddress, PIDList.LoadStateControl, ObjectTypeList.AssociationTable);
// }

// async getAPLoadStateFromDevice(deviceAddress: string) {
//   return await this.getDeviceProperty(deviceAddress, PIDList.LoadStateControl, ObjectTypeList.ApplicationProgram);
// }

// async getAP2LoadStateFromDevice(deviceAddress: string) {
//   return await this.getDeviceProperty(deviceAddress, PIDList.LoadStateControl, ObjectTypeList.ApplicationProgram2);
// }

// Merged Procedure
// async getGrpObjTableLoadStateFromDeviceMP(deviceAddress: string) {
//   return await this.getDeviceProperty(deviceAddress, PIDList.LoadStateControl, ObjectTypeList.GroupObjectTable_MP);
// }

// async getAPLoadStateFromDeviceMP(deviceAddress: string) {
//   return await this.getDeviceProperty(deviceAddress, PIDList.LoadStateControl, ObjectTypeList.ApplicationProgram_MP);
// }

// async getAP2LoadStateFromDeviceMP(deviceAddress: string) {
//   return await this.getDeviceProperty(deviceAddress, PIDList.LoadStateControl, ObjectTypeList.ApplicationProgram2_MP);
// }

// Run Control AppProgram and AppProgram2
// ObjIdx= 3,4 ; PID 6
// async getAPRunControlFromDevice(deviceAddress: string) {
//   return await this.getDeviceProperty(deviceAddress, PIDList.RunControl, ObjectTypeList.ApplicationProgram);
// }

// async getAP2RunControlFromDevice(deviceAddress: string) {
//   return await this.getDeviceProperty(deviceAddress, PIDList.RunControl, ObjectTypeList.ApplicationProgram2);
// }

// Table Offsets - AddressTable and AssociationTable
// ObjIdx= 1,2,3*,4* ; PID 7   (* for Merged Procedure only)
// async getAddressTableOffsetFromDevice(deviceAddress: string) {
//   const hexValue = await this.getDeviceProperty(deviceAddress, PIDList.TableReference, ObjectTypeList.AddressTable);
//   if (hexValue && parseInt("0x" + hexValue) != 0) return hexValue;
//   else return "";
// }

// async getAssocTableOffsetFromDevice(deviceAddress: string) {
//   const hexValue = await this.getDeviceProperty(
//     deviceAddress,
//     PIDList.TableReference,
//     ObjectTypeList.AssociationTable,
//   );
//   if (hexValue && parseInt("0x" + hexValue) != 0) return hexValue;
//   else return "";
// }

// MergedProcedure
// async getGrpObjTableOffsetFromDeviceMP(deviceAddress: string) {
//   const hexValue = await this.getDeviceProperty(
//     deviceAddress,
//     PIDList.TableReference,
//     ObjectTypeList.GroupObjectTable_MP,
//   );
//   if (hexValue && parseInt("0x" + hexValue) != 0) return hexValue;
//   else return "";
// }
// async getParamTableOffsetFromDeviceMP(deviceAddress: string) {
//   const hexValue = await this.getDeviceProperty(
//     deviceAddress,
//     PIDList.TableReference,
//     ObjectTypeList.ApplicationProgram_MP,
//   );
//   if (hexValue && parseInt("0x" + hexValue) != 0) return hexValue;
//   else return "";
// }

// Application Program Versions
// ObjIdx= 3,4 / 4,5 ; PID 13
// async getAPVersionFromDevice(deviceAddress: string) {
//   return await this.getDeviceProperty(deviceAddress, PIDList.ApplicationVersion, ObjectTypeList.ApplicationProgram);
// }

// async getAP2VersionFromDevice(deviceAddress: string) {
//   return await this.getDeviceProperty(deviceAddress, PIDList.ApplicationVersion, ObjectTypeList.ApplicationProgram2);
// }

// MergedProcedure
// async getAPVersionFromDeviceMP(deviceAddress: string) {
//   return await this.getDeviceProperty(
//     deviceAddress,
//     PIDList.ApplicationVersion,
//     ObjectTypeList.ApplicationProgram_MP,
//   );
// }

// async getAP2VersionFromDeviceMP(deviceAddress: string) {
//   return await this.getDeviceProperty(
//     deviceAddress,
//     PIDList.ApplicationVersion,
//     ObjectTypeList.ApplicationProgram2_MP,
//   );
// }
